import { useState, useEffect, useRef } from "react";
import logoPathnote from './logo-pathnote.png';
import { ArrowLeft, Sparkles, Heart, Star, TrendingUp, Shield, Leaf, Users, Compass, BookOpen, Clock, ChevronRight, ChevronLeft, X, Pen, Bookmark } from "lucide-react";
import { tokens, components, themeFor, legacyC } from "./styles/designTokens";
import { EMOTIONS, EMOTION_MAP, isValidEmotion, LOG_FILTERS } from "./constants/emotions";
import { LandingPage } from "./LandingPage";

// ── Design tokens ─────────────────────────────────────────────
// 既存inline styleの段階移行のため、C は designTokens の note テーマから生成（互換オブジェクト）。
// タブ別テーマを使う箇所では legacyC(themeFor(tabId)) でテーマを差し替えて利用する。
const C = legacyC(themeFor("note"));
const F = tokens.fontFamily.base;
const SP = tokens.spacing;
const R = tokens.radius;
const SH = tokens.shadow;
const FS = tokens.fontSize;
const FW = tokens.fontWeight;
const LH = tokens.lineHeight;

// ── Storage ───────────────────────────────────────────────────
const KEY = "pathnote_mvp_v1";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };
// eslint-disable-next-line no-unused-vars
const save = (d) => localStorage.setItem(KEY, JSON.stringify(d));

// ── GA4: page_view ユーティリティ（コンポーネント外で定義して参照エラーを回避）
const gtagPageView = (path, title) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.origin + path,
    });
  }
};

// TODO: v4実装後に削除（不要になったら撤去）
// 旧フロー（v3以前）のlocalStorageキーを起動時に1回クリアする片付けコード。
// テストデータしか入っていない前提のリリース前クリーンアップ。
// v4新フロー安定後に本ブロックごと撤去する。
const V4_CLEANUP_FLAG = "pathnote_v4_cleanup_done";
if (typeof window !== "undefined" && !localStorage.getItem(V4_CLEANUP_FLAG)) {
  const legacyKeys = [
    "pathnote_mvp_v1",
    "pathnote_note_flow_draft_v1",
    "pathnote_log_seen",
    "pathnote_completed_milestones",
    "pathnote_pending_suggested_question",
    "pathnote_mood_category",
    "pathnote_mood_prompt_date",
    "pathnote_install_invite_done",
    "pathnote_last_page",
    "pathnote_last_mypage_tab",
  ];
  legacyKeys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
  try { localStorage.setItem(V4_CLEANUP_FLAG, "1"); } catch {}
}

// ── AI モデル名（Anthropic） ─────────────────────────────────
// .env で MODEL_HAIKU / MODEL_SONNET を設定するとエイリアスを上書きできる
// （バックエンドで env を見るが、フロントは固定エイリアスを使い、バックエンドが
// 必要に応じて実モデル名へ差し替える設計）
const MODEL_HAIKU  = "claude-haiku-4-5";
const MODEL_SONNET = "claude-sonnet-4-6";

// Claude が JSON を markdown コードブロックで包まないよう、各プロンプトの末尾に付与する
const JSON_ONLY_INSTRUCTION = "\n\nOutput ONLY raw JSON. Do not wrap in markdown code blocks. Do not add any text before or after the JSON.";

// ── ネガ路線（受容ファーストフロー）の対象感情タグ ────────────────
// unease=モヤモヤ / tired=疲れた / doubt=迷い
// insight（気づき）/ joy / growth はポジ路線（通常フロー）で扱う
const NEGATIVE_EMOTIONS = new Set(["unease", "tired", "doubt"]);

// ── API ───────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
async function callAIJSON(messages, model, retries=2, maxTokens) {
  // モデルに応じたタイムアウト: Sonnet（重い集計）= 65s、Haiku = 35s
  const timeoutMs = (model || "").includes("sonnet") ? 65000 : 35000;
  for (let attempt=0; attempt<=retries; attempt++) {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch("/api/analyze", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ messages, model, max_tokens: maxTokens }),
        signal: controller.signal,
      });
      clearTimeout(tid);
      if (!res.ok) {
        const e = await res.json().catch(()=>({}));
        const err = new Error(e?.error || "APIエラー: " + res.status);
        err.status = res.status;
        err.code = e?.code;
        err.retryAfter = e?.retryAfter;
        // レートリミットは即時中断（サーバー側でリトライ済み）
        if (res.status === 429) throw err;
        throw err;
      }
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content || "";
      if (!text) throw new Error("AIからの応答が空でした");

      // JSON部分を抽出（```json ... ``` コードブロックにも対応）
      const cleaned = text.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
      const start = cleaned.indexOf("{");
      if (start === -1) throw new Error("JSON形式の応答が得られませんでした。再試行します");

      let depth=0, end=-1;
      for (let i=start; i<cleaned.length; i++) {
        if (cleaned[i]==="{") depth++;
        else if (cleaned[i]==="}") { depth--; if (depth===0){end=i;break;} }
      }
      if (end === -1) throw new Error("JSONが不完全です。再試行します");
      return JSON.parse(cleaned.slice(start, end+1));
    } catch(e) {
      clearTimeout(tid);
      // 429 はリトライしないで即throw（サーバー側でリトライ済み）
      if (e?.status === 429) throw e;
      if (attempt === retries) throw e;
      // リトライ前に少し待つ
      await new Promise(r=>setTimeout(r, 1000 * (attempt+1)));
    }
  }
}

const COMPANY = {
  name:"合同会社Min.lake", service:"PathNote", email:"info@minlake.jp",
  address:"お問い合わせいただいた際に開示いたします",
  tel:"お問い合わせはメールにて承ります", since:"2025年5月",
  price:"現在無料でご利用いただけます（将来的に有料プランを導入する場合は、事前にご案内いたします）",
  payment:"該当なし（現在無料）",
  delivery:"お申し込み後、即時ご利用いただけます（インターネット上でのサービス提供）",
  cancel:"いつでも利用を停止できます。データの削除をご希望の場合はお問い合わせください",
  returns:"デジタルコンテンツの性質上、返金はお受けできません（有料プラン導入時に別途規定を設けます）",
};

// ── 保存完了アイコン（useEffect で transition を確実に発火） ──
function SavedIcon() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(id);
  }, []);
  return (
    <div style={{
      width: 80, height: 80, borderRadius: "50%",
      background: "#FDF0EF",
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 12px",
      opacity: show ? 1 : 0,
      transform: show ? "scale(1)" : "scale(0.8)",
      transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
    }}>
      <Bookmark size={36} strokeWidth={2} color="#C97B7B"/>
    </div>
  );
}

// ── 今の気づきボックス（module-level: コンポーネント同一性を保つため） ──
function InsightBox({ insightDraft, onDraftChange, insights, onCommit }) {
  const disabled = insightDraft.trim().length < 2;
  return (
    <div style={{
      marginTop: SP["2xl"],
      background: tokens.neutral.surface,
      border: `1.5px solid ${tokens.accent.primary}33`,
      borderRadius: R.xl,
      padding: `${SP["3xl"]}px ${SP["3xl"]}px ${SP["2xl"]}px`,
    }}>
      {/* ヘッダー */}
      <div style={{ display:"flex", alignItems:"center", gap:SP.base, marginBottom:SP.xl }}>
        <Leaf size={14} strokeWidth={1.8} color={tokens.accent.green}/>
        <span style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:tokens.text.secondary, letterSpacing:tokens.letterSpacing.wide }}>
          今の気づき
        </span>
      </div>
      {/* textarea */}
      <textarea
        value={insightDraft}
        onChange={e => onDraftChange(e.target.value)}
        placeholder={"うまく言葉になっていなくても\n大丈夫です"}
        style={{
          ...components.inputBase(),
          minHeight: 72,
          fontSize: FS.base,
          resize: "none",
          width: "100%",
          boxSizing: "border-box",
          background: tokens.neutral.bg,
        }}
      />
      {/* 確定ボタン */}
      <div style={{ marginTop: SP.xl, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap: SP.md }}>
        {/* 確定済みリスト */}
        {insights.length > 0 ? (
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, marginBottom:SP.sm }}>
              これまでの気づき：
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:SP.xs }}>
              {insights.map(ins => (
                <div key={ins.id} style={{ display:"flex", alignItems:"flex-start", gap:SP.xs, fontSize:FS.sm, color:tokens.text.secondary, lineHeight:LH.relaxed }}>
                  <span style={{ flexShrink:0, color:tokens.accent.green }}>・</span>
                  <span>{ins.content}</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div/>}
        {/* そっと残すボタン */}
        <button
          onClick={onCommit}
          disabled={disabled}
          style={{
            flexShrink: 0,
            display: "inline-flex", alignItems: "center", gap: SP.xs,
            padding: `${SP.sm}px ${SP.xl}px`,
            background: disabled ? tokens.neutral.surfaceAlt : tokens.accent.light,
            border: `1px solid ${disabled ? tokens.neutral.border : tokens.accent.medium}`,
            borderRadius: R.lg,
            fontSize: FS.sm,
            fontWeight: FW.semibold,
            color: disabled ? tokens.text.tertiary : tokens.accent.primary,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            fontFamily: F,
            transition: `background ${tokens.motion.durationFast} ease`,
            whiteSpace: "nowrap",
          }}
        >
          🌱 そっと残す
        </button>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  const currentYear = new Date().getFullYear();
  const [page, setPage]             = useState(()=>{
    try {
      const lastPage = localStorage.getItem("pathnote_last_page");
      return lastPage === "mypage" && load() ? "mypage" : "home";
    } catch {
      return "home";
    }
  });
  const [legalPage, setLegalPage]   = useState(null);
  const [mypageInitialTab, setMypageInitialTab] = useState(()=>{
    try { return localStorage.getItem("pathnote_last_mypage_tab") || "note"; } catch { return "note"; }
  });

  useEffect(()=>{
    if (page === "mypage" || page === "home") {
      localStorage.setItem("pathnote_last_page", page);
    }
  }, [page]);

  // ── Global Styles & Nav & Footer ──────────────────────────
  const GlobalStyles = () => (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
      @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
      @keyframes spin{to{transform:rotate(360deg)}}
      @keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
      /* 自分の言葉タブのローディング演出 */
      @keyframes breathe{0%,100%{opacity:.6}50%{opacity:1}}
      @keyframes msgFade{0%{opacity:0;transform:translateY(4px)}15%,85%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-4px)}}

      /* 成長する植物（14秒1ループ）
         PathNoteの「言葉が育つ」というコンセプトを視覚化するアニメーション。
         1ループで種→茎→葉が順番に育ち、呼吸の間を置いてフェードアウトしてから再生する。

         フェーズ:
           0-14%  (0-2s)   種が左右に微かに揺れる（茎・葉は不可視）
           14-36% (2-5s)   茎が stroke-dashoffset で下から上に線描画される
           36-57% (5-8s)   双葉が中央から左右に開く（scale + rotate + opacity）
           57-86% (8-12s)  全体が呼吸（scale 1↔1.03、葉の開いた状態を維持）
           86-100%(12-14s) フェードアウト → ループ

         注意: scale/translate の transform 競合を避けるため、
               位置決め（translate -50%, -50%）は親 div に分離し、
               keyframes は scale だけを担うようにしている。 */
      @keyframes seedSway{
        0%,14%{transform:rotate(0);opacity:1}
        3%{transform:rotate(-3deg)}
        10%{transform:rotate(3deg)}
        86%{transform:rotate(0);opacity:1}
        100%{transform:rotate(0);opacity:0}
      }
      @keyframes stemDraw{
        0%,14%{stroke-dashoffset:60;opacity:1}
        36%,86%{stroke-dashoffset:0;opacity:1}
        100%{stroke-dashoffset:0;opacity:0}
      }
      @keyframes leafRevealLeft{
        0%,36%{transform:scale(0) rotate(0);opacity:0}
        57%,86%{transform:scale(1) rotate(-12deg);opacity:1}
        100%{transform:scale(1) rotate(-12deg);opacity:0}
      }
      @keyframes leafRevealRight{
        0%,36%{transform:scale(0) rotate(0);opacity:0}
        57%,86%{transform:scale(1) rotate(12deg);opacity:1}
        100%{transform:scale(1) rotate(12deg);opacity:0}
      }
      @keyframes plantBreathe{
        0%,57%{transform:scale(1);opacity:1}
        71%{transform:scale(1.03);opacity:1}
        86%{transform:scale(1);opacity:1}
        100%{transform:scale(1);opacity:0}
      }

      /* 周囲の粒子（6個、5〜8秒ループ）*/
      @keyframes drift1{0%,100%{transform:translate(0,0);opacity:.4}25%{transform:translate(-8px,-14px);opacity:1}50%{transform:translate(4px,-22px);opacity:.7}75%{transform:translate(-3px,-10px);opacity:.6}}
      @keyframes drift2{0%,100%{transform:translate(0,0);opacity:.4}30%{transform:translate(10px,-10px);opacity:.9}55%{transform:translate(-4px,-20px);opacity:.6}80%{transform:translate(6px,-6px);opacity:.5}}
      @keyframes drift3{0%,100%{transform:translate(0,0);opacity:.35}20%{transform:translate(-12px,-6px);opacity:.8}45%{transform:translate(-5px,-18px);opacity:.55}70%{transform:translate(7px,-12px);opacity:.45}}
      @keyframes drift4{0%,100%{transform:translate(0,0);opacity:.4}35%{transform:translate(12px,-8px);opacity:.95}65%{transform:translate(2px,-20px);opacity:.7}}
      @keyframes drift5{0%,100%{transform:translate(0,0);opacity:.35}40%{transform:translate(-10px,-14px);opacity:.85}70%{transform:translate(4px,-8px);opacity:.5}}
      @keyframes drift6{0%,100%{transform:translate(0,0);opacity:.3}45%{transform:translate(8px,-16px);opacity:.8}75%{transform:translate(-6px,-9px);opacity:.55}}
      *{box-sizing:border-box;margin:0;padding:0}
      /* 横スクロールはカルーセル等のローカル要素だけに限定し、ページ全体には絶対に出さない。
         カルーセル内の負マージン bleed が body を押し出すケースの保険として、html/body/#root に
         overflow-x:hidden を強制する。 */
      html,body,#root{overflow-x:hidden;max-width:100vw}
      body{background:${C.bg};font-family:${F}}
      button,textarea,input{font-family:${F}}
      ::-webkit-scrollbar{width:4px}
      ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
      .lp-nav-cta{transition:background 0.18s ease}
      .lp-nav-cta:hover{background:#D6E8FF!important}
      @media(max-width:390px){
        h1{font-size:22px!important}
        h2{font-size:18px!important}
        h3{font-size:17px!important}
        p{font-size:14px}
        .quiz-choice{font-size:13px!important;padding:12px 14px!important}
      }
    `}</style>
  );

  const Nav = ({ onStart } = {}) => {
    const d=load()||{};
    const has=d.sessions?.length>0||d.result;
    return (
      <nav style={{ background:C.surface, borderBottom:"1px solid "+C.border, padding:"0 20px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, position:"sticky", top:0, zIndex:100, boxShadow:C.shadow }}>
        <button type="button" onClick={()=>setPage("home")} aria-label="PathNote トップへ戻る" style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", alignItems:"center", gap:8, fontFamily:F }}>
          <img src={logoPathnote} alt="PathNote" style={{ width:44, height:44, objectFit:"contain" }}/>
          <span style={{ fontFamily:"'Cormorant Garamond', serif", fontWeight:600, fontSize:26, color:"#4A3F35", letterSpacing:"0.01em", lineHeight:1 }}>PathNote</span>
        </button>
        {onStart ? (
          <button onClick={onStart} className="lp-nav-cta" style={{ background:tokens.accent.light, color:tokens.accent.primaryDark, border:`1.5px solid ${tokens.accent.medium}`, borderRadius:24, padding:"8px 20px", fontSize:14, fontWeight:600, fontFamily:F, cursor:"pointer", whiteSpace:"nowrap", lineHeight:1 }}>
            自分ノートを書く
          </button>
        ) : (
          has&&<button onClick={()=>{ setPage("mypage"); gtagPageView("/mypage", "PathNote - マイページ"); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:8, padding:"5px 12px", color:C.sub, cursor:"pointer", fontSize:12 }}>マイページ</button>
        )}
      </nav>
    );
  };

  const Footer = () => (
    <div style={{ borderTop:"1px solid "+C.border, padding:"24px 24px 32px", textAlign:"center", background:C.surface }}>
      <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>© {currentYear} {COMPANY.name}</div>
      <div style={{ display:"flex", justifyContent:"center", gap:20, flexWrap:"wrap" }}>
        {[{label:"特定商取引法に基づく表記",key:"tokusho"},{label:"利用規約",key:"terms"},{label:"プライバシーポリシー",key:"privacy"}].map(item=>(
          <button key={item.key} onClick={()=>setLegalPage(item.key)} style={{ background:"none",border:"none",color:C.sub,cursor:"pointer",fontSize:12,fontFamily:F,textDecoration:"underline" }}>{item.label}</button>
        ))}
      </div>
    </div>
  );

  // ══ 法的ページ ════════════════════════════════════════════
  if (legalPage) {
    const LR=({label,value})=>(<div style={{borderBottom:"1px solid "+C.border,padding:"14px 0",display:"flex",gap:16,flexWrap:"wrap"}}><div style={{width:180,flexShrink:0,fontSize:13,fontWeight:600,color:C.sub}}>{label}</div><div style={{flex:1,fontSize:14,color:C.text,lineHeight:1.8,minWidth:180}}>{value}</div></div>);
    const LS=({title,children})=>(<div style={{marginBottom:32}}><h2 style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:14,paddingBottom:8,borderBottom:"2px solid "+C.accent+"33"}}>{title}</h2><div style={{fontSize:14,color:C.sub,lineHeight:2}}>{children}</div></div>);
    return (
      <div style={{ minHeight:"100vh", background:C.bg, fontFamily:F }}>
        <GlobalStyles/>
        <nav style={{ background:C.surface, borderBottom:"1px solid "+C.border, padding:"0 20px", display:"flex", alignItems:"center", height:52, position:"sticky", top:0, zIndex:100, boxShadow:C.shadow }}>
          <button onClick={()=>setLegalPage(null)} style={{ background:"none",border:"none",cursor:"pointer",color:C.muted,padding:4,display:"flex",alignItems:"center",gap:6,fontFamily:F,fontSize:13 }}><ArrowLeft size={18}/> 戻る</button>
        </nav>
        <div style={{ maxWidth:720, margin:"0 auto", padding:"36px 24px 64px" }}>
          {legalPage==="tokusho"&&<><h1 style={{fontSize:22,fontWeight:500,marginBottom:24}}>特定商取引法に基づく表記</h1><div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:"8px 24px",marginBottom:24}}><LR label="販売業者" value={COMPANY.name}/><LR label="サービス名" value={COMPANY.service}/><LR label="所在地" value={COMPANY.address}/><LR label="電話番号" value={COMPANY.tel}/><LR label="メールアドレス" value={COMPANY.email}/><LR label="販売価格" value={COMPANY.price}/><LR label="お支払い方法" value={COMPANY.payment}/><LR label="サービス提供時期" value={COMPANY.delivery}/><LR label="キャンセル・解約" value={COMPANY.cancel}/><LR label="返金・返品" value={COMPANY.returns}/></div></>}
          {legalPage==="terms"&&<><h1 style={{fontSize:22,fontWeight:500,marginBottom:24}}>利用規約</h1>{[{title:"第1条（サービスの内容）",body:"本サービスは、AIを活用したキャリア自己理解支援を提供します。AIによる分析・提案は参考情報であり、専門家によるコンサルティングの代替ではありません。"},{title:"第2条（禁止事項）",body:"法令違反・不正アクセス・AIへの不正操作・取得情報の無断転載を禁じます。"},{title:"第3条（免責事項）",body:"当社は、本サービスの利用により生じた損害について、当社の故意または重大な過失による場合を除き、責任を負いません。"},{title:"第4条（準拠法・管轄）",body:"本規約は日本法に準拠します。紛争については東京地方裁判所を専属的合意管轄とします。"},{title:"第5条（お問い合わせ）",body:"メールアドレス："+COMPANY.email}].map((a,i)=>(<LS key={i} title={a.title}><p style={{whiteSpace:"pre-wrap"}}>{a.body}</p></LS>))}</>}
          {legalPage==="privacy"&&<><h1 style={{fontSize:22,fontWeight:500,marginBottom:24}}>プライバシーポリシー</h1>{[{title:"1. 収集する情報",body:"・入力いただいた回答内容\n・アクセスログ（IPアドレス・ブラウザ種別・日時）"},{title:"2. 利用目的",body:"・本サービスの提供・改善\n・AIによる分析・レポート生成"},{title:"3. 第三者提供",body:"法令に基づく場合を除き、利用者の個人情報を第三者に提供しません。"},{title:"4. 業務委託先への情報送信",body:"・Groq API（Groq, Inc. / 米国）：AI分析\n・Vercel（Vercel, Inc. / 米国）：ホスティング"},{title:"5. データの保管",body:"データはブラウザのローカルストレージに保存されます。サーバー上に個人情報を保存していません。"},{title:"6. お問い合わせ",body:"メール："+COMPANY.email}].map((s,i)=>(<LS key={i} title={s.title}><p style={{whiteSpace:"pre-wrap"}}>{s.body}</p></LS>))}</>}
        </div>
        <Footer/>
      </div>
    );
  }

  // ══ HOME ══════════════════════════════════════════════════
  if (page==="home") {
    // tab: "note"(STEP1) | "stock" | "log" | "page"
    const goTo = (tab="note") => {
      setMypageInitialTab(tab);
      setPage("mypage");
      gtagPageView("/mypage", "PathNote - マイページ");
    };
    return (
      <div style={{ minHeight:"100vh", background:tokens.neutral.bg, fontFamily:F, display:"flex", flexDirection:"column" }}>
        <GlobalStyles/>
        <Nav onStart={()=>goTo("note")}/>
        <LandingPage onStart={goTo}/>
        <Footer/>
      </div>
    );
  }

  // ══ MYPAGE ════════════════════════════════════════════════
  if (page==="mypage") return <MyPage
    data={load()||{}}
    onBack={()=>setPage("home")}
    logoSrc={logoPathnote}
    initialTab={mypageInitialTab}
    onTabConsumed={()=>setMypageInitialTab("note")}
  />;

  return null;
}


// ══ MyPage ═══════════════════════════════════════════════════
function MyPage({ data, onBack, logoSrc, initialTab, onTabConsumed }) {
  const [tab, setTab] = useState(initialTab || "note");
  const [dailyNotes] = useState(data.dailyNotes || []);
  const [showInstallInvite, setShowInstallInvite] = useState(false);
  const [installHelp, setInstallHelp] = useState(false);
  const [installPromptReady, setInstallPromptReady] = useState(()=>Boolean(window.pathnoteInstallPrompt));
  const [profile, setProfile] = useState(()=>{
    try {
      const d = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      return d.profile || data.profile || { name:"PathNoteユーザー", avatar:null };
    } catch {
      return data.profile || { name:"PathNoteユーザー", avatar:null };
    }
  });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileDraftName, setProfileDraftName] = useState(profile?.name || "");
  const avatarInputRef = useRef(null);

  // ── v4 新フロー state（STEP1〜STEP6 + cycles） ─────────────────
  // データ構造（フェーズ1完成時の保存形）:
  //   { id, body, cycles: [{cycleIndex, level, nearWords, detail, summary, probe}, ...], closedAt, closedBy }
  const NOTE_DRAFT_KEY = "pathnote_note_draft_v4";
  const loadNoteDraft = () => {
    try { return JSON.parse(localStorage.getItem(NOTE_DRAFT_KEY) || "null"); } catch { return null; }
  };
  const initialDraft = loadNoteDraft();
  const [noteStep, setNoteStep] = useState(initialDraft?.noteStep || "step1");
  const [noteBody, setNoteBody] = useState(initialDraft?.noteBody || "");
  // 任意の気持ちタグ（null = 未選択）。書きかけにも保存される
  const [noteEmotion, setNoteEmotion] = useState(isValidEmotion(initialDraft?.noteEmotion) ? initialDraft.noteEmotion : null);
  // eslint-disable-next-line no-unused-vars
  const [noteCycles, setNoteCycles] = useState(initialDraft?.noteCycles || []);
  // STEP2 state
  const [nearCandidates, setNearCandidates] = useState(initialDraft?.nearCandidates || []); // AI生成された近い言葉候補
  const [nearSelected, setNearSelected] = useState(initialDraft?.nearSelected || null); // {text, type:"candidate"|"free"}
  const [nearFreeDraft, setNearFreeDraft] = useState(initialDraft?.nearFreeDraft || ""); // 「どれも違う」自由入力
  const [nearLoading, setNearLoading] = useState(false);
  const [nearError, setNearError] = useState(null);

  // STEP1→STEP2 or step_reception: 感情タグに応じてフローを分岐
  const goToStep2 = async () => {
    const body = noteBody.trim();
    if (!body) return;
    // ネガ感情タグ選択時 → STEP2「近い言葉」生成をスキップして受容ファーストへ
    if (NEGATIVE_EMOTIONS.has(noteEmotion)) {
      await goToStepReception();
      return;
    }
    setNearLoading(true);
    setNearError(null);
    setNearCandidates([]);
    setNearSelected(null);
    setNearFreeDraft("");
    setNoteStep("step2");
    gtagPageView("/note/step2", "PathNote - STEP2");
    try {
      const prompt = `あなたは心理学の知見を持つ傾聴のプロです。以下の本文を読んで、書き手が「自分でも気づいていなかった気持ち」を言葉にしてあげる候補を4〜5個生成してください。

【本文】
${body}

【最重要：表面の言い換えではなく、奥にある気持ちを読む】
本文に書かれた行為や状況（例：「資料作成を依頼された」「内部レビューしてもらえなかった」）を そのまま言い換えるのは絶対に禁止です。
それらの出来事の奥で、書き手が本当は何を望んでいたのか、何を大切にしたかったのか、を仮置きの言葉で示してください。

▼ 悪い例（本文の事実をそのまま繰り返している）
本文「資料作成を依頼されたのに内部レビューをしてもらえなかった」
× 「内部レビューを手伝ってほしかった」  ← ただの言い換え。本人はもう言っている
× 「一緒に作業してほしかった」          ← 表面的でズレている
× 「規約修正をサポートしてほしかった」  ← 本文の用語の繰り返し

▼ 良い例（奥にある気持ち・動機を仮置きで言葉にしている）
○ 「ちゃんと協力してほしかった」          ← 抱えていた孤独や責任感に名前をつける
○ 「ひとりで背負わずに済む形がよかった」  ← 望んでいた関係性に焦点
○ 「自分の頑張りをちゃんと見てほしかった」← 認められたかった気持ち
○ 「同じ温度で向き合ってほしかった」      ← 相手との温度差への違和感

【ルール】
- 「悲しい」「悔しい」「不安」のような単独の感情語は使わない
- 必ず "状況付き感情" にする
- 12〜22文字程度の短い動機文に揃える
- 4〜5個それぞれが別の切り口になるよう、視点（孤独感／認められたい／関係性／自分のあり方）を散らす
- 本人が共感して選びやすいよう、断定せず気持ちのニュアンスを表す
- 重複や類似表現は絶対に避ける（「協力してほしい」と「サポートしてほしい」は同じ）
- 文法的に自然な日本語にすること。声に出して読んでも違和感のない文にする

【文法的に絶対に避けること】
- 「誰かが協力してくれたかった」  ← ❌ 文法的に誤り
- 「相手に理解してもらえたかった」  ← ❌ 文法的に誤り
- 「〜してくれたかった」「〜してもらえたかった」「〜してくれてほしかった」のような言い回しは禁止
- 受身+願望の二重表現（「〜られたかった」）も禁止
- 願望は「〜してほしかった」「〜したかった」「〜でいたかった」「〜であってほしかった」のいずれかに揃える

【出力形式】必ず以下のJSONのみで返す（前後に説明を付けない）：
必ず4個の候補を出力すること。3個以下は絶対に不可。ユーザーが複数の選択肢から選べるよう、必ず4つの別々の切り口で提案すること。
{ "candidates": ["1つ目の候補", "2つ目の候補", "3つ目の候補", "4つ目の候補"] }`;
      // 不自然な日本語を補正する後処理
      const fixAwkwardJapanese = (text) => {
        let t = String(text || "").trim();
        // 「〜してくれたかった」→「〜してほしかった」
        t = t.replace(/(し)てくれたかった/g, "$1てほしかった");
        t = t.replace(/(し)てもらえたかった/g, "$1てほしかった");
        t = t.replace(/(し)てくれてほしかった/g, "$1てほしかった");
        t = t.replace(/(し)てもらえてほしかった/g, "$1てほしかった");
        // 「〜られたかった」→「〜してほしかった」（自然形にしづらいが、二重表現を解消）
        t = t.replace(/てもらえなかったかった/g, "てもらえなかった");
        // 主語の助詞ずれ（「誰かが…してほしかった」→「誰かに…してほしかった」）
        t = t.replace(/^誰かが([^、。]+?してほしかった)/g, "誰かに$1");
        t = t.replace(/^相手が([^、。]+?してほしかった)/g, "相手に$1");
        // 末尾の余分な記号
        t = t.replace(/[。．\s]+$/, "");
        return t;
      };
      // 本文に登場する固有名詞・名詞句（2文字以上のカタカナ/漢字連続）を抽出
      const extractDomainNouns = (src) => {
        const text = String(src || "");
        const matches = [];
        // カタカナ2文字以上
        (text.match(/[ァ-ヴー]{2,}/g) || []).forEach(m => matches.push(m));
        // 漢字2文字以上
        (text.match(/[一-龯々]{2,}/g) || []).forEach(m => matches.push(m));
        // 一般名詞・動詞末を除外（あまりに一般的なものはフィルタしない）
        const generic = new Set(["自分","相手","誰か","気持","思い","本当","場面","時間","今日","最近","状況","内容","結果","対応","関係","仕事","会議","会社","上司","部下","同僚","家族","友達","友人","気持ち","正直","本心","本音","内心"]);
        return matches.filter(m => !generic.has(m) && m.length >= 2);
      };
      const domainNouns = extractDomainNouns(body);
      // 候補が本文の固有名詞を含んでいたら、表面的な言い換えとして除外（強め）
      const isTooLiteral = (candidate) => {
        if (domainNouns.length === 0) return false;
        return domainNouns.some(noun => candidate.includes(noun));
      };
      // 候補リストを組み立てるヘルパー（後処理 + フィルタを共通化）
      const buildCandidateList = (res) => {
        const raw = Array.isArray(res?.candidates) ? res.candidates.filter(c=>typeof c === "string" && c.trim()) : [];
        const cleaned = Array.from(new Set(raw.map(fixAwkwardJapanese).filter(Boolean)));
        let lst = cleaned.filter(c => !isTooLiteral(c));
        if (lst.length === 0) lst = cleaned; // 固有名詞除外で全消えした場合は緩める
        return lst;
      };

      let res = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_HAIKU);
      let list = buildCandidateList(res);

      // 候補が3個未満なら最大1回だけ再試行（フローは止めない）
      if (list.length < 3) {
        try {
          const res2 = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_HAIKU);
          const list2 = buildCandidateList(res2);
          if (list2.length > list.length) list = list2;
        } catch {
          // 再試行失敗は無視して最初の結果で続ける
        }
      }

      if (list.length === 0) throw new Error("候補を生成できませんでした");
      setNearCandidates(list.slice(0, 5));
    } catch (e) {
      setNearError(e?.message || "候補の生成に失敗しました");
      // フォールバック: 最後の保険（PathNote の優しいトーンで、自由入力に誘導）
      setNearCandidates([
        "もう少し書き出してみたい",
        "別の角度から考えてみたい",
        "他の言葉を探したい",
        "ちょうど良い言葉がまだ見つからない",
      ]);
    } finally {
      setNearLoading(false);
    }
  };

  const handleNearSelect = (text, type = "candidate") => {
    setNearSelected({ text, type });
  };

  const goBackToStep1 = () => {
    setNoteStep("step1");
  };

  // ── ネガ路線 受容フロー関数 ─────────────────────────────────
  const goToStepReception = async () => {
    setNoteStep("step_reception");
    gtagPageView("/note/reception", "PathNote - 受容");
    setReceptionText(null);
    setReceptionError(null);
    setShowReceptionFeedback(false);
    setReceptionFeedback("");
    await generateReception();
  };

  const generateReception = async ({ correction = "" } = {}) => {
    setReceptionLoading(true);
    setReceptionError(null);
    try {
      const correctionLine = correction
        ? `\n書き手から補足がありました：「${correction}」\n→ この補足は"方向のヒント"として受け取ってください。補足の言葉をそのまま言い換えて返すのではなく、元の書いた内容と補足を統合して、その人の感覚をもう少し正確に言い直してください。抽象的な願いや結論に飛ばず、生の感覚の輪郭に留めてください。`
        : "";
      const prompt = `あなたはPathNoteという内省アプリのやさしい声です。
書き手の言葉を読んで、以下の2つだけを返してください。
あなたの役割は"分析する人"ではなく"保存する人（編集者）"です。正解を当てにいかず、その人の生の感覚をそっと残してください。

【書いた内容】
${noteBody}${correctionLine}

【返す内容】
1. message: 書き手の気持ちをそっと受け取る一言。
   - 書き手が実際に書いた言葉・状況に必ず触れること（一般論にしない）
   - アドバイスや分析は一切しない
   - 「なぜ？」を問わない
   - 断定しない（「〜を感じていたのかもしれません」などの仮説形で）
   - PathNoteの静かで寄り添う口調。臨床・コーチ口調にしない
   - 生の感覚・感情（モヤモヤ・引っかかり・困惑など）をそのまま言葉にする。きれいな願いや結論に変換して感情を薄めない
   - 感情から飛躍した抽象的な結論（「〜を求めていた」「〜を必要としていた」）に飛ばない。感覚の輪郭を少しだけ見せる程度に留める
   - 書き手が書いていない比喩・情景・イメージを足さない（「空白が顔に映る」等の詩的・創作的な表現は禁止）
   - 文面から一番素直に読める感情を拾う。深読み・凝った解釈より直読みを優先する
   - 補足があっても、その言葉をそのまま言い換えて返さない（オウム返し禁止）
   - トーンは書き手に合わせる：やわらかい入力（「なんだかなー」など）を強い感情語（「悔しさ」「憤り」）に上げない
   - 書き手が使いそうな平易な日常語で書く（「腑に落ちない」より「なんか引っかかった」など）
   - 元の文に相手への気遣いがあるなら、それを保つ。自分寄り・防御寄りに勝手に振らない
   - 60〜100文字程度

2. feeling: この気持ちに名前をつけるとしたら（12〜20文字の短い表現）
   - 本人の言葉に近い、具体的な状態文または感覚文
   - 「〜な感じ」「〜でいられなかった」「〜がもやもやした」「〜したかった気持ち」などの形
   - 一般的な感情語（「悲しい」「不安」「怒り」）だけにしない
   - 抽象的・ビジネス的な願い文にしない（「〜を求めていた気持ち」「〜への渇望」など）
   - 補足の言葉をそのままなぞらない。生の感覚ベース・平易な言葉で（例：補足「信頼されてない」→「信頼してもらえなかった感じ」）

【出力形式】必ず以下のJSONのみで返す：
{ "feeling": "12〜20文字の感情名", "message": "受容の一言" }`;
      const res = await callAIJSON(
        [{ role: "user", content: prompt + JSON_ONLY_INSTRUCTION }],
        MODEL_HAIKU
      );
      const feeling = String(res?.feeling || "").trim();
      const message = String(res?.message || "").trim();
      if (!feeling || !message) throw new Error("受容メッセージを生成できませんでした");
      const reception = { feeling, message };
      setReceptionText(reception);
      return reception;
    } catch (e) {
      setReceptionError(e?.message || "受容メッセージの生成に失敗しました");
      const fallback = {
        feeling: "うまく言葉にならない気持ち",
        message: `「${noteBody.slice(0, 30)}${noteBody.length > 30 ? "…" : ""}」という気持ちが、ここに届きました。`,
      };
      setReceptionText(fallback);
      return fallback;
    } finally {
      setReceptionLoading(false);
    }
  };

  // 受容テキストを確認して step_confirm_save へ進む
  const approveReception = () => {
    if (!receptionText) return;
    // feeling を nearSelected に入れる → ログのsubTagとして表示される
    setNearSelected({ text: receptionText.feeling, type: "ai_reception" });
    // reception message を summaryA として承認済みに設定 → closeNote でそのまま保存される
    setSummaryA({ text: receptionText.message, approved: true, revisions: [] });
    setNoteStep("step_confirm_save");
    gtagPageView("/note/confirm-save", "PathNote - 着地確認");
  };

  // 「少し違う」→ 1行補足を渡して1回だけ再生成 → 自動で step_confirm_save へ
  const submitReceptionFeedback = async () => {
    const fb = receptionFeedback.trim();
    if (!fb) return;
    const prevMsg = receptionText?.message || "";
    setShowReceptionFeedback(false);
    setReceptionFeedback("");
    const newReception = await generateReception({ correction: fb });
    setNearSelected({ text: newReception.feeling, type: "ai_reception" });
    setSummaryA({ text: newReception.message, approved: true, revisions: [{ feedback: fb, prevText: prevMsg }] });
    setNoteStep("step_confirm_save");
    gtagPageView("/note/confirm-save", "PathNote - 着地確認");
  };

  const goToStep3 = () => {
    if (!nearSelected) return;
    setNoteStep("step3");
    gtagPageView("/note/step3", "PathNote - STEP3");
  };

  // STEP3 state
  const [detailDraft, setDetailDraft] = useState(initialDraft?.detailDraft || ""); // STEP3 補足の自由入力（任意）
  const STEP3_QUESTION = "どの場面でそう感じましたか？";

  const goBackToStep2 = () => {
    setNoteStep("step2");
  };

  // STEP4 state（aレベル仮説整理）
  const [summaryA, setSummaryA] = useState(initialDraft?.summaryA || null); // {text, approved, revisions}
  const [summaryALoading, setSummaryALoading] = useState(false);
  const [summaryAError, setSummaryAError] = useState(null);
  const [showAFeedbackInput, setShowAFeedbackInput] = useState(false);
  const [aFeedbackDraft, setAFeedbackDraft] = useState("");

  // 純粋な断言パターンのみガード（全文への強制 hedge は廃止）
  const ensureHypotheticalTone = (text) => {
    let t = String(text || "").trim();
    if (!t) return t;
    t = t.replace(/あなたは([^。]*?)です。/g,      "あなたは$1なのかもしれません。");
    t = t.replace(/あなたは([^。]*?)だ。/g,        "あなたは$1なのかもしれません。");
    t = t.replace(/あなたは([^。]*?)なのです。/g,  "あなたは$1なのかもしれません。");
    t = t.replace(/だと思います。/g, "かもしれません。");
    return t;
  };

  // STEP4: AIで整理（aレベル）
  const generateSummaryA = async ({ feedback = "" } = {}) => {
    setSummaryALoading(true);
    setSummaryAError(null);
    try {
      const detailLine = detailDraft.trim() ? `本人の補足: ${detailDraft.trim()}` : "本人の補足: （なし）";
      const feedbackLine = feedback ? `\n直前の整理に対する違和感: ${feedback}\n→ この違和感を踏まえて整理し直してください。` : "";
      const prompt = `あなたは心理学の知見を持つ傾聴のプロです。以下の対話を読んで、書き手の気持ちをやさしく整理してください。

【本文】
${noteBody}

【近いと感じた言葉】
${nearSelected?.text || ""}

【補足】
${detailLine}${feedbackLine}

【ルール】
- 整理は以下の2層で書く：
  第1層：ユーザーが文中で明確に書いた事実・感情
    → 断定的に反映。「〜と感じられたのですね」「〜だったわけですね」「〜という思いがあったのですね」
    → この層には「かもしれません」を使わない
  第2層（任意）：言葉の奥にある動機・願いへの推測
    → 「〜のかもしれません」「〜のではないでしょうか」
    → 1文まで。省略してもよい
- 構成例：「あなたは[第1層]。その奥には[第2層]があったのかもしれません。」
- 「あなたは〜です」と断言しない（第2層への推測は仮説形のみ）
- 価値観タグ（安心感、納得感、誠実さ等）への翻訳はしない。本人の言葉に近い動機文として残す
- 80〜140文字程度。1〜2文。改行可
- 本人の言葉を1か所程度「」で短く引用してもよい

【出力形式】必ず以下のJSONのみで返す（前後に説明を付けない）：
{ "summary": "あなたは〜と感じられたのですね。その奥には〜があったのかもしれません。" }`;
      const res = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_SONNET);
      const text = ensureHypotheticalTone(res?.summary || "");
      if (!text) throw new Error("整理に失敗しました");
      setSummaryA(prev => ({
        text,
        approved: false,
        revisions: feedback ? [...(prev?.revisions || []), { feedback, prevText: prev?.text || "" }] : (prev?.revisions || []),
      }));
    } catch (e) {
      setSummaryAError(e?.message || "整理に失敗しました");
      // フォールバック
      const fallback = ensureHypotheticalTone(`「${nearSelected?.text || noteBody.slice(0, 30)}」という気持ちが、頭に残っていたのかもしれません。`);
      setSummaryA(prev => ({
        text: fallback,
        approved: false,
        revisions: feedback ? [...(prev?.revisions || []), { feedback, prevText: prev?.text || "" }] : (prev?.revisions || []),
      }));
    } finally {
      setSummaryALoading(false);
    }
  };

  const goToStep4 = async ({ skipped = false } = {}) => {
    if (skipped) setDetailDraft("");
    setNoteStep("step4");
    gtagPageView("/note/step4", "PathNote - STEP4");
    setSummaryA(null);
    setShowAFeedbackInput(false);
    setAFeedbackDraft("");
    await generateSummaryA();
  };

  const goBackToStep3 = () => {
    setNoteStep("step3");
    setSummaryA(null);
    setShowAFeedbackInput(false);
    setAFeedbackDraft("");
  };

  const submitAFeedback = async () => {
    const fb = aFeedbackDraft.trim();
    if (!fb) return;
    setShowAFeedbackInput(false);
    setAFeedbackDraft("");
    await generateSummaryA({ feedback: fb });
  };

  const approveSummaryA = () => {
    setSummaryA(prev => prev ? { ...prev, approved:true } : prev);
    enterStep5();
  };

  // ── STEP5/6: 深掘り3タイプ + bレベル整理ループ ─────────────────
  const [cycleIndex, setCycleIndex] = useState(initialDraft?.cycleIndex || 0); // 完了したbレベルサイクル数
  const [probeCandidates, setProbeCandidates] = useState(initialDraft?.probeCandidates || []); // {question, type}[]
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeError, setProbeError] = useState(null);
  const [selectedProbe, setSelectedProbe] = useState(initialDraft?.selectedProbe || null); // {question, type}
  const [probeAnswer, setProbeAnswer] = useState(initialDraft?.probeAnswer || "");
  const [summaryB, setSummaryB] = useState(initialDraft?.summaryB || null); // {text, approved, revisions}
  const [summaryBLoading, setSummaryBLoading] = useState(false);
  const [summaryBError, setSummaryBError] = useState(null);
  const [showBFeedbackInput, setShowBFeedbackInput] = useState(false);
  const [bFeedbackDraft, setBFeedbackDraft] = useState("");
  const [completedSummaries, setCompletedSummaries] = useState(initialDraft?.completedSummaries || []); // 承認済みsummaryのリスト（aと各b）
  const [insightDraft, setInsightDraft] = useState(initialDraft?.insightDraft || "");   // 今の気づき：入力中テキスト
  const [insights,     setInsights]     = useState(initialDraft?.insights     || []);   // 今の気づき：確定済みリスト

  // ── ネガ路線 受容フロー state ─────────────────────────────────
  const [receptionText, setReceptionText] = useState(initialDraft?.receptionText || null); // {feeling, message}
  const [receptionLoading, setReceptionLoading] = useState(false);
  const [receptionError, setReceptionError] = useState(null);
  const [receptionFeedback, setReceptionFeedback] = useState("");
  const [showReceptionFeedback, setShowReceptionFeedback] = useState(false);

  // STEP5: 深掘り質問候補の生成（感情タグに応じてネガ/ポジ路線を分岐）
  const generateProbes = async (currentCycle, unusedTypes = ["A", "B", "C"]) => {
    setProbeLoading(true);
    setProbeError(null);
    setSelectedProbe(null);
    setProbeAnswer("");
    try {
      const isNega = NEGATIVE_EMOTIONS.has(noteEmotion);
      const lastSummary = (completedSummaries[completedSummaries.length - 1]?.text) || summaryA?.text || "";

      // 角度（タイプ）別の問い定義 — unusedTypes に含まれるものだけを使用
      const typeDefsNega = {
        A: "A（本音の言葉）: その時、一番引っかかっていた言葉は何ですか？ / 心の中で何度も繰り返した言葉は？",
        B: "B（願い）: 本当は、どうしてほしかったですか？ / 自分が一番大切にしたかったことは何ですか？",
        C: "C（伝言）: 相手に一言だけ伝えられるなら、何と言いますか？ / 今の自分に一言かけるとしたら？",
      };
      const typeDefsPosi = {
        A: "A（感情深掘り）: 例 本当はどうしてほしかった？/どんな気持ちが一番強かった？/そのとき何を感じてた？",
        B: "B（状況整理）: 例 そのとき、何が気になっていましたか？/どの瞬間が一番引っかかった？/誰の言葉が気になった？",
        C: "C（外在化）: 例 もし友人が同じことで悩んでいたら、どう声をかける？/5年後の自分から見たら、今の自分にどう声をかける？",
      };
      const unusedTypeListNega = unusedTypes.map(t => `- ${typeDefsNega[t]}`).join("\n");
      const unusedTypeListPosi = unusedTypes.map(t => `- ${typeDefsPosi[t]}`).join("\n");
      const probeCount = unusedTypes.length;

      let prompt;
      if (isNega) {
        // ネガ路線専用: 未使用角度のみ（原因探し・自責を誘発する問いを排除）
        prompt = `あなたはPathNoteという内省アプリのやさしい声です。書き手がモヤモヤや疲れ・迷いを感じており、気持ちをもう少し見つめようとしています。
次の問いを${probeCount}つ提案してください。

【これまでの対話】
書いた内容: ${noteBody}
気持ちの名前: ${nearSelected?.text || ""}
${lastSummary ? `受容の言葉: ${lastSummary}` : ""}
${cycleIndex > 0 ? `これまでの深掘り: ${completedSummaries.slice(-2).map(s=>s.text).join(" / ")}` : ""}

【以下の角度から各1つずつ問いを出してください（計${probeCount}問）】
${unusedTypeListNega}

【絶対禁止（本音が出ない・自責を誘発する問い）】
- 「どの場面で〜」「どんな状況で〜」「いつから〜」（場面・原因探し）
- 「なぜそう感じた」「なぜ〜」「どうすれば良かった」（反省・改善の問い）
- 「誰が〜」「誰の〜」（他責・人物特定の問い）
- 「もし〜思い通りだったら」「もし〜がなかったら」（仮定・反実仮想）

【ルール】
- 各角度で1問ずつ。同じ角度の言い換えや再提示は禁止
- 14〜30文字程度の問いに揃える
- PathNoteのやわらかい、静かな口調で書く

【出力形式】必ず以下のJSONのみで返す（${probeCount}件）：
{ "probes": [ { "question":"...", "type":"A" }, ... ] }`;
      } else {
        // ポジ路線: 未使用角度のみ
        const cTypeRatio = Math.min(0.6, 0.2 + currentCycle * 0.15);
        prompt = `あなたは心理学の知見を持つ傾聴のプロです。書き手の対話を踏まえ、次の問いを${probeCount}つ提案してください。

【これまでの対話】
本文: ${noteBody}
近い言葉: ${nearSelected?.text || ""}
${detailDraft.trim() ? `補足: ${detailDraft.trim()}` : ""}
直前の整理: ${lastSummary}
${cycleIndex > 0 ? `これまでの深掘り: ${completedSummaries.slice(-2).map(s=>s.text).join(" / ")}` : ""}

【以下の角度から各1つずつ問いを出してください（計${probeCount}問）】
${unusedTypeListPosi}

【ルール】
- 各角度で1問ずつ。同じ角度の言い換えや再提示は禁止
- サイクル数=${currentCycle}。${cTypeRatio>=0.45 ? "C（外在化）を必ず含める" : "C（外在化）を1個含める"}
- 14〜34文字程度の問いに揃える
- 「奥底」「本質」「核心」などカウンセリング感の強い言葉は避け、日常的で軽い言葉を使う

【出力形式】必ず以下のJSONのみで返す（${probeCount}件）：
{ "probes": [ { "question":"...", "type":"A" }, ... ] }`;
      }

      const res = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_HAIKU);
      const ensureQuestionMark = (q) => {
        const trimmed = String(q || "").trim().replace(/[。．.、，,\s]+$/, "");
        if (!trimmed) return "";
        return /[？?]$/.test(trimmed) ? trimmed : trimmed + "？";
      };
      const list = Array.isArray(res?.probes)
        ? res.probes
            .filter(p=>p?.question && p?.type)
            .map(p=>({ ...p, question: ensureQuestionMark(p.question) }))
            .filter(p=>p.question)
            .slice(0, 4)
        : [];
      if (list.length === 0) throw new Error("問いを生成できませんでした");
      setProbeCandidates(list);
    } catch (e) {
      setProbeError(e?.message || "問い生成に失敗しました");
      const isNegaFb = NEGATIVE_EMOTIONS.has(noteEmotion);
      // フォールバック: unusedTypes に含まれる角度のみ表示
      setProbeCandidates((isNegaFb ? [
        { question:"その時、一番引っかかっていた言葉は何ですか？", type:"A" },
        { question:"本当は、どうしてほしかったですか？", type:"B" },
        { question:"相手に一言だけ伝えられるなら、何と言いますか？", type:"C" },
      ] : [
        { question:"本当はどうしてほしかったですか？", type:"A" },
        { question:"どの瞬間が一番引っかかりましたか？", type:"B" },
        { question:"もし友人が同じことで悩んでいたら、どう声をかけますか？", type:"C" },
      ]).filter(p => unusedTypes.includes(p.type)));
    } finally {
      setProbeLoading(false);
    }
  };

  const enterStep5 = async () => {
    setNoteStep("step5");
    gtagPageView("/note/step5", "PathNote - STEP5");
    setSummaryB(null);
    setShowBFeedbackInput(false);
    setBFeedbackDraft("");
    await generateProbes(cycleIndex);
  };

  const handleProbeSelect = (probe) => {
    setSelectedProbe(probe);
    setProbeAnswer("");
  };

  const submitProbeAnswer = async () => {
    if (!selectedProbe || !probeAnswer.trim()) return;
    setNoteStep("step6");
    gtagPageView("/note/step6", "PathNote - STEP6");
    await generateSummaryB();
  };

  // STEP6: bレベル整理（意味づけ寄り、仮説形）
  const generateSummaryB = async ({ feedback = "" } = {}) => {
    setSummaryBLoading(true);
    setSummaryBError(null);
    try {
      const isNega = NEGATIVE_EMOTIONS.has(noteEmotion);
      const lastSummary = summaryA?.text || "";
      const previousB = completedSummaries.filter(s=>s.level==="b").map(s=>s.text).slice(-2).join("\n");
      const feedbackLine = feedback ? `\n直前の整理に対する違和感: ${feedback}\n→ この違和感を踏まえて整理し直してください。` : "";
      let prompt;
      if (isNega) {
        // ネガ路線: 状況説明でなく「本音の結晶」として核心の一行を抽出
        prompt = `あなたはPathNoteという内省アプリのやさしい声です。
深掘りの対話から、書き手の一番深い本音を「結晶」として受け止めてください。

【これまでの対話】
書いた内容: ${noteBody}
気持ちの名前（受容時）: ${nearSelected?.text || ""}
受容の言葉: ${lastSummary}
${previousB ? `これまでの深掘り:\n${previousB}\n` : ""}今回の問い: ${selectedProbe?.question}
今回の答え: ${probeAnswer.trim()}${feedbackLine}

【「本音の結晶」とは】
書き手の言葉の中で一番深いところにある願いや欲求を、
「本当は〜したかった」「本当は〜してほしかった」「本当は〜でいたかった」
の形で受け止めた一行です。

【ルール】
- 会話の要約・経緯説明にしない（何が起きたかではなく、何を望んでいたか）
- 受容時の感情名（${nearSelected?.text || ""}）と同等以上の深さ・鋭さで
- 断定しない（「〜だったのかもしれません」で受け止める）
- アドバイスしない、分析しない
- 書き手が実際に使った言葉を必ず1つ引用する（「〜」で囲む）
- 書き手が書いていない情景・比喩・イメージを足さない（「ただ並んで同じ景色を見ながら」等の創作的な表現は禁止）
- 70〜130文字程度。1〜2文。

【出力形式】必ず以下のJSONのみで返す：
{ "summary": "本音の結晶（1〜2文）" }`;
      } else {
        prompt = `あなたは傾聴のプロです。書き手の対話から、その人が大切にしている価値観の輪郭をやさしく整理してください。

【整理の哲学】
整理は「問題の指摘」ではなく「意味の発見」を目的とする。
- 行動・感情・状態を、修正すべき欠陥として描写しない
- その背景にある価値観・誠実さ・願いを言葉にする
- 結果として今ある自分を受け止める方向で書く

【良い整理の例】
問い：「同じ立場の友人にどう声をかけますか？」
回答：「無理する必要はないと声をかけたい」
→ 良い整理：「友人には無理する必要はないと伝えたいと思ったのですね。周りの期待に応えたい気持ちや、これまで自分が守り抜いてきた誠実さがあるから、つい無理を重ねてしまう自分がいるのかもしれません。」

【避けるべき表現】
❌ 「優しさが自分には届かない」（欠落の指摘）
❌ 「本当は自分にも同じ言葉をかけたかった」（押し付け）
❌ 「もっと自分を大切にしていい」（指導的）
❌ 「〜できていない」「〜すべき」（判定的）

【目指すべき表現】
✓ 「〜したい気持ちがある」「〜してきた誠実さ」（価値観の肯定）
✓ 「〜してしまう自分がいるのですね」（受容的な受け止め）
✓ 「〜だからこそ、〜のかもしれません」（因果ではなく意味）

【本文】
${noteBody}

【近い言葉】
${nearSelected?.text || ""}

【補足】
${detailDraft.trim() || "（なし）"}

【直前の整理(aレベル)】
${lastSummary}

${previousB ? `【これまでの深掘り整理】\n${previousB}\n` : ""}
【今回の深掘り】
問い: ${selectedProbe?.question}
回答: ${probeAnswer.trim()}${feedbackLine}

【ルール】
- 整理は以下の2層で書く：
  第1層：ユーザーが今回の深掘りや本文で明確に語った事実・感情
    → 断定的に反映。「〜と感じられたのですね」「〜だったわけですね」「〜という思いがあったのですね」
    → この層には「かもしれません」を使わない
  第2層：その奥にある価値観・願いへの推測
    → 「〜を大切にしているのかもしれません」「〜を求めているのかもしれません」
    → 「〜だからこそ、〜なのかもしれません」のトーンを意識する
    → 1〜2文。押し付けない
- 構成例：「あなたは[第1層]。[価値観・誠実さ]があるから、[第2層]のかもしれません。」
- 文頭に「まだ整理途中ですが、」は付けない（画面側の見出しに既にある）
- 価値観タグへの直接の言い換えはしない。本人の動機文として残す
- 100〜180文字程度。1〜3文。改行可
- 直前のaレベル整理より、もう少し意味づけ寄りにする（押し付けはしない）

【出力形式】必ず以下のJSONのみで返す：
{ "summary": "あなたは〜と感じられたのですね。〜があるから、〜のかもしれません。" }`;
      }
      const res = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_SONNET);
      let text = ensureHypotheticalTone(res?.summary || "");
      if (!text) throw new Error("整理に失敗しました");
      // 見出しに既に「まだ整理途中ですが、〜」があるので本文側からは除去
      text = text.replace(/^まだ整理途中ですが、?\s*/u, "");
      setSummaryB(prev => ({
        text,
        approved: false,
        revisions: feedback ? [...(prev?.revisions || []), { feedback, prevText: prev?.text || "" }] : (prev?.revisions || []),
      }));
    } catch (e) {
      setSummaryBError(e?.message || "整理に失敗しました");
      const fallback = `「${selectedProbe?.question.replace(/[？?]$/, "")}」という問いに対して、あなたが書いてくれた「${probeAnswer.trim().slice(0, 24)}」が、いまの自分にとって大切にしたいことのヒントになりそうです。`;
      setSummaryB(prev => ({
        text: fallback,
        approved: false,
        revisions: feedback ? [...(prev?.revisions || []), { feedback, prevText: prev?.text || "" }] : (prev?.revisions || []),
      }));
    } finally {
      setSummaryBLoading(false);
    }
  };

  const submitBFeedback = async () => {
    const fb = bFeedbackDraft.trim();
    if (!fb) return;
    setShowBFeedbackInput(false);
    setBFeedbackDraft("");
    await generateSummaryB({ feedback: fb });
  };

  const approveSummaryB = async () => {
    if (!summaryB) return;
    const approved = { ...summaryB, approved:true };
    setSummaryB(approved);
    // 承認済みリストに追加
    const cycleEntry = {
      cycleIndex,
      level: "b",
      probe: { question:selectedProbe?.question, questionType:selectedProbe?.type, answer:probeAnswer.trim(), answerType:"free" },
      summary: { text: approved.text, approved:true, revisions: approved.revisions || [] },
    };
    setCompletedSummaries(prev => [...prev, { level:"b", text:approved.text, cycleIndex }]);
    const updatedCycles = [...noteCycles, cycleEntry];
    setNoteCycles(updatedCycles);
    setCycleIndex(prev => prev + 1);
    // 次のサイクル（STEP5）へ — stale closure 回避のため updatedCycles を渡す
    await enterStep5Next(cycleIndex + 1, updatedCycles);
  };

  const enterStep5Next = async (nextCycle, allCycles = noteCycles) => {
    // 全角度（A/B/C）を使い切ったら深掘りを終了して保存
    const usedTypes = new Set(allCycles.map(c => c.probe?.questionType).filter(Boolean));
    const unusedTypes = ["A", "B", "C"].filter(t => !usedTypes.has(t));
    if (unusedTypes.length === 0) {
      closeNote("depth_complete", allCycles);
      return;
    }
    setNoteStep("step5");
    setSelectedProbe(null);
    setProbeAnswer("");
    setSummaryB(null);
    setShowBFeedbackInput(false);
    setBFeedbackDraft("");
    await generateProbes(nextCycle, unusedTypes);
  };

  const closeNote = (closedBy = "user_choice", cyclesOverride = null) => {
    // ノートを保存
    try {
      const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      const cycle0 = {
        cycleIndex: 0,
        level: "a",
        nearWords: {
          question: "近い感覚はありますか？",
          candidates: nearCandidates,
          selected: nearSelected?.text,
          selectedType: nearSelected?.type,
        },
        detail: {
          question: STEP3_QUESTION,
          answer: detailDraft.trim(),
          skipped: !detailDraft.trim(),
        },
        summary: {
          text: summaryA?.text,
          approved: !!summaryA?.approved,
          revisions: summaryA?.revisions || [],
        },
      };
      const note = {
        id: "note_" + Date.now(),
        body: noteBody,
        emotion: isValidEmotion(noteEmotion) ? noteEmotion : null,
        cycles: [cycle0, ...(cyclesOverride ?? noteCycles)],
        insights: insights.filter(i => i.content.trim()),
        closedAt: new Date().toISOString(),
        closedBy,
      };
      const next = [...(stored.notesV4 || []), note];
      localStorage.setItem("pathnote_mvp_v1", JSON.stringify({ ...stored, notesV4: next, savedAt: new Date().toISOString() }));
    } catch {}
    // リセット
    setNoteStep("done");
  };

  const startNewNote = () => {
    setNoteStep("step1");
    gtagPageView("/note/step1", "PathNote - STEP1");
    setNoteBody("");
    setNoteEmotion(null);
    setNoteCycles([]);
    setNearCandidates([]);
    setNearSelected(null);
    setNearFreeDraft("");
    setNearError(null);
    setDetailDraft("");
    setSummaryA(null);
    setSummaryAError(null);
    setShowAFeedbackInput(false);
    setAFeedbackDraft("");
    setCycleIndex(0);
    setProbeCandidates([]);
    setSelectedProbe(null);
    setProbeAnswer("");
    setSummaryB(null);
    setSummaryBError(null);
    setShowBFeedbackInput(false);
    setBFeedbackDraft("");
    setCompletedSummaries([]);
    setInsightDraft("");
    setInsights([]);
    setReceptionText(null);
    setReceptionError(null);
    setReceptionFeedback("");
    setShowReceptionFeedback(false);
  };

  // ── 感情タグ ホバー管理 ───────────────────────────────────
  const [hoveredEmotion, setHoveredEmotion] = useState(null);
  // ── STEP2 候補カード ホバー管理 ──────────────────────────────
  const [hoveredCandidate, setHoveredCandidate] = useState(null);

  // ── ノートを閉じる確認 ─────────────────────────────────────
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const hasSomethingToSave = !!(summaryA?.approved || completedSummaries.length > 0);
  const requestCloseNote = () => setShowCloseConfirm(true);
  const closeWithSave = () => {
    setShowCloseConfirm(false);
    closeNote("user_choice");
  };
  const closeWithoutSave = () => {
    setShowCloseConfirm(false);
    startNewNote();
    setNoteStep("step1");
  };
  const cancelClose = () => setShowCloseConfirm(false);

  // 進捗インジケータ用のステップ番号
  const STEP_LABELS = { step1:1, step2:2, step3:3, step4:4, step5:5, step6:6 };
  const currentStepNumber = STEP_LABELS[noteStep] || 0;

  // STEPヘッダー（進捗 + 閉じるアイコン）
  const NoteFlowHeader = () => {
    if (!currentStepNumber) return null;
    // ネガ路線の深掘り（STEP5/6）はカウンター非表示、× ボタンのみ
    const isNegaDeepDive = NEGATIVE_EMOTIONS.has(noteEmotion) && (noteStep === "step5" || noteStep === "step6");
    if (isNegaDeepDive) {
      return (
        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
          <button
            onClick={requestCloseNote}
            aria-label="ノートを閉じる"
            style={{ width:28, height:28, borderRadius:8, border:"1px solid "+C.border, background:C.surface, color:C.muted, cursor:"pointer", fontSize:14, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center", padding:0, fontFamily:F }}
          >
            ×
          </button>
        </div>
      );
    }
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
          {[1,2,3,4,5,6].map(n => (
            <span key={n} style={{
              width: n === currentStepNumber ? 18 : 6,
              height: 6,
              borderRadius: 3,
              background: n <= currentStepNumber ? C.accent : C.border,
              transition: "all 0.25s ease",
            }}/>
          ))}
          <span style={{ marginLeft:6, fontSize:11, color:C.muted, fontWeight:500 }}>STEP {currentStepNumber} / 6</span>
        </div>
        <button
          onClick={requestCloseNote}
          aria-label="ノートを閉じる"
          style={{ width:28, height:28, borderRadius:8, border:"1px solid "+C.border, background:C.surface, color:C.muted, cursor:"pointer", fontSize:14, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center", padding:0, fontFamily:F }}
        >
          ×
        </button>
      </div>
    );
  };

  // ── 今の気づき：確定処理 ──────────────────────────────────────
  const commitInsight = () => {
    const trimmed = insightDraft.trim();
    if (trimmed.length < 2) return;
    setInsights(prev => [...prev, {
      id: "insight_" + Date.now(),
      content: trimmed,
      timestamp: new Date().toISOString(),
      step_context: noteStep,
    }]);
    setInsightDraft("");
  };

  // ── 自分ログ：notesV4 を時系列で表示 ─────────────────────────
  const loadNotesV4 = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      return stored.notesV4 || [];
    } catch { return []; }
  };
  const [logNotes, setLogNotes] = useState(loadNotesV4);
  // 自分ログのカードをタップすると、このIDに紐づくノート詳細をポップアップ表示する
  const [popupNoteId, setPopupNoteId] = useState(null);
  // 自分ログタブのフィルタ：null = すべて、それ以外は emotion 値
  const [logFilter, setLogFilter] = useState(null);
  // 自分ログの月セレクタ：null = 最新月（logNotes から自動決定）
  const [logSelectedYM, setLogSelectedYM] = useState(null);
  const [deleteTargetNoteId, setDeleteTargetNoteId] = useState(null);
  // タブを開くたびに最新を読み込む（他タブで保存された場合に備える）
  useEffect(()=>{
    if (tab === "log") setLogNotes(loadNotesV4());
  }, [tab]);

  const deleteNote = (noteId) => {
    try {
      const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      const next = (stored.notesV4 || []).filter(n => n.id !== noteId);
      localStorage.setItem("pathnote_mvp_v1", JSON.stringify({ ...stored, notesV4: next, savedAt: new Date().toISOString() }));
      setLogNotes(next);
      setPopupNoteId(null);
      setDeleteTargetNoteId(null);
      // stock集計のキャッシュをリセット（次にstockタブを開いたとき再集計される）
      setStockAgg(null);
    } catch {}
  };

  const formatNoteDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("ja-JP", { year:"numeric", month:"long", day:"numeric" });
    } catch { return ""; }
  };

  // マイページ統計
  const computeMyStats = () => {
    const notes = (Array.isArray(logNotes) ? logNotes : []);
    const totalNotes = notes.length;
    // 深掘り回数：各 cycle のうち level==="b"（STEP6 整理）の合計。
    const totalDeepDives = notes.reduce((sum, n) => sum + ((n.cycles || []).filter(c => c.level === "b").length), 0);
    // 言葉の数：全ノートの cycle のうち summary.approved===true をカウント。
    // 1ノートで複数 cycle がある場合、それぞれを 1 つの「育った言葉」として数える。
    const totalWords = notes.reduce((sum, n) => sum + ((n.cycles || []).filter(c => c.summary?.approved === true).length), 0);
    // 向き合った日：closedAt/createdAt を YYYY-MM-DD 単位で重複排除した数。
    const dayKeys = new Set();
    for (const n of notes) {
      const t = new Date(n.closedAt || n.createdAt || 0).getTime();
      if (t > 0) {
        const d = new Date(t);
        dayKeys.add(`${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`);
      }
    }
    const engagedDays = dayKeys.size;
    const allDates = notes.map(n => new Date(n.closedAt || n.createdAt || 0).getTime()).filter(t => t > 0);
    const firstDate = allDates.length ? new Date(Math.min(...allDates)) : null;
    const lastDate = allDates.length ? new Date(Math.max(...allDates)) : null;
    const continuingDays = firstDate ? Math.max(1, Math.floor((Date.now() - firstDate.getTime()) / 86400000) + 1) : 0;
    // 直近6週：今週含む 6 週間について、各週に承認された summary 数を集計。
    // 週は月曜始まり（日本のビジネス慣習）。各 cycle の summary.approved を closedAt/createdAt の週に振り分ける。
    const last6Weeks = (() => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      // 今週の月曜0時を求める：日曜(0) は 6日前、月曜(1) は 0日前、...、土曜(6) は 5日前。
      const daysSinceMonday = (today.getDay() + 6) % 7;
      const thisMonday = new Date(today.getTime() - daysSinceMonday * 86400000);
      thisMonday.setHours(0, 0, 0, 0);
      const buckets = [];
      for (let i = 5; i >= 0; i--) {
        const weekStart = new Date(thisMonday.getTime() - i * 7 * 86400000);
        const weekEnd = new Date(weekStart.getTime() + 7 * 86400000);
        const key = `${weekStart.getFullYear()}-${weekStart.getMonth() + 1}-${weekStart.getDate()}`;
        const label = `${weekStart.getMonth() + 1}/${weekStart.getDate()}週`;
        let count = 0;
        for (const n of notes) {
          const t = new Date(n.closedAt || n.createdAt || 0).getTime();
          if (t < weekStart.getTime() || t >= weekEnd.getTime()) continue;
          count += (n.cycles || []).filter(c => c.summary?.approved === true).length;
        }
        buckets.push({ key, label, count });
      }
      return buckets;
    })();
    return { totalNotes, totalDeepDives, totalWords, engagedDays, firstDate, lastDate, continuingDays, last6Weeks };
  };

  // ── 自分の言葉タブ集計 (v4 §13) ─────────────────────────────
  // データ構造: { currentSelf:{headline,description,recentWords[]}, newlyEmerged:[{iconHint,title,description}], valueCloud:[{keyword,count}], strengths:[{description,count}], wordChange, ... }
  // localStorage にキャッシュして、ページ再読み込み時の不要な再集計を防ぐ
  const [stockAgg, setStockAgg] = useState(()=>{
    try {
      const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      return stored.stockAgg || null;
    } catch { return null; }
  });
  // 各セクションの「すべて見る/もっと見る」展開状態
  // stockExpand: 旧UIで使っていたインライン展開フラグ。現状は使われていないが、将来必要になる可能性に備えて構造ごと残す。
  // eslint-disable-next-line no-unused-vars
  const [stockExpand, setStockExpand] = useState({ newlyEmerged:false, valueCloud:false, strengths:false, wordChange:false });
  // newlyEmerged カード詳細モーダル管理
  const [neDetailCard, setNeDetailCard] = useState(null);      // 表示中カード or null
  const [neDetailExpandedBodies, setNeDetailExpandedBodies] = useState({}); // 原文展開: {noteId: bool}
  // 「もっと見る」をタップしたときに、全件を表示するモーダルを開くためのキー。
  // "newlyEmerged" | "valueCloud" | "strengths" | null。自分ログの popupNote と同じ表現スタイルで描画する。
  const [stockModalKey, setStockModalKey] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState(null);
  // ローディング中の段階メッセージ index（0=言葉を集めて, 1=見つめて, 2=整えて, 3=もうすぐ）
  const [stockLoadStage, setStockLoadStage] = useState(0);

  // ローディング中だけタイマーで段階メッセージを切替（3s, 7s, 12s）
  useEffect(() => {
    if (!stockLoading) {
      setStockLoadStage(0);
      return;
    }
    const t1 = setTimeout(() => setStockLoadStage(1), 3000);
    const t2 = setTimeout(() => setStockLoadStage(2), 7000);
    const t3 = setTimeout(() => setStockLoadStage(3), 12000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [stockLoading]);

  const collectApprovedSummaries = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
      const notes = stored.notesV4 || [];
      const items = [];
      notes.forEach(note => {
        (note.cycles || []).forEach(c => {
          if (c?.summary?.approved && c.summary.text) {
            items.push({
              noteId: note.id,
              level: c.level || "a",
              text: c.summary.text,
              createdAt: note.closedAt || note.createdAt || new Date().toISOString(),
              body: note.body,
              probe: c.probe || null,
            });
          }
        });
      });
      return { notes, items };
    } catch {
      return { notes: [], items: [] };
    }
  };

  const runStockAggregation = async () => {
    const { notes, items } = collectApprovedSummaries();
    if (items.length === 0) {
      setStockAgg({ newlyAdded:[], values: [], strengths: [], wordChange: [], noteCount: 0, approvedCount: 0, computedAt: Date.now() });
      return;
    }
    setStockLoading(true);
    setStockError(null);
    try {
      // ── 2トラック分割 ────────────────────────────────────────
      // 持続トラック: 承認済み全件（全履歴）
      // 直近トラック: 直近5ノート（件数固定、カレンダー期間ではなく件数で切る）
      const distinctNoteIds = [...new Set(items.map(i => i.noteId))];
      const recentNoteIdSet = new Set(distinctNoteIds.slice(-5));
      const recentItems = items.filter(i => recentNoteIdSet.has(i.noteId));
      // 直近5ノート以前の件数（wordChange セクション用: 0なら空配列を返す）
      const olderCount = items.filter(i => !recentNoteIdSet.has(i.noteId)).length;
      // 「前」ラベルを実データの日付から計算（固定 "1ヶ月前" を廃止）
      const olderNoteItems = items.filter(i => !recentNoteIdSet.has(i.noteId));
      const olderMs = olderNoteItems.map(i => new Date(i.createdAt).getTime()).filter(t => !isNaN(t));
      const earliestOlderMs = olderMs.length > 0 ? Math.min(...olderMs) : null;
      const latestRecentMs = recentItems.length > 0
        ? Math.max(...recentItems.map(i => new Date(i.createdAt).getTime()).filter(t => !isNaN(t)))
        : Date.now();
      const daysDiff = earliestOlderMs ? Math.round((latestRecentMs - earliestOlderMs) / 86400000) : 0;
      const formatBeforeLabel = (days) => {
        if (days < 21) return null; // 3週間未満は変化として出さない
        if (days < 35) return "3週間前";
        if (days < 50) return "1ヶ月前";
        if (days < 80) return "2ヶ月前";
        if (days < 110) return "3ヶ月前";
        if (days < 140) return "4ヶ月前";
        if (days < 200) return "半年前";
        return `${Math.round(days / 30)}ヶ月前`;
      };
      const wordChangeBeforeLabel = formatBeforeLabel(daysDiff);
      // 全NoteIDセット（sourceNoteIds 検証用）
      const validNoteIdSet = new Set(items.map(i => i.noteId));
      // 持続トラック: 全件をノート単位でグループ化（日付付き）
      const noteGroupMap = {};
      items.forEach(item => {
        if (!noteGroupMap[item.noteId]) {
          noteGroupMap[item.noteId] = { date: (item.createdAt || "").slice(0, 10), summaries: [] };
        }
        noteGroupMap[item.noteId].summaries.push(item.text);
      });
      const persistentPromptText = Object.entries(noteGroupMap)
        .map(([nid, { date, summaries }]) =>
          `[${nid}] (${date})\n${summaries.map(s => `  - ${s}`).join("\n")}`)
        .join("\n");
      // 直近トラック: 直近5ノートのサマリー
      const recentPromptText = recentItems.map(i => `[${i.noteId}] (${i.level}) ${i.text}`).join("\n");
      const totalNotes = distinctNoteIds.length;
      const recentNoteCount = recentNoteIdSet.size;

      const prompt = `あなたは "傾聴のプロ" です。サマリ作成者ではなく、書き手の言葉を尊重する役。
書き手の承認済みの言葉（仮説形の整理）が${items.length}件（${totalNotes}ノート分）あります。
これらは "素材" として渡します。あなたの仕事は「抽出・要約」ではなく「引用・再構成」です。

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【全履歴サマリー — 持続トラック（全${totalNotes}ノート）】
[noteId] (日付YYYY-MM-DD) の形式でノートごとに列挙します。
C（繰り返し現れる言葉）・D（自然とやっていること）・A（今のあなたを一言で）の主信号はこの全体を参照してください。

${persistentPromptText}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【直近${recentNoteCount}ノートのサマリー — 最近トラック】
最新${recentNoteCount}件のノートのみ。B（最近、新しく見えてきたこと）・E（言葉の変化）はこのセクションを参照してください。

${recentPromptText}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【最重要思想】
PathNote は「翻訳しない・ユーザーの言葉のまま育てる」サービスです。
- 抽象名詞への翻訳禁止
- ユーザーが書いた **具体的な動詞・状況・気持ち** を必ず1つ含める
- 一般論・自己啓発本風の表現は禁止
- その人固有の特徴が立ち上がる表現にする

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【絶対に使ってはいけない語彙（禁止リスト）】
これらを含む単語は出力に絶対入れない：
- 強み / 能力 / スキル / ポテンシャル
- 活かす / 大切にする（"〜する人"の型で安易に使わない）
- 効率 / リソース / モチベーション
- 適切 / 最適 / 効果的
- 自己実現 / 自己肯定 / 自己成長
- 成長 / 貢献 / 向上 / 発揮
- チーム / 組織 / マネジメント / コミュニケーション
- ビジネス / プロジェクト / タスク / パフォーマンス

これらを使うと "自己啓発本のサマリー" 風になり失格。

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【悪い例 — 絶対にこういう出力をしない】
× 「自分の強みを大切にする人」（"強み" 禁止語＋"大切にする" 凡庸）
× 「効率を重視する人」（"効率" 禁止語）
× 「適切なタスク割り当てを望んでいるようです」（"適切" "タスク" ビジネス書風）
× 「自己成長を望むようです」（"自己成長" 禁止語）
× 「コミュニケーションを大切にする人」（禁止語の塊）

【良い例 — 状況や動詞に根ざす】
◯ 「ちゃんと届く形で伝えたい人」（"届く"=具体動詞、"ちゃんと"=固有な言い回し）
◯ 「他人より自分の納得を優先する人」（"優先する"=動作の選択）
◯ 「迷ったら一度立ち止まる人」（"立ち止まる"=具体的な行動）
◯ 「自分の声を届けたい気持ちを持つ人」（具体状況＋願いの形）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【最重要：ネガティブな状況を "願い・大事にしていること" に再構成する】

ユーザーが書いたネガティブな状況（〜できなかった、〜してくれなかった、〜が辛かった、
〜を引きずる、〜を飲み込む、〜を我慢する 等）を、そのままレッテル化して
"ネガティブな自己像" を提示してはいけません。

PathNote の目的:
  ユーザーが「自分はこういうことを大事にしてたんだ」と気づくこと。
  自分にネガティブな自己レッテルを貼る場所ではなく、
  自分が何を大切にしているかを優しく映し出す場所。

【出力時の基本姿勢】
- 「〜できなかった / 〜してくれなかった」のような "不足の表現" は使わない
  → その裏にある「〜したい / 〜してほしい」という願いとして表す
- 「引きずる / 飲み込む / 我慢する / 押し殺す / こじらせる」のような
  重い動詞・暗いトーンは使わない
- 「あなたが大事にしていること」を表に出す
- 願いとして表すと、自然と仮説形 + 優しいトーンになる

【絶対NGな自己ラベル形】
× 「誰も反応しなかった場面を引きずっている人」
× 「断れない人」
× 「言えなかった人」
× 「飲み込んできた人」
× 「ずっと我慢している人」
× 「うまく言えなかったことを抱えている人」

【望ましい "願い" への再構成】
◯ 「人と通じ合うことを大事にしている人」
◯ 「誠実に応えたい気持ちを持つ人」
◯ 「自分の声を届けたい人」
◯ 「相手に伝わる形を探している人」
◯ 「ちゃんと向き合いたい気持ちが強い人」

【カードタイトル・説明文の再構成例】
× 「誰も反応しなかったことへの違和感」
× 「もっと真剣に考えてほしい言葉を、会議で飲み込んでいた」
× 「言いたいことを言えなかった場面の重さ」
◯ 「人と通じ合いたい気持ち」
◯ 「自分の意見をちゃんと届けたい気持ちがあったようです」
◯ 「ちゃんと伝わる形を求めているようです」

【引用と再構成】
- ユーザーが書いた**具体的な状況（場面・人物・出来事）は引用してOK**
- それを **「大事にしたい願い」のフレームに置き換える**
  × 「会議で言いきれなかった言葉を探す人」  (不足を強調)
  ◯ 「会議でも自分の声を届けたい人」        (願いに変換)
  ◯ 「会議でちゃんと伝わる形を探している人」(願いに変換)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【表現スタイルのルール（読みやすさ）】
- 凝った比喩・詩的な抽象表現は禁止
  × 「沈黙の裏に見えない『伝わっているか』」（メタファーが2重で読みにくい）
  × 「夜の影に潜む願いの輪郭」（詩的すぎて意味不明）
  ◯ 「伝わっているかを確かめたい気持ち」（素直で読みやすい）
- 鍵括弧「」は引用1か所まで、ネスト禁止
  × 「沈黙の裏に見えない『伝わっているか』」（ネスト「『...』」）
  × 「『〜』を『〜』する人」（複数引用）
  ◯ 「『伝わっているか』を確かめたい人」（引用1か所）
- 一文で2つ以上の主題を詰め込まない（書き手が混乱する）
- 短く・素直に・読みやすく

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【セクション別ルール】

(A) 今のあなたを一言で（currentSelf）
【参照】主に【全履歴サマリー】を根拠にしてください。【直近ノート】のテーマは2割程度の補助情報として軽く加味するだけにし、数本の直近ノートで全体像を上書きしないこと。
- headline: **12〜18文字を厳守**。"〜する人" "〜したい人" のシンプルな **願いの形**で体言止め
  - **必ずユーザーのノートに出てきた具体的な状況や場面を含める**
  - ただし状況は引用しつつ、"願い・大事にしていること" のフレームに置き換える
  - 抽象的な一般論禁止 / ネガティブな自己ラベル禁止
  - 文として完結させ、不自然な切れ方は避ける
  - 【禁止される冗長表現】「気持ちを持つ」「願いを持つ」のような名詞化は使わない:
    × 「自分の判断で動きたい気持ちを持つ人」 ◯ 「自分の判断で動きたい人」
    × 「人と通じ合いたいという願いを持つ人」 ◯ 「人と通じ合いたい人」
    × 「自分のペースを求めている気持ちの人」 ◯ 「自分のペースを求めている人」
  - 良い例:
    ◯「自分の判断で動きたい人」
    ◯「人と通じ合いたい人」
    ◯「自分のペースで進めたい人」
    ◯「誠実に応えたい人」
  ◯「自分の声を届けたい人」
  ◯「人と通じ合うことを大事にしている人」
  ◯「ちゃんと向き合いたい気持ちが強い人」
  × 「会議で言いきれなかった言葉を探す人」（不足の強調 → 願いに変換すべき）
  × 「断れない人」「飲み込んできた人」（自己ラベル化）
- description: 「。」で終わる完結文、30〜70文字
  - ユーザーのsummary から具体状況は引用しつつ、"願い" の形で描写
  - 「〜したいようです」「〜が見えてきます」「〜を求めているようです」の優しい仮説形
  ◯「『ちゃんと届く形で伝えたい』という気持ちが何度も出てきています。」
  ◯「会議や雑談の場面でも、自分の意見を届けたい気持ちがありそうです。」
  × 「会議で言いきれなかった言葉を残したいようです。」（不足にフォーカス）
- recentWords: 4〜6個。ノートに繰り返し出てきた**短い名詞句**（3〜8文字）
  - 抽象タグ（"納得感"等）への翻訳ではなく、本人の言葉を残す
  - ★ 語句は文法的に完結した形のみ。助詞（の／を／に／は／が／と／も／で／へ／や）で終わる未完結な句は禁止
  ◯「腑に落ちない」「誠実に向き合う」「安心感」「自分らしさ」「通じ合えた」
  × 「向き合いたいも」（「も」で終わる）「やりがいを」（「を」で終わる）「自分にしか」（未完結）

(B) 最近、新しく見えてきたこと（newlyEmerged）
【参照】【直近ノートのサマリー】のみを参照してください（全履歴は参照しない）。
- 直近${recentNoteCount}ノートに新しく登場した気づきを 3〜4 件
- 各カードに { iconHint, title, description }
  - iconHint: "shield"/"leaf"/"people"/"compass"/"heart"/"sparkle"/"book"/"clock" のいずれか
  - title: **12〜18文字以内を厳守**（UI の表示崩れ防止）。ユーザーsummaryから繰り返し出てきた **具体的なフレーズを "気持ち・願い" として再構成**
    - 句点なし、体言止め or 自然な名詞句（"〜気持ち" "〜願い" "〜場面" 等）
    - 「新しく見えてきた言葉」のような汎用語は禁止
    ◯「人と通じ合いたい気持ち」
    ◯「自分の声を届けたい願い」
    ◯「ちゃんと伝わる形への気づき」
    × 「会議で言いきれなかった言葉の重さ」（重い／不足の強調）
    × 「自分の納得を後回しにしてきた場面」（自己ラベル化）
  - description: 「。」で終わる完結文、20〜45文字
    - 仮説形「〜のかもしれません」「〜が見えてきます」「〜を求めているようです」「〜があったようです」
    ◯「自分の意見をちゃんと届けたい気持ちがあったようです。」
    ◯「相手に伝わる形を求めているようです。」
    × 「うまく言えなかった気持ちが、後でじわじわ残ることに気づいたようです。」（重い）
  - sourceNoteIds: この気持ちに関連が感じられるノートの noteId を 1〜3件の配列で返す
    - 素材一覧の [ ] 内にある ID をそのまま使う（改変しない）
    - 断定的な根拠ではなく「関連性が感じられる」程度の選択でよい
    - 例: "sourceNoteIds": ["note-abc123", "note-def456"]

(C) 繰り返し出てきた価値観（valueCloud）
【参照】【全履歴サマリー】全体を対象に集計してください（直近5ノートだけを見ない）。
- 5〜12個の **短い名詞句**（3〜10文字）+ count（ノート数）
- ユーザーのsummary に出てきた言葉のまま使う（"納得感"等の抽象タグへ変換しない）
- count は「そのキーワードが含まれる distinct ノートの数」（延べ出現回数ではなくノート数で重みづけ）
- 同一日付（YYYY-MM-DD）のノートは、同じキーワードにつき最大1カウント
- 採用ライン: 3ノート以上に出現したもののみ含める（少数ノートにしか出ていない言葉は除外）
- 降順で並べる
- ★ 語句は文法的に完結した形のみ抽出すること：
  - 名詞・名詞句、動詞の終止形、形容詞の終止形、意味のかたまりとして自然に読めるフレーズに限定
  - 助詞（の／を／に／は／が／と／も／で／へ／や）で終わる未完結な句は絶対禁止
  - 連体形・連用形で後ろに語が必要な形は禁止（「向き合いたい」はOK、「向き合いたいも」はNG）
  - 元のノート本文から切り出す際は、自然な意味のまとまり単位で切ること
  ◯「腑に落ちない」「通じ合えた」「誇らしさ」「安心感」「自分らしさ」「誠実に向き合う」
  × 「向き合いたいも」（「も」で終わる）「やりがいを」（「を」で終わる）「自分にしか」（未完結）

(D) 繰り返し発揮している強み（strengths）
【参照】【全履歴サマリー】全体を対象に集計してください（直近5ノートだけを見ない）。
- ★ "強み"という言葉は出力に使わない（禁止語彙）
- 3〜6個の { description, count }
- count は「その行動パターンが見られる distinct ノートの数」（同一日付のノートは最大1カウント）
- 採用ライン: 3ノート以上に出現したもののみ含める
- description: 14〜30文字の自然な文章表現
  - ユーザーが繰り返し書いている動作・場面を描写
  - 抽象名詞ではなく、状況に根ざす表現
  - ★★ description は必ず **動詞（辞書形または〜ている形）で終わること**（絶対要件）
  - "我慢している" "飲み込んでいる" のようなネガ動詞は禁止
  - ★★ 以下の語で終わる表現は **絶対に禁止**：
    「〜動き」「〜姿勢」「〜流れ」「〜性質」「〜傾向」「〜スタンス」「〜力」「〜スキル」「〜方」「〜あり方」
  ◯「相手の言葉を最後まで聞いてから返す」（動詞で終わる ✓）
  ◯「迷いを言葉にしてから動き出す」（動詞で終わる ✓）
  ◯「腑に落ちるまで立ち止まってから次に進む」（動詞で終わる ✓）
  ◯「気になった細部を言葉に残しておく」（動詞で終わる ✓）
  × 「相手の言葉を最後まで聞いてから返す動き」（「動き」で終わる → 禁止）
  × 「腑に落ちるまで立ち止まる姿勢」（「姿勢」で終わる → 禁止）
  × 「自分の納得を確かめてから進む流れ」（「流れ」で終わる → 禁止）
  × 「整理力」「気づき力」「傾聴力」（抽象名詞のみ）
  × 「言いたいことを飲み込んで場を保つ動き」（ネガ動詞 + 禁止語尾）

(E) 言葉の変化（wordChange）
【参照】【直近ノートのサマリー】と【全履歴サマリー】の最初期ノートを比較してください。
- ${wordChangeBeforeLabel ? `【全履歴サマリー】の最初期のノート（約${daysDiff}日前）と【直近ノート】を比較し、表現がどう育ってきたかを 'before' / 'after' のペアで1〜2件（短い名詞句）` : "3週間未満のため変化を判断するには履歴が短い。空配列で返す"}

【ペア採用の条件（厳守）】
- before と after は「同じ根（テーマ・感情）が時間をかけて変化したもの」に限る。意味がつながらない別テーマを組み合わせない
- 変化として認めるのは次の2種類のみ：
  ① 曖昧 → 具体（「なんかモヤモヤ」→「認めてほしかった」のように輪郭が育った）
  ② 意味そのものが移った（「辞めたい」→「自分のペースでやりたい」のように願いの方向が変わった）
- 単なる言い換え（ほぼ同じ意味を別の言葉で言っただけ）はペアにしない
  × 「伝えたい」→「届けたい」（言い換えのみ）
  × 「安心したい」→「落ち着きたい」（ほぼ同義）
- before は【全履歴サマリーの最初期ノート】から、after は【直近ノート】から取ること
- 上記の条件を満たすペアが見つからない場合は、無理に作らず空配列で返す（1〜2件を"埋めること"を優先しない）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【日本語として完結した自然な文を出すこと】
- 助詞単独で終わる文は禁止（〜に / 〜と / 〜が / 〜の / 〜を / 〜は / 〜で）
- 接続表現で終わる文は禁止（〜ており / 〜ところ / 〜こと、 / 〜たい、）
- 連用形で終わる文は禁止（〜し / 〜作り / 〜持ち）
- description は「。」で完結、title は「。」を付けない

【出力形式】必ず以下のJSONのみで返す（前後に説明を付けない）：
{
  "currentSelf": {
    "headline": "人と通じ合うことを大事にしている人",
    "description": "最近は、自分の声を相手にちゃんと届けたいという気持ちが、繰り返し出てきているようです。",
    "recentWords": ["安心", "納得", "自分らしいペース", "信頼"]
  },
  "newlyEmerged": [
    { "iconHint": "people", "title": "人と通じ合いたい気持ち", "description": "自分の意見をちゃんと届けたい気持ちがあったようです。", "sourceNoteIds": ["note-abc123"] }
  ],
  "valueCloud": [
    { "keyword": "安心", "count": 8 }
  ],
  "strengths": [
    { "description": "相手の言葉を最後まで聞いてから返す", "count": 9 }
  ],
  "wordChange": [
    { "before": "...", "after": "..." }
  ]
}`;
      // ── 禁止語彙（抽象名詞・自己啓発本風） ─────────────────────
      // ユーザーの固有な言葉を尊重するため、これらが含まれていたら無効として扱う
      const FORBIDDEN_WORDS = [
        "強み", "能力", "スキル", "ポテンシャル",
        "効率", "リソース", "モチベーション",
        "適切", "最適", "効果的",
        "自己実現", "自己肯定", "自己成長",
        "成長", "貢献", "向上", "発揮",
        "チーム", "組織", "マネジメント", "コミュニケーション",
        "ビジネス", "プロジェクト", "タスク", "パフォーマンス",
      ];
      const containsForbidden = (text) => FORBIDDEN_WORDS.some(w => text.includes(w));

      // ── 検証ロジック ────────────────────────────────────────
      // 自然な日本語として完結 + 禁止語彙を含まない を判定
      const isValidText = (text, type) => {
        if (!text || typeof text !== "string") return false;
        const t = text.trim();
        if (!t) return false;
        // 禁止語彙チェック（type に関わらず適用、ただし strengthsDescription は別扱い）
        if (type !== "strengthDescription" && containsForbidden(t)) return false;
        // 禁止パターン（共通）
        const badEndings = [
          /[にとがのをはへで、,]$/,           // 助詞単独で終わる
          /(?:ており|ところ|こと|ように)[、,]?$/, // 接続表現で終わる
          /(?:を|に|で|と)[ぁ-ゔ]{1,2}$/,    // 助詞 + 1-2字の連用形
          /(?:を|に|で|と)[一-龥]+$/,         // 助詞 + 漢字止め（動詞抜け）
        ];
        if (badEndings.some(rx => rx.test(t))) return false;
        // 鍵括弧「」のネスト禁止：「...『...』...」「...「...」...」のような構造を弾く
        // 引用が2か所以上含まれる場合も読みにくいので弾く
        const quoteOpenCount = (t.match(/「|『/g) || []).length;
        const quoteCloseCount = (t.match(/」|』/g) || []).length;
        if (quoteOpenCount >= 2 || quoteCloseCount >= 2) return false;
        if (quoteOpenCount !== quoteCloseCount) return false;
        if (type === "title") {
          if (t.endsWith("。") || t.endsWith(".")) return false;
          if (t.length < 6 || t.length > 26) return false;
        } else if (type === "description") {
          if (!/[。.]$/.test(t)) return false;
          if (/^[ぁ-ヿ一-龥ー]{1,8}[。.]$/.test(t)) return false;
          if (t.length < 12) return false;
        } else if (type === "heroDescription") {
          if (!/[。.]$/.test(t)) return false;
          if (/^[ぁ-ヿ一-龥ー]{1,8}[。.]$/.test(t)) return false;
          if (t.length < 18) return false;
        } else if (type === "strengthDescription") {
          // 強み記述は禁止語のうち "強み" "発揮" は除外して残りをチェック
          const limited = ["強み", "発揮"];
          const others = FORBIDDEN_WORDS.filter(w => !limited.includes(w));
          if (others.some(w => t.includes(w))) return false;
          if (t.length < 10) return false;
          // 名詞止め語尾チェック：「〜動き」「〜姿勢」「〜流れ」等で終わる場合は無効
          const badNounEndings = ["動き", "姿勢", "流れ", "性質", "傾向", "スタンス", "あり方", "方"];
          if (badNounEndings.some(w => t.endsWith(w))) return false;
        }
        return true;
      };

      // ── フォールバック方針：generic 文は使わない ─────────────
      // 古い「新しく見えてきた言葉」「最近、この言葉が…」のような汎用フォールバックは廃止。
      // 検証で通らないフィールドは null/空にして、UI 側で「データ不足」または「非表示」を選ぶ。

      // ── データ不足の判定（< 3 件で空状態に切り替え） ─────────
      const INSUFFICIENT_THRESHOLD = 3;
      const isInsufficient = items.length < INSUFFICIENT_THRESHOLD;

      // データが3件未満なら AI を呼ばず、insufficient フラグだけ立てて終了
      if (isInsufficient) {
        setStockAgg({
          insufficient: true,
          approvedCount: items.length,
          noteCount: notes.length,
          computedAt: Date.now(),
        });
        setStockLoading(false);
        return;
      }

      // ── AI呼び出し＋検証＋最大3回まで再生成 ─────────────────
      // callAIJSON の内部リトライは 0 に固定（外側で再生成するため）。
      // 合計のAPIコール数を MAX_RETRY 回（=最大3回）に抑え、レートリミットを回避。
      let res = null;
      let validResult = null;
      let lastError = null;
      let rateLimited = false;
      const MAX_RETRY = 3;
      for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
        try {
          // 自分の言葉タブの集計（PathNote の核）:
          // - 品質一貫性を優先して Sonnet 4.6 で全フィールドを単発生成
          // - max_tokens は応答の途切れを防ぐため 2500 を維持
          res = await callAIJSON([{ role:"user", content:prompt + JSON_ONLY_INSTRUCTION }], MODEL_SONNET, 0, 2500);
        } catch (e) {
          lastError = e;
          // eslint-disable-next-line no-console
          console.warn(`[stockAgg] API call failed (attempt ${attempt}/${MAX_RETRY}):`, e?.message || e);
          // 429 はリトライ禁止（追い打ちでさらに枠を食わない）
          if (e?.status === 429 || e?.code === "AI_RATE_LIMIT") {
            rateLimited = true;
            break;
          }
          continue;
        }
        // 主要フィールドの検証
        const heroDescValid = !res?.currentSelf?.description || isValidText(res.currentSelf.description, "heroDescription");
        const cardsRaw = Array.isArray(res?.newlyEmerged) ? res.newlyEmerged : [];
        const cardsAllValid = cardsRaw.length === 0 || cardsRaw.every(c =>
          isValidText(c?.title, "title") && isValidText(c?.description, "description")
        );
        if (heroDescValid && cardsAllValid) {
          validResult = res;
          break;
        }
        if (attempt < MAX_RETRY) {
          // eslint-disable-next-line no-console
          console.info(`[stockAgg] validation failed (attempt ${attempt}), retrying...`);
        }
      }
      // 全試行で失敗しても、最後に取れた応答を使い、後でフィールドごとにフォールバックを適用する。
      res = validResult || res || {};
      // レートリミットの場合は専用メッセージ＋静かなフォールバック表示
      if (rateLimited) {
        setStockError("AIのリクエストが集中しています。1〜2分ほど待ってから「集計し直す」を押してください。");
      } else if (!validResult && lastError && !res?.currentSelf && !res?.newlyEmerged) {
        setStockError("AI集計に失敗しました。少し時間をおいて、もう一度お試しください。");
      }

      // キーワードのサニタイズ：句点除去 + 助詞止めの修正
      const sanitizeKeyword = (s) => {
        let t = String(s || "").replace(/[。．.、，,\s]+$/, "").trim();
        if (!t) return "";
        // 1文字以下は意味をなさないので除去
        if (t.length <= 1) return "";
        // 「〜大切に / 〜丁寧に」など副詞句で終わるパターンは「〜する」を補う
        if (/(大切|大事|丁寧|真剣|誠実|前向き|柔軟|慎重|素直|正直|積極的|主体的)に$/.test(t)) {
          t = t + "する";
          return t.trim();
        }
        // 末尾の助詞だけが残る中途半端な切れは削る
        // 「も」を追加：「向き合いたいも」→「向き合いたい」のような切れを修正
        // 「と」「や」は名詞末尾としても使われるため除外
        // 「に」は「丁寧に」等が上のブロックで処理済みのため追加
        // 短い名詞（4文字以下）は助詞除去をスキップして AI の出力を尊重
        if (t.length > 4 && /[をがはのでへもに]$/.test(t)) {
          t = t.replace(/[をがはのでへもに]+$/, "");
        }
        // 助詞除去後に1文字以下になった場合も除去
        if (!t || t.length <= 1) return "";
        // 「〜こと」「〜すること」が冗長な場合のみ削る（短い名詞では削らない）
        if (t.length > 6) {
          t = t.replace(/(?:する)?こと$/, "");
        }
        return t.trim();
      };
      // count降順
      const sortByCount = (arr) => [...arr].sort((a,b) => (b?.count || 0) - (a?.count || 0));

      // 今のあなたを一言で
      const cs = res?.currentSelf && typeof res.currentSelf === "object" ? res.currentSelf : null;
      // headline / description は AI 出力をそのまま尊重。検証で通らなければ null（非表示）。
      const pickHeadline = (raw) => {
        const t = sanitizeKeyword(raw || "");
        if (!t) return null;
        if (containsForbidden(t)) return null;
        // 抽象的な決まり文句「強みを大切にする人」「効率を重視する人」も containsForbidden で弾かれる
        if (t.length < 6 || t.length > 26) return null;
        return t;
      };
      const pickHeroDescription = (raw) => {
        const t = String(raw || "").trim();
        return isValidText(t, "heroDescription") ? t : null;
      };
      const currentSelf = cs ? {
        headline: pickHeadline(cs.headline),
        description: pickHeroDescription(cs.description),
        recentWords: Array.isArray(cs.recentWords)
          ? cs.recentWords.map(sanitizeKeyword).filter(w => w && !containsForbidden(w)).slice(0, 6)
          : [],
      } : null;

      // 最近、新しく見えてきたこと（カード形式）
      const allowedIcons = new Set(["shield","leaf","people","compass","heart","sparkle","book","clock"]);

      // 検証で通らないカードは除外（generic フォールバックは使わない）
      const newlyEmerged = Array.isArray(res?.newlyEmerged) ? res.newlyEmerged
        .filter(c => c?.title)
        .map(c => {
          const title = String(c.title || "").replace(/[。．.、，,\s]+$/, "").trim();
          let desc = String(c.description || "").trim();
          if (desc && !/[。.]$/.test(desc)) desc = desc + "。";
          if (!isValidText(title, "title")) return null;
          if (!isValidText(desc, "description")) return null;
          // sourceNoteIds: AI が返した ID が実際に存在するもののみ残す
          const rawIds = Array.isArray(c.sourceNoteIds) ? c.sourceNoteIds : [];
          const sourceNoteIds = rawIds.filter(id => typeof id === "string" && validNoteIdSet.has(id));
          return {
            iconHint: allowedIcons.has(c.iconHint) ? c.iconHint : "sparkle",
            title,
            description: desc,
            sourceNoteIds,
          };
        })
        .filter(Boolean)
        .slice(0, 4) : [];

      // 価値観（ワードクラウド用）— 5〜12個まで
      const valueCloudRaw = Array.isArray(res?.valueCloud) ? res.valueCloud
        .filter(v => v?.keyword)
        .map(v => ({ keyword: sanitizeKeyword(v.keyword), count: Math.max(1, parseInt(v.count, 10) || 1) }))
        .filter(v => v.keyword) : [];
      const valueCloud = sortByCount(valueCloudRaw).slice(0, 12);

      // 強み（自然文表現）— 抽象名詞・禁止語は除外
      const strengthsRaw = Array.isArray(res?.strengths) ? res.strengths
        .filter(s => s?.description || s?.keyword)
        .map(s => ({
          description: String(s.description || s.keyword || "").trim().replace(/[。．.\s]+$/, ""),
          count: Math.max(1, parseInt(s.count, 10) || 1),
        }))
        .filter(s => s.description && isValidText(s.description, "strengthDescription")) : [];
      const strengths = sortByCount(strengthsRaw).slice(0, 6);

      // wordChangeBeforeLabel が null（3週間未満）の場合は data-gate で採用しない
      const wordChange = wordChangeBeforeLabel && Array.isArray(res?.wordChange)
        ? res.wordChange.filter(w => w?.before && w?.after).slice(0, 2)
        : [];

      setStockAgg({
        currentSelf: (currentSelf && currentSelf.headline) ? currentSelf : null,
        newlyEmerged,
        valueCloud,
        strengths,
        wordChange,
        wordChangeBeforeLabel,
        noteCount: notes.length,
        approvedCount: items.length,
        olderCount,
        computedAt: Date.now(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[stockAgg] unexpected error:", e);
      // 想定外エラーでもユーザーには控えめなメッセージとフォールバック表示を出す
      setStockError("AI集計に失敗しました。少し時間をおいて、もう一度お試しください。");
      setStockAgg({
        currentSelf: {
          headline: "言葉が育っています",
          description: "最近、いくつかの言葉が繰り返し出てきています。",
          recentWords: [],
        },
        newlyEmerged: [],
        valueCloud: [],
        strengths: [],
        wordChange: [],
        noteCount: notes.length,
        approvedCount: items.length,
        computedAt: Date.now(),
      });
    } finally {
      setStockLoading(false);
    }
  };

  // stockAgg が更新されたら localStorage にキャッシュ保存
  useEffect(()=>{
    if (!stockAgg) return;
    try {
      const raw = localStorage.getItem("pathnote_mvp_v1") || "{}";
      const stored = JSON.parse(raw);
      localStorage.setItem("pathnote_mvp_v1", JSON.stringify({ ...stored, stockAgg }));
    } catch {}
  }, [stockAgg]);

  // 自分の言葉タブを開いたとき：
  // 承認済みサマリー数が変化している場合のみ再集計（キャッシュ活用）
  useEffect(()=>{
    if (tab !== "stock") return;
    const { items } = collectApprovedSummaries();
    if (items.length === 0) {
      setStockAgg({ values: [], strengths: [], wordChange: [], noteCount: 0, approvedCount: 0, computedAt: Date.now() });
      return;
    }
    if (!stockAgg || stockAgg.approvedCount !== items.length) {
      runStockAggregation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // 書きかけノートの自動保存（noteStep が done または step1+空 のときはクリア）
  useEffect(()=>{
    try {
      const isEmpty = noteStep === "step1" && !noteBody.trim() && !noteEmotion && !nearSelected && !detailDraft && !summaryA;
      if (noteStep === "done" || isEmpty) {
        localStorage.removeItem(NOTE_DRAFT_KEY);
        return;
      }
      const draft = {
        noteStep, noteBody, noteEmotion, noteCycles,
        nearCandidates, nearSelected, nearFreeDraft,
        detailDraft,
        summaryA,
        cycleIndex, probeCandidates, selectedProbe, probeAnswer, summaryB,
        completedSummaries,
        insightDraft, insights,
        receptionText,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(NOTE_DRAFT_KEY, JSON.stringify(draft));
    } catch {}
  }, [noteStep, noteBody, noteEmotion, noteCycles, nearCandidates, nearSelected, nearFreeDraft, detailDraft, summaryA, cycleIndex, probeCandidates, selectedProbe, probeAnswer, summaryB, completedSummaries, insightDraft, insights, receptionText]);

  useEffect(()=>{
    if (initialTab && initialTab !== "note") {
      setTab(initialTab);
      if (onTabConsumed) onTabConsumed();
    }
  }, [initialTab, onTabConsumed]);

  useEffect(()=>{
    try {
      localStorage.setItem("pathnote_last_page", "mypage");
      localStorage.setItem("pathnote_last_mypage_tab", tab);
    } catch {}
  }, [tab]);

  useEffect(()=>{
    const ready = () => setInstallPromptReady(Boolean(window.pathnoteInstallPrompt));
    window.addEventListener("pathnote-install-prompt-ready", ready);
    window.addEventListener("appinstalled", ready);
    return () => {
      window.removeEventListener("pathnote-install-prompt-ready", ready);
      window.removeEventListener("appinstalled", ready);
    };
  }, []);

  useEffect(()=>{
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    const alreadyHandled = localStorage.getItem("pathnote_install_invite_done") === "1";
    if (tab === "note" && (data.dailyNotes||[]).length >= 3 && !alreadyHandled && !isStandalone) {
      const timer = setTimeout(()=>setShowInstallInvite(true), 600);
      return () => clearTimeout(timer);
    }
  }, [tab, data.dailyNotes]);

  const closeInstallInvite = () => {
    localStorage.setItem("pathnote_install_invite_done", "1");
    setShowInstallInvite(false);
    setInstallHelp(false);
  };

  const handleInstallApp = async () => {
    const promptEvent = window.pathnoteInstallPrompt;
    if (promptEvent?.prompt) {
      try {
        await promptEvent.prompt();
        await promptEvent.userChoice;
        window.pathnoteInstallPrompt = null;
        setInstallPromptReady(false);
        closeInstallInvite();
      } catch {
        setInstallHelp(true);
      }
      return;
    }
    setInstallHelp(true);
  };

  const F = "'Noto Sans JP','Hiragino Sans',sans-serif";
  const C = { bg:"#FAF4ED",surface:"#FEFCF9",border:"#E8DDD6",accent:"#C97B7B",accentL:"#FDF0EF",accentM:"#DEAEAE",text:"#1F2937",sub:"#4B5563",muted:"#9CA3AF",warm:"#D4897B",warmL:"#FFF1ED",shadow:"0 1px 6px rgba(100,80,50,0.07)" };

  const TABS = [
    { id:"note",  label:"自分ノート" },
    { id:"stock", label:"自分の言葉" },
    { id:"log",   label:"自分ログ" },
    { id:"page",  label:"マイページ" },
  ];

  const allRecordDates = [data.savedAt, ...(dailyNotes||[]).map(n=>n.createdAt)].filter(Boolean);
  const firstRecordDate = allRecordDates.length
    ? new Date(Math.min(...allRecordDates.map(d=>new Date(d).getTime())))
    : null;
  const profileName = (profile?.name || "").trim() || "PathNoteユーザー";

  const saveProfile = (nextProfile = profile) => {
    const normalizedProfile = {
      ...nextProfile,
      name: (nextProfile?.name || "").trim() || "PathNoteユーザー",
    };
    const stored = JSON.parse(localStorage.getItem("pathnote_mvp_v1") || "{}");
    localStorage.setItem("pathnote_mvp_v1", JSON.stringify({ ...stored, profile:normalizedProfile, savedAt:new Date().toISOString() }));
    setProfile(normalizedProfile);
    setProfileDraftName(normalizedProfile.name);
    setProfileEditing(false);
  };
  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const nextProfile = { ...profile, avatar:reader.result };
      setProfile(nextProfile);
      saveProfile(nextProfile);
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };


  const switchTab = (id) => {
    setTab(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // GA4: タブ切替を page_view として計測
    // id:"page" は UI上「マイページ」だが path は /mypage に統一する
    const tabTitles = { note:"自分ノート", stock:"自分の言葉", log:"自分ログ", page:"マイページ" };
    const pagePath = `/${id === "page" ? "mypage" : id}`;
    gtagPageView(pagePath, `PathNote - ${tabTitles[id] || id}`);
  };

  // ノートフローはすべて note テーマで統一
  const noteTheme = themeFor("note");

  // 自分ログ：ポップアップ表示中のノートを取り出す（再レンダリングごとに最新の logNotes から）
  const popupNote = popupNoteId ? logNotes.find(n => n.id === popupNoteId) : null;

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:F }}>
      {popupNote&&(() => {
        const emo = EMOTION_MAP[popupNote.emotion] || null;
        // 一覧カードと同じ「colorLighter 単色 + emotion 色の線画」に揃える。
        const iconStroke = emo?.color || tokens.accent.primary;
        const iconBg = emo?.colorLighter || tokens.accent.light;
        const IconCmp = emo?.Icon || Sparkles;
        const cycles = popupNote.cycles || [];
        const cycleA = cycles.find(c => c.level === "a");
        const subTag = cycleA?.nearWords?.selected || null;
        // 「あなた」「PathNote」セクション共通スタイル：左の細いカラーバーで発話者を識別する。
        // text = 白 / accent.primary バー、ai = ベージュ / accent.orange バー。
        const userBlock = {
          background: "#FEFDFB",
          borderLeft: `4px solid ${tokens.accent.primary}`,
          borderRadius: R.sm,
          padding: `${SP.md}px ${SP.lg}px`,
        };
        const aiBlock = {
          background: "#FBF6EF",
          borderLeft: `4px solid ${tokens.accent.orange}`,
          borderRadius: R.sm,
          padding: `${SP.md}px ${SP.lg}px`,
        };
        const userLabel = (text) => (
          <div style={{ display:"flex", alignItems:"center", gap:SP.xs, marginBottom:SP.xs, color:tokens.accent.primary, fontSize:FS.xxs, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide }}>
            <Pen size={12} strokeWidth={1.8}/>
            <span>{text}</span>
          </div>
        );
        const aiLabel = (text) => (
          <div style={{ display:"flex", alignItems:"center", gap:SP.xs, marginBottom:SP.xs, color:tokens.accent.orange, fontSize:FS.xxs, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide }}>
            <Sparkles size={12} strokeWidth={1.8}/>
            <span>{text}</span>
          </div>
        );
        const userText = (txt) => (
          <div style={{ fontSize:FS.sm, color:tokens.text.primary, lineHeight:LH.relaxed, whiteSpace:"pre-line" }}>{txt}</div>
        );
        return (
          <div
            style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.42)", zIndex:290, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:0 }}
            onClick={()=>setPopupNoteId(null)}
          >
            <div
              onClick={e=>e.stopPropagation()}
              style={{
                background: tokens.neutral.surface,
                width:"100%", maxWidth:540,
                maxHeight:"92vh",
                borderRadius: `${R["2xl"]}px ${R["2xl"]}px 0 0`,
                boxShadow:"0 -12px 40px rgba(15,23,42,0.18)",
                display:"flex", flexDirection:"column",
                animation:tokens.motion.fadeUp,
              }}
            >
              {/* ヘッダー：日付 + emotion アイコン + 閉じる */}
              <div style={{
                display:"flex", alignItems:"center", gap:SP.md,
                padding:`${SP.xl}px ${SP.xl}px`,
                borderBottom:`1px solid ${tokens.neutral.border}`,
              }}>
                <div style={{
                  width:40, height:40, borderRadius:"50%",
                  background: iconBg,
                  border: "none",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  flexShrink:0,
                }}>
                  <IconCmp size={22} strokeWidth={1.8} color={iconStroke}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:FS.sm, color:tokens.text.primary, fontWeight:FW.semibold }}>
                    {formatNoteDate(popupNote.closedAt || popupNote.createdAt)}
                  </div>
                  {(emo || subTag) && (
                    <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:SP.xs, marginTop:2 }}>
                      {emo && (
                        <span style={{ fontSize:FS.xs, color:emo.color, fontWeight:FW.medium }}>
                          {emo.label}
                        </span>
                      )}
                      {subTag && (
                        <span style={{
                          display:"inline-block",
                          padding:"1px 8px",
                          background: tokens.neutral.bg,
                          color: tokens.text.secondary,
                          border: `1px solid ${tokens.neutral.border}`,
                          borderRadius: R.sm,
                          fontSize: FS.xs, fontWeight: FW.medium,
                          lineHeight: 1.5,
                        }}>
                          {subTag}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={()=>setPopupNoteId(null)}
                  aria-label="閉じる"
                  style={{
                    width:36, height:36, borderRadius:"50%",
                    background:"transparent", border:"none", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:tokens.text.tertiary, flexShrink:0,
                  }}
                >
                  <X size={20} strokeWidth={1.5}/>
                </button>
              </div>

              {/* 本文スクロール領域：「あなた」（白bg + 青バー + Pen）と
                  「PathNote」（ベージュbg + オレンジバー + Sparkles）で発話者を明示。 */}
              <div style={{ flex:1, overflowY:"auto", padding:`${SP.xl}px ${SP.xl}px ${SP["2xl"]}px`, display:"flex", flexDirection:"column", gap:SP.md }}>
                {/* STEP1：本文（ユーザー） */}
                <div style={userBlock}>
                  {userLabel("あなたが書いたこと")}
                  {userText(popupNote.body)}
                </div>

                {/* 各サイクル詳細：問い(AI) → 答え(ユーザー) → 整理(AI) を時系列で並べる */}
                {cycles.map((c, i) => {
                  const cycleLabel = c.level === "a"
                    ? "STEP4 整理"
                    : `STEP6 整理（${c.cycleIndex + 1}サイクル目）`;
                  return (
                    <div key={i} style={{ display:"flex", flexDirection:"column", gap:SP.sm }}>
                      <div style={{ fontSize:FS.xxs, color:tokens.text.tertiary, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide, marginTop:SP.sm }}>
                        {cycleLabel}
                      </div>

                      {/* STEP4：選んだ言葉（ユーザーの選択） */}
                      {c.nearWords?.selected && c.level === "a" && (
                        <div style={userBlock}>
                          {userLabel("あなたの選択")}
                          {userText(c.nearWords.selected)}
                        </div>
                      )}

                      {/* STEP3：場面の補足（ユーザーの自由入力） */}
                      {c.detail && !c.detail.skipped && c.detail.answer && (
                        <div style={userBlock}>
                          {userLabel("あなたが書いたこと")}
                          {userText(c.detail.answer)}
                        </div>
                      )}

                      {/* STEP5：深掘りの問い（AI）と答え（ユーザー） */}
                      {c.probe && (
                        <>
                          <div style={aiBlock}>
                            {aiLabel("PathNote の問い")}
                            {userText(c.probe.question)}
                          </div>
                          {c.probe.answer && (
                            <div style={userBlock}>
                              {userLabel("あなたの答え")}
                              {userText(c.probe.answer)}
                            </div>
                          )}
                        </>
                      )}

                      {/* 整理（AI） */}
                      {c.summary?.text && (
                        <div style={aiBlock}>
                          {aiLabel("PathNote の整理")}
                          {userText(c.summary.text)}
                          {Array.isArray(c.summary?.revisions) && c.summary.revisions.length > 0 && (
                            <div style={{ marginTop:SP.sm, fontSize:FS.xxs, color:tokens.text.tertiary }}>
                              ※ {c.summary.revisions.length}回 整理し直しました
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* 削除リンク */}
                <div style={{ textAlign:"right", marginTop:SP.sm }}>
                  <button
                    onClick={()=>setDeleteTargetNoteId(popupNote.id)}
                    style={{ background:"transparent", border:"none", color:"#C44", fontSize:FS.sm, fontWeight:FW.medium, cursor:"pointer", fontFamily:F, textDecoration:"underline" }}
                  >
                    このノートを削除
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 「自分の言葉」タブの「もっと見る」モーダル（全件表示）
          自分ログの popupNote と同じボトムシート風のパターンで、3セクション共通レイアウトを使う。 */}
      {stockModalKey && stockAgg && (() => {
        const closeModal = () => setStockModalKey(null);
        // iconMap/iconColorMap は集計タブの render 内で定義されているため、モーダルでは独自に持つ。
        const modalIconMap = { shield:Shield, leaf:Leaf, people:Users, compass:Compass, heart:Heart, sparkle:Sparkles, book:BookOpen, clock:Clock };
        const modalIconColor = (hint) => ({
          shield: tokens.accent.primary,
          leaf:   tokens.accent.green,
          people: tokens.accent.primary,
          compass:tokens.accent.primary,
          heart:  tokens.accent.orange,
          sparkle:tokens.accent.orange,
          book:   tokens.accent.primary,
          clock:  tokens.text.tertiary,
        }[hint] || tokens.accent.primary);

        let title = "";
        let body = null;
        if (stockModalKey === "newlyEmerged") {
          title = "最近、新しく見えてきたこと（全件）";
          const items = Array.isArray(stockAgg.newlyEmerged) ? stockAgg.newlyEmerged : [];
          body = (
            <div style={{ display:"flex", flexDirection:"column", gap:SP.md }}>
              {items.map((c, i) => {
                const Icon = modalIconMap[c.iconHint] || Sparkles;
                const iconColor = modalIconColor(c.iconHint);
                return (
                  <div key={i} style={{
                    background: tokens.neutral.bg,
                    border: `1px solid ${tokens.neutral.border}66`,
                    borderRadius: R.lg,
                    padding: `${SP.lg}px ${SP.lg}px`,
                    display:"flex", flexDirection:"column", gap:SP.sm,
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:SP.sm }}>
                      <span style={{ width:30, height:30, borderRadius:R.md, background:iconColor+"18", color:iconColor, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <Icon size={16} strokeWidth={2.2}/>
                      </span>
                    </div>
                    <div style={{ fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.semibold, lineHeight:LH.snug, wordBreak:"break-word", overflowWrap:"break-word" }}>
                      {c.title}
                    </div>
                    {c.description && (
                      <div style={{ fontSize:FS.sm, color:tokens.text.secondary, lineHeight:LH.normal, wordBreak:"break-word", overflowWrap:"break-word" }}>
                        {c.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        } else if (stockModalKey === "valueCloud") {
          title = "あなたの中で、繰り返し現れている言葉（全件）";
          const items = Array.isArray(stockAgg.valueCloud) ? [...stockAgg.valueCloud].sort((a, b) => (b.count || 0) - (a.count || 0)) : [];
          body = (
            <div style={{ display:"flex", flexDirection:"column" }}>
              {items.map((v, i) => (
                <div key={i} style={{
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:SP.lg,
                  padding:`${SP.md}px 0`,
                  borderBottom: i < items.length - 1 ? `1px solid ${tokens.neutral.border}55` : "none",
                }}>
                  <span style={{ flex:1, fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.medium, lineHeight:LH.snug, wordBreak:"break-word", minWidth:0 }}>
                    {v.keyword}
                  </span>
                  <span style={{ fontSize:FS.base, color:"#7A5A3A", fontWeight:FW.semibold, background:"#F7F2E5", borderRadius:R.pill, padding:"4px 10px", flexShrink:0, whiteSpace:"nowrap" }}>
                    {v.count}回
                  </span>
                </div>
              ))}
            </div>
          );
        } else if (stockModalKey === "strengths") {
          title = "自然とやっていること（全件）";
          const items = Array.isArray(stockAgg.strengths) ? [...stockAgg.strengths].sort((a, b) => (b.count || 0) - (a.count || 0)) : [];
          const strengthIcons = [Users, BookOpen, Sparkles, Heart, Star, Compass];
          body = (
            <div style={{ display:"flex", flexDirection:"column", gap:SP.md }}>
              {items.map((s, i) => {
                const Icon = strengthIcons[i % strengthIcons.length];
                return (
                  <div key={i} style={{
                    background: tokens.neutral.bg,
                    border: `1px solid ${tokens.neutral.border}66`,
                    borderRadius: R.lg,
                    padding: `${SP.lg}px ${SP.lg}px`,
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:SP.md }}>
                      <span style={{ width:34, height:34, borderRadius:"50%", background:tokens.accent.light, color:tokens.accent.primary, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <Icon size={16} strokeWidth={2.2}/>
                      </span>
                      <span style={{ flex:1, fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.medium, lineHeight:LH.snug, minWidth:0, wordBreak:"break-word", overflowWrap:"break-word" }}>
                        {s.description}
                      </span>
                      <span style={{ fontSize:FS.base, color:"#7A5A3A", fontWeight:FW.semibold, background:"#F7F2E5", borderRadius:R.pill, padding:"4px 10px", flexShrink:0, whiteSpace:"nowrap" }}>
                        {s.count}回
                      </span>
                    </div>
                    {/* evidence（背景にあるノートのプレビュー）がある場合のみ表示 */}
                    {Array.isArray(s.evidence) && s.evidence.length > 0 && (
                      <div style={{ marginTop:SP.md, paddingLeft:46, display:"flex", flexDirection:"column", gap:SP.xs }}>
                        {s.evidence.map((e, ei) => (
                          <div key={ei} style={{ fontSize:FS.xs, color:tokens.text.tertiary, lineHeight:LH.normal, paddingLeft:SP.md, borderLeft:`2px solid ${tokens.neutral.border}` }}>
                            {typeof e === "string" ? e : (e?.text || "")}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div
            style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.42)", zIndex:290, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:0 }}
            onClick={closeModal}
          >
            <div
              onClick={e=>e.stopPropagation()}
              style={{
                background: tokens.neutral.surface,
                width:"100%", maxWidth:540,
                maxHeight:"92vh",
                borderRadius: `${R["2xl"]}px ${R["2xl"]}px 0 0`,
                boxShadow:"0 -12px 40px rgba(15,23,42,0.18)",
                display:"flex", flexDirection:"column",
                animation:tokens.motion.fadeUp,
              }}
            >
              <div style={{
                display:"flex", alignItems:"center", gap:SP.md,
                padding:`${SP.xl}px ${SP.xl}px`,
                borderBottom:`1px solid ${tokens.neutral.border}`,
              }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.semibold, lineHeight:LH.snug }}>
                    {title}
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  aria-label="閉じる"
                  style={{
                    width:36, height:36, borderRadius:"50%",
                    background:"transparent", border:"none", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:tokens.text.tertiary, flexShrink:0,
                  }}
                >
                  <X size={20} strokeWidth={1.5}/>
                </button>
              </div>
              <div style={{ flex:1, overflowY:"auto", padding:`${SP.xl}px ${SP.xl}px ${SP["2xl"]}px` }}>
                {body}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── newlyEmerged カード詳細モーダル ── */}
      {neDetailCard && (() => {
        const closeDetail = () => { setNeDetailCard(null); setNeDetailExpandedBodies({}); };
        const detailIconMap = { shield:Shield, leaf:Leaf, people:Users, compass:Compass, heart:Heart, sparkle:Sparkles, book:BookOpen, clock:Clock };
        const detailIconColor = (hint) => ({
          shield: tokens.accent.primary, leaf: tokens.accent.green,
          people: tokens.accent.primary, compass: tokens.accent.primary,
          heart:  tokens.accent.orange,  sparkle: tokens.accent.orange,
          book:   tokens.accent.primary, clock:  tokens.text.tertiary,
        }[hint] || tokens.accent.primary);
        const DetIcon = detailIconMap[neDetailCard.iconHint] || Sparkles;
        const iconColor = detailIconColor(neDetailCard.iconHint);

        // sourceNoteIds に対応するノートエントリを localStorage から取得
        const { items: allItems } = collectApprovedSummaries();
        const sourceIds = Array.isArray(neDetailCard.sourceNoteIds) ? neDetailCard.sourceNoteIds : [];
        // noteId → 最新サマリー（level=b 優先、なければ a）をまとめる
        const sourceEntries = sourceIds.map(nid => {
          const related = allItems.filter(it => it.noteId === nid);
          if (!related.length) return null;
          // level b（整理済み）を優先、なければ最後の a
          const best = related.find(it => it.level === "b") || related[related.length - 1];
          return { noteId: nid, text: best.text, createdAt: best.createdAt, body: best.body };
        }).filter(Boolean);

        const fmtDate = (iso) => {
          try {
            const d = new Date(iso);
            return `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日`;
          } catch { return ""; }
        };

        return (
          <div
            style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.42)", zIndex:292,
              display:"flex", alignItems:"flex-end", justifyContent:"center" }}
            onClick={closeDetail}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: tokens.neutral.surface,
                width:"100%", maxWidth:540,
                maxHeight:"92vh",
                borderRadius: `${R["2xl"]}px ${R["2xl"]}px 0 0`,
                boxShadow:"0 -12px 40px rgba(15,23,42,0.18)",
                display:"flex", flexDirection:"column",
                animation: tokens.motion.fadeUp,
              }}
            >
              {/* ヘッダー */}
              <div style={{
                display:"flex", alignItems:"center", gap:SP.md,
                padding:`${SP.xl}px ${SP.xl}px`,
                borderBottom:`1px solid ${tokens.neutral.border}`,
                flexShrink:0,
              }}>
                <span style={{ width:36, height:36, borderRadius:R.md, background:iconColor+"18",
                  color:iconColor, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <DetIcon size={18} strokeWidth={2}/>
                </span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.semibold, lineHeight:LH.snug }}>
                    {neDetailCard.title}
                  </div>
                </div>
                <button onClick={closeDetail} aria-label="閉じる"
                  style={{ width:36, height:36, borderRadius:"50%", background:"transparent",
                    border:"none", cursor:"pointer", display:"flex", alignItems:"center",
                    justifyContent:"center", color:tokens.text.tertiary, flexShrink:0 }}>
                  <X size={20} strokeWidth={1.5}/>
                </button>
              </div>

              {/* スクロール本文 */}
              <div style={{ flex:1, overflowY:"auto", padding:`${SP.xl}px ${SP.xl}px ${SP["4xl"]}px` }}>
                {/* カード説明文 */}
                {neDetailCard.description && (
                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed,
                    margin:`0 0 ${SP["3xl"]}px` }}>
                    {neDetailCard.description}
                  </p>
                )}

                {/* ソースノートセクション */}
                {sourceEntries.length > 0 && (
                  <>
                    <div style={{ borderTop:`1px solid ${tokens.neutral.border}`, margin:`0 0 ${SP["3xl"]}px` }}/>
                    <div style={{ fontSize:FS.sm, color:tokens.text.secondary, fontWeight:FW.semibold,
                      marginBottom:SP.xl }}>
                      この気持ちにつながる、あなたの言葉
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:SP.xl }}>
                      {sourceEntries.map((entry) => {
                        const bodyOpen = !!neDetailExpandedBodies[entry.noteId];
                        return (
                          <div key={entry.noteId} style={{
                            background: tokens.neutral.surfaceAlt,
                            border:`1px solid ${tokens.neutral.border}66`,
                            borderRadius: R.lg,
                            padding:`${SP.lg}px ${SP.lg}px`,
                          }}>
                            {/* 日付 */}
                            <div style={{ fontSize:FS.xs, color:tokens.text.tertiary,
                              marginBottom:SP.sm }}>
                              {fmtDate(entry.createdAt)}
                            </div>
                            {/* 承認済みサマリー */}
                            <p style={{ fontSize:FS.base, color:tokens.text.primary,
                              lineHeight:LH.relaxed, margin:`0 0 ${SP.md}px`,
                              whiteSpace:"pre-line" }}>
                              {entry.text}
                            </p>
                            {/* 原文を読む トグル */}
                            {entry.body && (
                              <>
                                <button
                                  onClick={() => setNeDetailExpandedBodies(prev =>
                                    ({ ...prev, [entry.noteId]: !prev[entry.noteId] })
                                  )}
                                  style={{
                                    background:"transparent", border:"none", padding:0,
                                    fontSize:FS.xs, color:tokens.accent.primary,
                                    cursor:"pointer", fontFamily:F,
                                    display:"inline-flex", alignItems:"center", gap:2,
                                    lineHeight:1.4,
                                  }}
                                >
                                  {bodyOpen ? "閉じる" : "原文を読む ›"}
                                </button>
                                {bodyOpen && (
                                  <div style={{
                                    marginTop:SP.md,
                                    padding:`${SP.md}px ${SP.lg}px`,
                                    background: tokens.neutral.surface,
                                    border:`1px solid ${tokens.neutral.border}`,
                                    borderRadius: R.md,
                                    fontSize:FS.sm, color:tokens.text.secondary,
                                    lineHeight:LH.relaxed, whiteSpace:"pre-line",
                                  }}>
                                    {entry.body}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {deleteTargetNoteId&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.36)", zIndex:285, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={()=>setDeleteTargetNoteId(null)}>
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:18, padding:"22px 20px", width:"100%", maxWidth:400, boxShadow:"0 18px 60px rgba(31,41,55,0.18)" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:17, color:C.text, fontWeight:600, lineHeight:1.55, marginBottom:8 }}>このノートを削除しますか？</div>
            <p style={{ fontSize:13, color:C.sub, lineHeight:1.8, margin:"0 0 16px" }}>
              削除したノートと、それに含まれる整理は元に戻せません。
            </p>
            <button onClick={()=>deleteNote(deleteTargetNoteId)}
              style={{ width:"100%", padding:"13px", background:"#C44", color:"#fff", border:"none", borderRadius:12, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:F, marginBottom:8 }}>
              削除する
            </button>
            <button onClick={()=>setDeleteTargetNoteId(null)}
              style={{ width:"100%", padding:"10px", background:"transparent", color:C.muted, border:"none", fontSize:13, cursor:"pointer", fontFamily:F }}>
              キャンセル
            </button>
          </div>
        </div>
      )}
      {showCloseConfirm&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.36)", zIndex:280, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={cancelClose}>
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:18, padding:"22px 20px", width:"100%", maxWidth:400, boxShadow:"0 18px 60px rgba(31,41,55,0.18)" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:17, color:C.text, fontWeight:600, lineHeight:1.55, marginBottom:8 }}>ここで終わりますか？</div>
            <p style={{ fontSize:13, color:C.sub, lineHeight:1.8, margin:"0 0 16px" }}>
              {hasSomethingToSave
                ? "ここまでで承認した整理は、保存して残せます。"
                : "まだ整理が承認されていないので、保存できる内容はありません。"}
            </p>
            {hasSomethingToSave && (
              <button onClick={closeWithSave}
                style={{ width:"100%", padding:"13px", background:C.accent, color:"#fff", border:"none", borderRadius:12, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:F, marginBottom:8 }}>
                保存して終わる
              </button>
            )}
            <button onClick={closeWithoutSave}
              style={{ width:"100%", padding:"12px", background:C.surface, color:C.sub, border:"1px solid "+C.border, borderRadius:12, fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:F, marginBottom:8 }}>
              {hasSomethingToSave ? "保存しないで終わる" : "終わる（書きかけは破棄）"}
            </button>
            <button onClick={cancelClose}
              style={{ width:"100%", padding:"10px", background:"transparent", color:C.muted, border:"none", fontSize:13, cursor:"pointer", fontFamily:F }}>
              続ける
            </button>
          </div>
        </div>
      )}
      {showInstallInvite&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(31,41,55,0.46)", zIndex:250, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}
          onClick={closeInstallInvite}>
          <div style={{ background:C.surface, borderRadius:20, padding:"26px 22px 22px", width:"100%", maxWidth:420, boxShadow:"0 16px 40px rgba(31,41,55,0.18)", border:"1px solid "+C.border }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ textAlign:"center", marginBottom:18 }}>
              <div style={{ width:44, height:44, borderRadius:14, background:C.accentL, color:C.accent, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px", fontSize:22 }}>□</div>
              <h2 style={{ fontSize:19, fontWeight:500, color:C.text, lineHeight:1.55, margin:"0 0 8px" }}>このノートを、迷った日にすぐ開けるようにしますか？</h2>
              <p style={{ fontSize:13, color:C.sub, lineHeight:1.8, margin:0 }}>ホーム画面に追加すると、アプリのようにPathNoteを開けます。記録はこの端末に残ります。</p>
            </div>
            {installHelp&&(
              <div style={{ background:C.bg, border:"1px solid "+C.border, borderRadius:12, padding:"12px 14px", marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:6 }}>追加方法</div>
                <p style={{ fontSize:12, color:C.sub, lineHeight:1.75, margin:0 }}>
                  ブラウザ右上のメニューから「ホーム画面に追加」または「アプリをインストール」を選んでください。
                </p>
              </div>
            )}
            <button onClick={handleInstallApp}
              style={{ width:"100%", padding:"14px", background:C.accent, color:"#fff", border:"none", borderRadius:12, fontSize:15, fontWeight:500, cursor:"pointer", fontFamily:F, marginBottom:8 }}>
              {installPromptReady ? "ホーム画面に追加する" : "追加方法を見る"}
            </button>
            <button onClick={closeInstallInvite}
              style={{ width:"100%", padding:"10px", background:"transparent", border:"none", color:C.muted, cursor:"pointer", fontSize:13, fontFamily:F }}>
              今はしない
            </button>
          </div>
        </div>
      )}


      <nav style={{ background:C.surface, borderBottom:"1px solid "+C.border, padding:"0 20px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, position:"sticky", top:0, zIndex:100, boxShadow:C.shadow }}>
        <button type="button" onClick={onBack} aria-label="PathNote トップへ戻る" style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", alignItems:"center", gap:8, fontFamily:F }}>
          <img src={logoSrc} alt="PathNote" style={{ width:44, height:44, objectFit:"contain" }}/>
          <span style={{ fontFamily:"'Cormorant Garamond', serif", fontWeight:600, fontSize:26, color:"#4A3F35", letterSpacing:"0.01em", lineHeight:1 }}>PathNote</span>
        </button>
      </nav>

      <div style={{ maxWidth:600, margin:"0 auto", padding:"24px 20px 80px" }}>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:14, fontWeight:500, color:C.muted, letterSpacing:"0.12em", marginBottom:6 }}>MY PAGE</div>
          {data.savedAt&&<div style={{ fontSize:12, color:C.muted }}>最終更新：{new Date(data.savedAt).toLocaleDateString("ja-JP")}</div>}
        </div>

        {/* タブ */}
        <div style={{ display:"flex", borderBottom:"2px solid "+C.border, marginBottom:24, gap:0 }}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>switchTab(t.id)}
              style={{ padding:"10px 16px", background:"transparent", border:"none", borderBottom:tab===t.id?"2px solid "+C.accent:"2px solid transparent", marginBottom:"-2px", color:tab===t.id?C.accent:C.muted, cursor:"pointer", fontSize:13, fontWeight:tab===t.id?700:400, whiteSpace:"nowrap", transition:"all 0.2s" }}>
              {t.label}
            </button>
          ))}
        </div>


        {/* ══ 自分ノート STEP1: 気持ちタグ + 自由入力 ══ */}
        {tab==="note"&&noteStep==="step1"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            <div style={components.card({ theme:noteTheme })}>
              <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 1</div>

              {/* 気持ちタグ（任意） */}
              <div style={{ marginBottom:SP.xl }}>
                <div style={{ fontSize:FS.sm, fontWeight:FW.medium, color:tokens.text.secondary, marginBottom:SP.md }}>
                  今、どんな気持ち？<span style={{ fontSize:FS.xs, color:tokens.text.tertiary, marginLeft:SP.sm }}>（選ばなくてもOK）</span>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:SP.sm }}>
                  {EMOTIONS.map((emo) => {
                    const selected = noteEmotion === emo.value;
                    const hovered = hoveredEmotion === emo.value && !selected;
                    const Icon = emo.Icon;
                    // ★ 文字・アイコン色は emo.color で固定（選択前後で変えない）
                    // 選択感は border(2px) + box-shadow だけで表現する
                    // 背景は colorLighter（極薄）に留め、テキストコントラストを保持
                    const bg = selected ? emo.colorLighter
                               : hovered  ? emo.colorLighter
                               : tokens.neutral.surface;
                    const border = selected ? `2px solid ${emo.color}`
                                 : hovered  ? `1.5px solid ${emo.color}88`
                                 : `1.5px solid ${tokens.neutral.border}`;
                    const shadow = selected
                                 ? `0 2px 8px rgba(0,0,0,0.10), 0 0 0 1px ${emo.color}44`
                                 : "none";
                    return (
                      <button
                        key={emo.value}
                        type="button"
                        onClick={()=>setNoteEmotion(selected ? null : emo.value)}
                        onMouseEnter={()=>setHoveredEmotion(emo.value)}
                        onMouseLeave={()=>setHoveredEmotion(null)}
                        style={{
                          display:"inline-flex", alignItems:"center", gap:SP.sm,
                          padding:`${SP.sm}px ${SP.lg}px`,
                          background: bg,
                          color: emo.color,
                          border,
                          borderRadius: R.pill,
                          fontSize: FS.sm,
                          fontWeight: selected ? FW.semibold : FW.medium,
                          boxShadow: shadow,
                          cursor:"pointer", fontFamily:F,
                          transition: "background 0.15s ease-out, border-color 0.15s ease-out, box-shadow 0.15s ease-out",
                        }}
                      >
                        <Icon size={16} strokeWidth={1.8} color={emo.color}/>
                        <span>{emo.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <h2 style={{ fontSize:FS.h3+1, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.base}px` }}>
                今、どんなことを感じていますか?
              </h2>
              <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, margin:`0 0 ${SP["2xl"]}px` }}>
                うまくまとまっていなくて、大丈夫です。
              </p>
              <textarea
                value={noteBody}
                onChange={(e)=>setNoteBody(e.target.value)}
                placeholder="思っていることを、そのまま書いてみてください。"
                style={{ ...components.inputBase(), minHeight:140, resize:"vertical" }}
              />
              <button
                onClick={goToStep2}
                disabled={!noteBody.trim()}
                style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!noteBody.trim() }), marginTop:SP.lg, fontSize:FS.lg }}
              >
                次へ
              </button>
            </div>
          </div>
        )}

        {/* ══ 自分ノート step_reception: AI受容（ネガ路線） ══ */}
        {tab==="note"&&noteStep==="step_reception"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            {/* 数字カウンターなし: × ボタンのみ（ここは途中点ではなく受け取りの場所） */}
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
              <button
                onClick={requestCloseNote}
                aria-label="閉じる"
                style={{ width:28, height:28, borderRadius:8, border:"1px solid "+C.border, background:C.surface, color:C.muted, cursor:"pointer", fontSize:14, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center", padding:0, fontFamily:F }}
              >
                ×
              </button>
            </div>
            {/* 書いた内容を控えめに表示 */}
            <div style={{ background:tokens.neutral.surfaceAlt, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, padding:`${SP.lg}px ${SP.xl}px`, marginBottom:SP.xl }}>
              <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, marginBottom:SP.sm }}>あなたが書いたこと</div>
              <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:0, whiteSpace:"pre-line" }}>{noteBody}</p>
            </div>

            <div style={components.card({ theme:noteTheme })}>
              {/* ローディング */}
              {receptionLoading && (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:`${SP["6xl"]}px ${SP["2xl"]}px`, gap:SP.lg }}>
                  <div style={{ width:36, height:36, border:`3px solid ${noteTheme.light}`, borderTopColor:noteTheme.primary, borderRadius:"50%", animation:tokens.motion.spin }}/>
                  <div style={{ fontSize:FS.base, color:noteTheme.primary, fontWeight:FW.semibold }}>あなたの言葉を受け取っています…</div>
                </div>
              )}

              {/* 未生成（リロード復元時など）*/}
              {!receptionLoading && !receptionText && (
                <div style={{ textAlign:"center", padding:`${SP["4xl"]}px 0` }}>
                  <button
                    onClick={()=>generateReception()}
                    style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:`${SP.md}px ${SP["3xl"]}px`, fontSize:FS.base }}
                  >
                    受け取る
                  </button>
                </div>
              )}

              {/* 受容テキスト表示（フィードバック入力前） */}
              {!receptionLoading && receptionText && !showReceptionFeedback && (
                <>
                  {receptionError && (
                    <div style={{ padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.md, marginBottom:SP.lg, fontSize:FS.sm, color:"#7A5A1F", lineHeight:LH.normal }}>
                      <div style={{ marginBottom:SP.sm }}>受容メッセージの生成に失敗しました。下記は仮のものです。</div>
                      <button
                        onClick={()=>generateReception()}
                        style={{ padding:`${SP.xs}px ${SP.lg}px`, background:tokens.neutral.surface, border:"1px solid #C58A2D55", color:"#7A5A1F", borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                      >
                        もう一度試す
                      </button>
                    </div>
                  )}

                  {/* 受容メッセージ */}
                  <div style={{ ...components.highlightCard({ theme:noteTheme }), padding:`${SP["3xl"]}px ${SP["3xl"]}px`, marginBottom:SP.xl }}>
                    <p style={{ fontSize:FS.lg, color:tokens.text.primary, lineHeight:LH.loose, margin:0 }}>
                      {receptionText.message}
                    </p>
                  </div>

                  {/* 感情の名前 */}
                  <div style={{ textAlign:"center", marginBottom:SP["2xl"] }}>
                    <span style={{
                      display:"inline-block",
                      fontSize:FS.base,
                      fontWeight:FW.bold,
                      color:tokens.text.primary,
                    }}>
                      {receptionText.feeling}
                    </span>
                  </div>

                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:`0 0 ${SP.lg}px`, textAlign:"center" }}>
                    しっくりきますか？
                  </p>

                  <div style={{ display:"flex", flexDirection:"column", gap:SP.base }}>
                    <button
                      onClick={approveReception}
                      style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.md }}
                    >
                      しっくりくる
                    </button>
                    <button
                      onClick={()=>setShowReceptionFeedback(true)}
                      style={{ padding:SP.lg, background:"#F8D5D0", border:"none", borderRadius:R.lg, color:"#3A3735", fontSize:FS.md, fontWeight:FW.medium, cursor:"pointer", fontFamily:F, transition:`background ${tokens.motion.durationFast}` }}
                    >
                      ちょっと違う
                    </button>
                  </div>
                </>
              )}

              {/* 「少し違う」フィードバック入力（1回のみ） */}
              {!receptionLoading && showReceptionFeedback && (
                <div>
                  <div style={{ fontSize:FS.sm, color:tokens.text.secondary, marginBottom:SP.base, lineHeight:LH.relaxed }}>
                    どんなところが違いましたか？<br/>
                    <span style={{ color:tokens.text.tertiary, fontSize:FS.xs }}>一言だけ添えてもらえると助かります</span>
                  </div>
                  <textarea
                    value={receptionFeedback}
                    onChange={e=>setReceptionFeedback(e.target.value)}
                    placeholder="例：もっと疲れていること / 仕事のことが中心だった"
                    style={{ ...components.inputBase(), minHeight:72, fontSize:FS.md, resize:"none" }}
                    rows={2}
                  />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:SP.base, marginTop:SP.md }}>
                    <button
                      onClick={()=>{ setShowReceptionFeedback(false); setReceptionFeedback(""); }}
                      style={{ padding:SP.lg, background:tokens.neutral.surface, color:tokens.text.tertiary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={submitReceptionFeedback}
                      disabled={!receptionFeedback.trim()}
                      style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!receptionFeedback.trim() }), padding:SP.lg, fontSize:FS.md }}
                    >
                      伝える
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ 自分ノート step_confirm_save: 着地確認（ネガ路線） ══ */}
        {tab==="note"&&noteStep==="step_confirm_save"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            {/* ヘッダーなし: ここが短パスの終点 */}
            <div style={components.card({ theme:noteTheme })}>
              <div style={{ textAlign:"center", paddingTop:SP.base, marginBottom:SP["2xl"] }}>
                {/* 感情の名前を主役で大きく */}
                {receptionText?.feeling && (
                  <div style={{ marginBottom:SP["2xl"] }}>
                    <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide, marginBottom:SP.md }}>
                      今日の気持ち
                    </div>
                    <span style={{
                      display:"inline-block",
                      fontSize:FS.xl,
                      fontWeight:FW.bold,
                      color:tokens.text.primary,
                      lineHeight:LH.snug,
                    }}>
                      {receptionText.feeling}
                    </span>
                  </div>
                )}

                {/* 受容メッセージ */}
                {receptionText?.message && (
                  <div style={{ ...components.highlightCard({ theme:noteTheme }), padding:`${SP["2xl"]}px ${SP["2xl"]}px`, marginBottom:SP["2xl"], textAlign:"left" }}>
                    <p style={{ fontSize:FS.base, color:tokens.text.primary, lineHeight:LH.loose, margin:0 }}>
                      {receptionText.message}
                    </p>
                  </div>
                )}

                <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, margin:`0 0 ${SP["2xl"]}px` }}>
                  このまま残しておきますか？<br/>もう少し言葉を見てみることもできます。
                </p>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:SP.base }}>
                <button
                  onClick={()=>closeNote("user_choice")}
                  style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.md }}
                >
                  このまま保存する
                </button>
                <button
                  onClick={()=>{ setNoteStep("step_reception"); setShowReceptionFeedback(true); }}
                  style={{ padding:SP.lg, background:"#F8D5D0", border:"none", borderRadius:R.lg, color:"#3A3735", fontSize:FS.md, fontWeight:FW.medium, cursor:"pointer", fontFamily:F, transition:`background ${tokens.motion.durationFast}` }}
                >
                  ちょっと違う
                </button>
                <button
                  onClick={()=>enterStep5()}
                  style={{ ...components.buttonSecondary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.base }}
                >
                  もう少し見てみる
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ 自分ノート STEP2: 近い言葉 ══ */}
        {tab==="note"&&noteStep==="step2"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            {/* STEP1で書いた内容を上部に控えめに表示 */}
            <div style={{ background:tokens.neutral.surfaceAlt, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, padding:`${SP.lg}px ${SP.xl}px`, marginBottom:SP.xl }}>
              <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, marginBottom:SP.sm }}>あなたが書いたこと</div>
              <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:0, whiteSpace:"pre-line" }}>{noteBody}</p>
            </div>

            <div style={components.card({ theme:noteTheme })}>
              <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 2</div>
              <h2 style={{ fontSize:FS.h3, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.xl}px` }}>
                近い感覚はありますか？
              </h2>

              {nearLoading && (() => {
                // STEP2 のコンパクト版植物アニメ：自分の言葉タブと同じ新SVG構造・同じ keyframes を使い、
                // duration を 7s（5フェーズが短く流れるよう）に縮めて、render サイズも 72px に縮小する。
                const STEP_PLANT_DURATION = "7s";
                const particles = [
                  { x:-30, y:-6,  size:4, color:"#8FB996", anim:"drift1", duration:5,   delay:"0s"   },
                  { x: 32, y: 2,  size:3, color:"#D9A56B", anim:"drift3", duration:5.6, delay:"0.8s" },
                  { x:-22, y: 22, size:4, color:"#D9A56B", anim:"drift5", duration:6.2, delay:"1.6s" },
                  { x: 24, y:-20, size:3, color:"#8FB996", anim:"drift6", duration:5.4, delay:"2.4s" },
                ];
                return (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:`${SP["6xl"]}px ${SP["2xl"]}px`, gap:SP.lg }}>
                    <div style={{ position:"relative", width:96, height:96 }}>
                      {particles.map((p, i) => (
                        <span key={i} style={{
                          position:"absolute",
                          left: `calc(50% + ${p.x}px)`,
                          top:  `calc(50% + ${p.y}px)`,
                          width: p.size, height: p.size,
                          borderRadius:"50%",
                          background: p.color,
                          transform:"translate(-50%, -50%)",
                          animation: `${p.anim} ${p.duration}s ease-in-out infinite`,
                          animationDelay: p.delay,
                          opacity: 0.45,
                          pointerEvents:"none",
                          willChange:"transform, opacity",
                        }}/>
                      ))}
                      <div style={{
                        position:"absolute", left:"50%", top:"50%",
                        transform:"translate(-50%, -50%)",
                        width:72, height:72,
                      }}>
                        <svg
                          viewBox="0 0 120 120"
                          width="72" height="72"
                          style={{
                            display:"block",
                            animation:`plantBreathe ${STEP_PLANT_DURATION} ease-in-out infinite`,
                            willChange:"transform, opacity",
                          }}
                          aria-hidden="true"
                        >
                          <ellipse cx="60" cy="100" rx="26" ry="3" fill="#F0E4D0" opacity="0.6"/>
                          <circle
                            cx="60" cy="95" r="5" fill="#D9A56B"
                            style={{ transformOrigin:"60px 95px", transformBox:"fill-box",
                                     animation:`seedSway ${STEP_PLANT_DURATION} ease-in-out infinite` }}
                          />
                          <path
                            d="M 60 95 Q 57 78 61 65 Q 59 56 60 50"
                            stroke="#8FB996" strokeWidth="2" fill="none" strokeLinecap="round"
                            style={{ strokeDasharray:60, strokeDashoffset:60,
                                     animation:`stemDraw ${STEP_PLANT_DURATION} ease-in-out infinite` }}
                          />
                          <path
                            d="M 60 50 Q 42 42 32 50 Q 44 56 60 53 Z"
                            fill="#A4D2B0" stroke="#7DAE8A" strokeWidth="1" strokeLinejoin="round"
                            style={{ transformOrigin:"60px 50px", transformBox:"fill-box",
                                     animation:`leafRevealLeft ${STEP_PLANT_DURATION} ease-in-out infinite` }}
                          />
                          <path
                            d="M 60 50 Q 78 42 88 50 Q 76 56 60 53 Z"
                            fill="#C5E0CD" stroke="#7DAE8A" strokeWidth="1" strokeLinejoin="round"
                            style={{ transformOrigin:"60px 50px", transformBox:"fill-box",
                                     animation:`leafRevealRight ${STEP_PLANT_DURATION} ease-in-out infinite` }}
                          />
                        </svg>
                      </div>
                    </div>
                    <div style={{ fontSize:FS.base, color:noteTheme.primary, fontWeight:FW.semibold }}>気持ちに近い言葉を一緒に探しています</div>
                  </div>
                );
              })()}

              {!nearLoading && (
                <>
                  {nearError && (
                    <div style={{ padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.md, marginBottom:SP.lg, fontSize:FS.sm, color:"#7A5A1F", lineHeight:LH.normal }}>
                      <div style={{ marginBottom:SP.sm }}>候補の生成に失敗しました。下の候補は仮のものです。「どれも違う」から自由入力できます。</div>
                      <button
                        onClick={goToStep2}
                        style={{ padding:`${SP.xs}px ${SP.lg}px`, background:tokens.neutral.surface, border:"1px solid #C58A2D55", color:"#7A5A1F", borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                      >
                        もう一度AIで生成する
                      </button>
                    </div>
                  )}

                  <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:SP.lg }}>
                    {nearCandidates.map((c, i) => {
                      const selected = nearSelected?.type === "candidate" && nearSelected.text === c;
                      const hovered = hoveredCandidate === i && !selected;
                      // ★ 文字色は tokens.text.primary で固定（選択前後で変えない）
                      // 選択感は border(2px) + box-shadow だけで表現する
                      const bg     = selected ? noteTheme.light
                                   : hovered  ? noteTheme.light
                                   : tokens.neutral.surface;
                      const border = selected ? `2px solid ${noteTheme.primary}`
                                   : hovered  ? `1.5px solid ${noteTheme.primary}88`
                                   : `1.5px solid ${tokens.neutral.border}`;
                      const shadow = selected
                                   ? `0 2px 8px rgba(0,0,0,0.10), 0 0 0 1px ${noteTheme.primary}44`
                                   : "none";
                      return (
                        <button
                          key={i}
                          onClick={()=>handleNearSelect(c, "candidate")}
                          onMouseEnter={()=>setHoveredCandidate(i)}
                          onMouseLeave={()=>setHoveredCandidate(null)}
                          style={{
                            textAlign:"left",
                            padding:`${SP.lg}px ${SP["2xl"]}px`,
                            background: bg,
                            border,
                            borderRadius: R.lg,
                            fontSize: FS.md,
                            lineHeight: LH.snug,
                            color: tokens.text.primary,
                            fontWeight: FW.medium,
                            cursor: "pointer",
                            fontFamily: F,
                            boxShadow: shadow,
                            transition: "background 0.15s ease-out, border-color 0.15s ease-out, box-shadow 0.15s ease-out",
                          }}
                        >
                          {c}
                        </button>
                      );
                    })}

                    {/* どれも違う：自由入力 */}
                    <div style={{ marginTop:SP.xs }}>
                      <button
                        onClick={()=>handleNearSelect(nearFreeDraft.trim() || "", "free")}
                        style={{
                          textAlign:"left", width:"100%",
                          padding:`${SP.lg}px ${SP.xl}px`,
                          background: nearSelected?.type === "free" ? noteTheme.light : tokens.neutral.surface,
                          border: nearSelected?.type === "free"
                            ? `2px dashed ${noteTheme.primary}`
                            : `1.5px dashed ${tokens.neutral.border}`,
                          borderRadius: R.lg,
                          fontSize: FS.base,
                          color: tokens.text.tertiary,
                          boxShadow: nearSelected?.type === "free"
                            ? `0 2px 8px rgba(0,0,0,0.10), 0 0 0 1px ${noteTheme.primary}44`
                            : "none",
                          transition: "background 0.15s ease-out, border-color 0.15s ease-out, box-shadow 0.15s ease-out",
                          fontWeight: FW.medium, cursor:"pointer", fontFamily:F,
                        }}
                      >
                        どれも違う（自分で書く）
                      </button>
                      {nearSelected?.type === "free" && (
                        <textarea
                          value={nearFreeDraft}
                          onChange={(e)=>{ setNearFreeDraft(e.target.value); setNearSelected({ text:e.target.value.trim(), type:"free" }); }}
                          placeholder="例：もっと自分の話を聞いてほしかった"
                          style={{ ...components.inputBase(), marginTop:SP.base, minHeight:72, fontSize:FS.md, resize:"vertical" }}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:SP.base }}>
                    <button
                      onClick={goBackToStep1}
                      style={{ padding:SP.lg, background:tokens.neutral.surface, color:tokens.text.secondary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                    >
                      戻る
                    </button>
                    <button
                      onClick={goToStep3}
                      disabled={!nearSelected || !nearSelected.text?.trim()}
                      style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!nearSelected || !nearSelected.text?.trim() }), padding:SP.lg, fontSize:FS.md }}
                    >
                      次へ
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ 自分ノート STEP3: 場面の補足（任意） ══ */}
        {tab==="note"&&noteStep==="step3"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            {/* STEP1の本文を控えめに */}
            <div style={{ background:tokens.neutral.surfaceAlt, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, padding:`${SP.lg}px ${SP.xl}px`, marginBottom:SP.xl }}>
              <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, marginBottom:SP.sm }}>あなたが書いたこと</div>
              <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:0, whiteSpace:"pre-line" }}>{noteBody}</p>
            </div>

            <div style={components.card({ theme:noteTheme })}>
              <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 3</div>

              {/* 選んだ「近い言葉」を主役で表示 */}
              <div style={{ marginBottom:SP.xl }}>
                <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, marginBottom:SP.sm }}>選んだ言葉</div>
                <div style={{ ...components.chip({ theme:noteTheme }), padding:`${SP.base}px ${SP.xl}px`, fontSize:FS.md, lineHeight:LH.snug }}>
                  {nearSelected?.text}
                </div>
              </div>

              <h2 style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.sm}px` }}>
                {STEP3_QUESTION}
              </h2>
              <p style={{ fontSize:FS.sm, color:tokens.text.tertiary, lineHeight:LH.normal, margin:`0 0 ${SP.lg}px` }}>
                思い出せる場面があれば／なければスキップして大丈夫です
              </p>

              <textarea
                value={detailDraft}
                onChange={(e)=>setDetailDraft(e.target.value)}
                placeholder="例：上司に状況を聞かれたとき／会議で意見を求められたとき"
                style={{ ...components.inputBase(), minHeight:110, fontSize:FS.md, resize:"vertical" }}
              />

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 2fr", gap:SP.base, marginTop:SP.lg }}>
                <button
                  onClick={goBackToStep2}
                  style={{ padding:`${SP.lg}px ${SP.base}px`, background:tokens.neutral.surface, color:tokens.text.secondary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                >
                  戻る
                </button>
                <button
                  onClick={()=>goToStep4({ skipped:true })}
                  style={{ padding:`${SP.lg}px ${SP.base}px`, background:tokens.neutral.surface, color:tokens.text.secondary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                >
                  スキップ
                </button>
                <button
                  onClick={()=>goToStep4({ skipped:false })}
                  style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.md }}
                >
                  次へ
                </button>
              </div>
            </div>
          </div>
        )}

        {tab==="note"&&noteStep==="step4"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            <div style={components.card({ theme:noteTheme })}>
              <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 4 · 整理</div>
              <h2 style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.xl}px` }}>
                こんな気持ちがあったのかもしれません。
              </h2>

              {summaryALoading && (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:`${SP["7xl"]}px ${SP["2xl"]}px`, gap:SP.lg }}>
                  <div style={{ width:36, height:36, border:`3px solid ${noteTheme.light}`, borderTopColor:noteTheme.primary, borderRadius:"50%", animation:tokens.motion.spin }}/>
                  <div style={{ fontSize:FS.base, color:noteTheme.primary, fontWeight:FW.semibold }}>言葉を整理しています…</div>
                </div>
              )}

              {!summaryALoading && summaryA && (
                <>
                  {summaryAError && (
                    <div style={{ padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.md, marginBottom:SP.lg, fontSize:FS.sm, color:"#7A5A1F", lineHeight:LH.normal }}>
                      <div style={{ marginBottom:SP.sm }}>整理がうまくいかなかったので、仮の文を表示しています。</div>
                      <button
                        onClick={()=>generateSummaryA()}
                        style={{ padding:`${SP.xs}px ${SP.lg}px`, background:tokens.neutral.surface, border:"1px solid #C58A2D55", color:"#7A5A1F", borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                      >
                        もう一度AIで整理する
                      </button>
                    </div>
                  )}

                  <div style={{ ...components.highlightCard({ theme:noteTheme }), padding:`${SP["3xl"]}px ${SP["3xl"]}px`, marginBottom:SP.xl }}>
                    <p style={{ fontSize:FS.lg, color:tokens.text.primary, lineHeight:LH.loose, margin:0, whiteSpace:"pre-line" }}>
                      {summaryA.text}
                    </p>
                  </div>

                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:`0 0 ${SP.lg}px`, textAlign:"center" }}>これで近いですか？</p>

                  {!showAFeedbackInput && (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:SP.md }}>
                      <button
                        onClick={approveSummaryA}
                        style={{ ...components.buttonPrimary({ theme:noteTheme }), width:"100%", padding:SP.lg, fontSize:FS.md }}
                      >
                        そのとおり
                      </button>
                      <button
                        onClick={()=>setShowAFeedbackInput(true)}
                        style={{ width:"100%", padding:SP.lg, background:"#F8D5D0", border:"none", borderRadius:R.lg, color:"#3A3735", fontSize:FS.md, fontWeight:FW.medium, cursor:"pointer", fontFamily:F, transition:`background ${tokens.motion.durationFast}` }}
                      >
                        少し違う
                      </button>
                    </div>
                  )}

                  {showAFeedbackInput && (
                    <div style={{ marginTop:SP.xs, padding:SP.xl, background:tokens.neutral.bg, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.xl }}>
                      <div style={{ fontSize:FS.sm, color:tokens.text.secondary, marginBottom:SP.base }}>どんなところが違いましたか？</div>
                      <textarea
                        value={aFeedbackDraft}
                        onChange={(e)=>setAFeedbackDraft(e.target.value)}
                        placeholder="例：もっと仕事への疲れの話だと思う"
                        style={{ ...components.inputBase(), background:tokens.neutral.surface, minHeight:80, fontSize:FS.base, resize:"vertical" }}
                      />
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:SP.base, marginTop:SP.md }}>
                        <button
                          onClick={()=>{ setShowAFeedbackInput(false); setAFeedbackDraft(""); }}
                          style={{ padding:SP.lg-1, background:tokens.neutral.surface, color:tokens.text.tertiary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.md, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                        >
                          キャンセル
                        </button>
                        <button
                          onClick={submitAFeedback}
                          disabled={!aFeedbackDraft.trim()}
                          style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!aFeedbackDraft.trim() }), padding:SP.lg-1, fontSize:FS.base }}
                        >
                          整理し直す
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop:SP.lg, textAlign:"center" }}>
                    <button
                      onClick={goBackToStep3}
                      style={{ background:"transparent", border:"none", color:tokens.text.tertiary, fontSize:FS.sm, cursor:"pointer", fontFamily:F, textDecoration:"underline" }}
                    >
                      ← STEP3 に戻る
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ 自分ノート STEP5: 深掘り問い選択 ══ */}
        {tab==="note"&&noteStep==="step5"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            {/* 言葉の変化（cycleIndex>=1のとき）— ヒーローと同じ暖色アクセント */}
            {cycleIndex >= 1 && completedSummaries.length >= 1 && (
              <div style={{ background:tokens.hero.gradient, border:`1px solid ${tokens.hero.border}55`, borderRadius:R.xl, padding:`${SP.xl}px ${SP["2xl"]}px`, marginBottom:SP.xl, boxShadow:tokens.shadow.sm }}>
                <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:tokens.accent.orange, letterSpacing:tokens.letterSpacing.wide, marginBottom:SP.base }}>✨ あなたの言葉の変化</div>
                <div style={{ display:"flex", flexDirection:"column", gap:SP.sm }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                    <span style={{ fontSize:FS.xs, color:tokens.accent.orange, fontWeight:FW.semibold, flexShrink:0, paddingTop:2 }}>最初</span>
                    <span style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal }}>{noteBody.length > 60 ? noteBody.slice(0, 58) + "…" : noteBody}</span>
                  </div>
                  <div style={{ textAlign:"center", color:tokens.accent.orange, fontSize:FS.xs }}>↓</div>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                    <span style={{ fontSize:FS.xs, color:tokens.accent.orange, fontWeight:FW.semibold, flexShrink:0, paddingTop:2 }}>今　</span>
                    <span style={{ fontSize:FS.base, color:tokens.text.primary, lineHeight:LH.normal, fontWeight:FW.medium }}>
                      {(completedSummaries[completedSummaries.length-1]?.text) || summaryA?.text}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* 直前の整理を上部に表示 */}
            {cycleIndex < 1 && (completedSummaries[completedSummaries.length-1] || summaryA) && (
              <div style={{ background:tokens.neutral.surfaceAlt, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, padding:`${SP.lg}px ${SP.xl}px`, marginBottom:SP.xl }}>
                <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, marginBottom:SP.sm }}>直前の整理</div>
                <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:0 }}>
                  {(completedSummaries[completedSummaries.length-1]?.text) || summaryA?.text}
                </p>
              </div>
            )}

            <div style={components.card({ theme:noteTheme })}>
              {!NEGATIVE_EMOTIONS.has(noteEmotion) && <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 5 · 問い直し（{cycleIndex + 1}サイクル目）</div>}
              <h2 style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.lg}px` }}>
                もう少し見てみますか？
              </h2>

              {probeLoading && (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:`${SP["7xl"]}px ${SP["2xl"]}px`, gap:SP.lg }}>
                  <div style={{ width:36, height:36, border:`3px solid ${noteTheme.light}`, borderTopColor:noteTheme.primary, borderRadius:"50%", animation:tokens.motion.spin }}/>
                  <div style={{ fontSize:FS.base, color:noteTheme.primary, fontWeight:FW.semibold }}>問いを選んでいます…</div>
                </div>
              )}

              {!probeLoading && (
                <>
                  {probeError && (
                    <div style={{ padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.md, marginBottom:SP.lg, fontSize:FS.sm, color:"#7A5A1F", lineHeight:LH.normal }}>
                      <div style={{ marginBottom:SP.sm }}>問いの生成に失敗したので、仮の問いを表示しています。</div>
                      <button
                        onClick={()=>generateProbes(cycleIndex)}
                        style={{ padding:`${SP.xs}px ${SP.lg}px`, background:tokens.neutral.surface, border:"1px solid #C58A2D55", color:"#7A5A1F", borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                      >
                        もう一度AIで生成する
                      </button>
                    </div>
                  )}

                  {!selectedProbe && (
                    <>
                      <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:SP.md }}>
                        {probeCandidates.map((p, i) => {
                          const isNegaPath = NEGATIVE_EMOTIONS.has(noteEmotion);
                          const typeLabel = isNegaPath
                            ? (p.type === "A" ? "本音" : p.type === "B" ? "願い" : p.type === "C" ? "伝言" : "")
                            : (p.type === "A" ? "感情" : p.type === "B" ? "状況" : p.type === "C" ? "視点" : "");
                          // タイプ別カラー：A=暖色オレンジ(感情) / B=ブルー(状況) / C=若葉グリーン(視点)
                          const typeColor = p.type === "A" ? tokens.accent.orange
                            : p.type === "B" ? tokens.accent.primary
                            : p.type === "C" ? tokens.accent.green
                            : tokens.text.tertiary;
                          return (
                            <button
                              key={i}
                              onClick={()=>handleProbeSelect(p)}
                              style={{ textAlign:"left", padding:`${SP.lg}px ${SP["2xl"]}px`, background:tokens.neutral.surface, border:`1.5px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.md, lineHeight:LH.snug, color:tokens.text.primary, fontWeight:FW.medium, cursor:"pointer", fontFamily:F, transition:`all ${tokens.motion.durationFast} ${tokens.motion.ease}` }}
                            >
                              {typeLabel && <span style={{ display:"inline-block", marginRight:SP.base, padding:`${SP.xxs}px ${SP.base}px`, background:typeColor+"15", color:typeColor, borderRadius:R.sm, fontSize:FS.xxs, fontWeight:FW.semibold }}>{typeLabel}</span>}
                              {p.question}
                            </button>
                          );
                        })}
                        <button
                          onClick={()=>closeNote("user_choice")}
                          style={{ textAlign:"left", padding:`${SP.lg}px ${SP["2xl"]}px`, background:tokens.neutral.bg, border:`1.5px dashed ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, lineHeight:LH.snug, color:tokens.text.tertiary, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                        >
                          ✓ もう大丈夫
                        </button>
                      </div>
                      {cycleIndex >= 3 && (
                        <div style={{ marginTop:SP.md, padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}55`, borderRadius:R.md }}>
                          <p style={{ fontSize:FS.sm, color:tokens.semantic.warning, lineHeight:LH.normal, margin:0 }}>
                            まだ整理途中ですが、ここまでで一度区切りますか？それとも、もう少し続けますか？
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {selectedProbe && (
                    <div>
                      <div style={{ padding:`${SP.lg}px ${SP.xl}px`, background:noteTheme.light, border:`1px solid ${noteTheme.medium}55`, borderRadius:R.lg, color:tokens.text.primary, fontSize:FS.md, fontWeight:FW.semibold, lineHeight:LH.snug, marginBottom:SP.md }}>
                        {selectedProbe.question}
                      </div>
                      <textarea
                        value={probeAnswer}
                        onChange={(e)=>setProbeAnswer(e.target.value)}
                        placeholder="思いつくまま、1行でも"
                        style={{ ...components.inputBase(), minHeight:96, fontSize:FS.md, resize:"vertical" }}
                      />
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:SP.base, marginTop:SP.md }}>
                        <button
                          onClick={()=>{ setSelectedProbe(null); setProbeAnswer(""); }}
                          style={{ padding:SP.lg, background:tokens.neutral.surface, color:tokens.text.secondary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                        >
                          他の問いへ
                        </button>
                        <button
                          onClick={submitProbeAnswer}
                          disabled={!probeAnswer.trim()}
                          style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!probeAnswer.trim() }), padding:SP.lg, fontSize:FS.md }}
                        >
                          整理する
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <InsightBox
              insightDraft={insightDraft}
              onDraftChange={setInsightDraft}
              insights={insights}
              onCommit={commitInsight}
            />
          </div>
        )}

        {/* ══ 自分ノート STEP6: bレベル整理（意味づけ寄り） ══ */}
        {tab==="note"&&noteStep==="step6"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <NoteFlowHeader/>
            <div style={components.card({ theme:noteTheme })}>
              {!NEGATIVE_EMOTIONS.has(noteEmotion) && <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:noteTheme.primary, letterSpacing:tokens.letterSpacing.wider, marginBottom:SP.base }}>STEP 6 · 整理（{cycleIndex + 1}サイクル目）</div>}
              <h2 style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug, margin:`0 0 ${SP.xl}px` }}>
                まだ整理途中ですが、こんなふうに見え始めています。
              </h2>

              {summaryBLoading && (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:`${SP["7xl"]}px ${SP["2xl"]}px`, gap:SP.lg }}>
                  <div style={{ width:36, height:36, border:`3px solid ${noteTheme.light}`, borderTopColor:noteTheme.primary, borderRadius:"50%", animation:tokens.motion.spin }}/>
                  <div style={{ fontSize:FS.base, color:noteTheme.primary, fontWeight:FW.semibold }}>言葉を整理しています…</div>
                </div>
              )}

              {!summaryBLoading && summaryB && (
                <>
                  {summaryBError && (
                    <div style={{ padding:`${SP.md}px ${SP.lg}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.md, marginBottom:SP.lg, fontSize:FS.sm, color:"#7A5A1F", lineHeight:LH.normal }}>
                      <div style={{ marginBottom:SP.sm }}>整理がうまくいかなかったので、仮の文を表示しています。</div>
                      <button
                        onClick={()=>generateSummaryB()}
                        style={{ padding:`${SP.xs}px ${SP.lg}px`, background:tokens.neutral.surface, border:"1px solid #C58A2D55", color:"#7A5A1F", borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                      >
                        もう一度AIで整理する
                      </button>
                    </div>
                  )}

                  <div style={{ ...components.highlightCard({ theme:noteTheme }), padding:`${SP["3xl"]}px ${SP["3xl"]}px`, marginBottom:SP.xl }}>
                    <p style={{ fontSize:FS.lg, color:tokens.text.primary, lineHeight:LH.loose, margin:0, whiteSpace:"pre-line" }}>
                      {summaryB.text}
                    </p>
                  </div>

                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal, margin:`0 0 ${SP.lg}px`, textAlign:"center" }}>合ってますか？</p>

                  {!showBFeedbackInput && (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:SP.base }}>
                      <button
                        onClick={()=>setShowBFeedbackInput(true)}
                        style={{ padding:SP.lg, background:tokens.neutral.surface, color:tokens.text.secondary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.lg, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                      >
                        ちょっと違う
                      </button>
                      <button
                        onClick={approveSummaryB}
                        style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.md }}
                      >
                        そのとおり
                      </button>
                    </div>
                  )}

                  {showBFeedbackInput && (
                    <div style={{ marginTop:SP.xs, padding:SP.xl, background:tokens.neutral.bg, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.xl }}>
                      <div style={{ fontSize:FS.sm, color:tokens.text.secondary, marginBottom:SP.base }}>どんなところが違いましたか？</div>
                      <textarea
                        value={bFeedbackDraft}
                        onChange={(e)=>setBFeedbackDraft(e.target.value)}
                        placeholder="例：価値観というより、もう少し具体的な気持ちのほうが近い"
                        style={{ ...components.inputBase(), background:tokens.neutral.surface, minHeight:80, fontSize:FS.base, resize:"vertical" }}
                      />
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:SP.base, marginTop:SP.md }}>
                        <button
                          onClick={()=>{ setShowBFeedbackInput(false); setBFeedbackDraft(""); }}
                          style={{ padding:SP.lg-1, background:tokens.neutral.surface, color:tokens.text.tertiary, border:`1px solid ${tokens.neutral.border}`, borderRadius:R.md, fontSize:FS.base, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                        >
                          キャンセル
                        </button>
                        <button
                          onClick={submitBFeedback}
                          disabled={!bFeedbackDraft.trim()}
                          style={{ ...components.buttonPrimary({ theme:noteTheme, disabled:!bFeedbackDraft.trim() }), padding:SP.lg-1, fontSize:FS.base }}
                        >
                          整理し直す
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ 自分ノート 完了画面 ══ */}
        {tab==="note"&&noteStep==="done"&&(
          <div style={{ animation:tokens.motion.fadeUp }}>
            <div style={{ ...components.card({ theme:noteTheme }), padding:`${SP["6xl"]}px ${SP["4xl"]}px ${SP["4xl"]}px` }}>
              <div style={{ textAlign:"center", marginBottom:SP["3xl"] }}>
                <SavedIcon/>
                <div style={{ fontSize:FS.h3, fontWeight:FW.semibold, color:tokens.text.primary, marginBottom:SP.sm }}>あなたの言葉として、残りました</div>
                <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, margin:0 }}>
                  {cycleIndex > 0
                    ? `${cycleIndex}回、気持ちを見つめ直しました。`
                    : "今日の気づきが、ここに残りました。"}
                </p>
                <p style={{ fontSize:FS.sm, color:tokens.text.tertiary, lineHeight:LH.normal, margin:`${SP.sm}px 0 0` }}>
                  「自分ログ」タブから、いつでも見返せます。
                </p>
              </div>

              {/* 言葉の変化サマリ（最初→今） — stockテーマ(オレンジ)で強調 */}
              {(() => {
                const finalSummary = (completedSummaries[completedSummaries.length-1]?.text) || summaryA?.text;
                if (!finalSummary || !noteBody.trim()) return null;
                return (
                  <div style={{ background:tokens.hero.gradient, border:`1px solid ${tokens.hero.border}55`, borderRadius:R.xl, padding:`${SP.xl}px ${SP["2xl"]}px`, marginBottom:SP.xl, boxShadow:tokens.shadow.sm }}>
                    <div style={{ fontSize:FS.xs, fontWeight:FW.semibold, color:tokens.accent.orange, letterSpacing:tokens.letterSpacing.wide, marginBottom:SP.md }}>✨ 今日の言葉の変化</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:SP.sm }}>
                      <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                        <span style={{ fontSize:FS.xs, color:tokens.accent.orange, fontWeight:FW.semibold, flexShrink:0, paddingTop:2, minWidth:34 }}>最初</span>
                        <span style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.normal }}>{noteBody.length > 80 ? noteBody.slice(0, 78) + "…" : noteBody}</span>
                      </div>
                      <div style={{ textAlign:"center", color:tokens.accent.orange, fontSize:FS.xs }}>↓</div>
                      <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                        <span style={{ fontSize:FS.xs, color:tokens.accent.orange, fontWeight:FW.semibold, flexShrink:0, paddingTop:2, minWidth:34 }}>今</span>
                        <span style={{ fontSize:FS.base, color:tokens.text.primary, lineHeight:LH.relaxed, fontWeight:FW.medium }}>{finalSummary}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* CTA: 新しいノート / 自分の言葉を見る */}
              <div style={{ display:"flex", flexDirection:"column", gap:SP.base }}>
                <button
                  onClick={()=>{ startNewNote(); switchTab("stock"); }}
                  style={{ ...components.buttonSecondary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.base }}
                >
                  自分の言葉を見る →
                </button>
                <button
                  onClick={startNewNote}
                  style={{ ...components.buttonPrimary({ theme:noteTheme }), padding:SP.lg, fontSize:FS.md }}
                >
                  新しいノートを書く
                </button>
              </div>
            </div>
          </div>
        )}


        {/* ══ 自分の言葉 (v4 §13 集計, mockup-v2準拠) ══ */}
        {tab==="stock"&&(() => {
          const stockTheme = themeFor("stock");
          const strengthTheme = themeFor("note");
          // iconHint -> Icon コンポーネント
          const iconMap = { shield:Shield, leaf:Leaf, people:Users, compass:Compass, heart:Heart, sparkle:Sparkles, book:BookOpen, clock:Clock };
          // アイコン色：青白の空気感統一。ブルーを基調に少しの若葉グリーン/暖色オレンジで余韻
          const iconColorMap = {
            shield:  tokens.accent.primary,
            leaf:    tokens.accent.green,
            people:  tokens.accent.primary,
            compass: tokens.accent.primary,
            heart:   tokens.accent.orange,
            sparkle: tokens.accent.orange,
            book:    tokens.accent.primary,
            clock:   tokens.text.tertiary,
          };
          // ワードクラウド用パレット：ブルー基調 + 控えめな暖色アクセント
          const cloudColors = [
            tokens.accent.primary,     // 優しいブルー
            tokens.accent.orange,      // 落ち着いたオレンジ
            tokens.accent.primaryDark, // 濃いブルー
            tokens.accent.green,       // 若葉グリーン
            tokens.accent.medium,      // 中間ブルー
            tokens.text.secondary,     // ダークグレー
          ];
          return (
          <div style={{ animation:tokens.motion.fadeUp }}>
            {/* ノート未作成時 */}
            {(!stockAgg || stockAgg.approvedCount === 0) && !stockLoading && (
              <div style={{ background:tokens.neutral.surface, borderRadius:R["2xl"], padding:`${SP["8xl"]}px ${SP["5xl"]}px`, textAlign:"center", boxShadow:tokens.shadow.sm }}>
                <div style={{ fontSize:36, marginBottom:SP["2xl"] }}>💬</div>
                <div style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, marginBottom:SP.md }}>まずは自分ノートを書いてみましょう</div>
                <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, marginBottom:SP["2xl"] }}>
                  整理に「そのとおり」と答えた言葉が、ここに集まります。
                </p>
                <button
                  onClick={()=>switchTab("note")}
                  style={{ ...components.buttonPrimary({ theme:stockTheme, fullWidth:false }), padding:`${SP.md}px ${SP["5xl"]}px`, fontSize:FS.base }}
                >
                  自分ノートを書く →
                </button>
              </div>
            )}

            {/* データ不足（承認済み < 3）— AI を呼ばない静かな空状態 */}
            {stockAgg && stockAgg.insufficient && !stockLoading && (
              <>
                <section style={{
                  background: tokens.hero.gradient,
                  border: `1px solid ${tokens.hero.border}55`,
                  borderRadius: R["3xl"],
                  padding: `${SP["6xl"]}px ${SP["3xl"]}px`,
                  marginBottom: SP.xl,
                  boxShadow: tokens.shadow.sm,
                  textAlign: "center",
                }}>
                  <div style={{ fontSize:FS.sm, color:tokens.accent.orange, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide, marginBottom:SP.md }}>今のあなたを一言で</div>
                  <div style={{ fontSize:FS.h2, color:tokens.text.primary, fontWeight:FW.bold, lineHeight:LH.snug, marginBottom:SP.lg }}>
                    PathNoteで書いた言葉が、<br/>まだ少ないです
                  </div>
                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, margin:`0 auto`, maxWidth:300 }}>
                    あと {Math.max(0, 3 - stockAgg.approvedCount)} 件くらい書くと、ここに言葉が育ってきます。
                  </p>
                  <button
                    onClick={()=>switchTab("note")}
                    style={{ ...components.buttonPrimary({ theme:stockTheme, fullWidth:false }), padding:`${SP.md}px ${SP["5xl"]}px`, fontSize:FS.base, marginTop:SP["2xl"] }}
                  >
                    自分ノートを書く →
                  </button>
                </section>
                <p style={{ fontSize:FS.xs, color:tokens.text.tertiary, textAlign:"center", lineHeight:LH.normal, margin:`${SP["2xl"]}px 0 0` }}>
                  ※ 「繰り返し現れる言葉」「自然とやっていること」「言葉の変化」は、もう少し言葉が集まってからお見せします。
                </p>
              </>
            )}

            {/* ローディング：成長する植物SVG + 粒子 + 段階メッセージ */}
            {stockLoading && (() => {
              const stageMessages = [
                "言葉を集めています…",
                "あなたの言葉を見つめています…",
                "育ってきた言葉を整えています…",
                "もうすぐ見えてきます…",
              ];
              // 植物の周囲に配置する 6 粒子：色（緑/オレンジ）・サイズ・アニメーション・遅延を分散
              const particles = [
                { x:-46, y:-10, size:5, color:"#8FB996", anim:"drift1", duration:6, delay:"0s"   },
                { x: 48, y: -4, size:4, color:"#D9A56B", anim:"drift2", duration:7, delay:"1.4s" },
                { x:-38, y: 24, size:6, color:"#8FB996", anim:"drift3", duration:8, delay:"2.6s" },
                { x: 40, y: 30, size:5, color:"#D9A56B", anim:"drift4", duration:6.5, delay:"0.7s" },
                { x:-20, y:-32, size:4, color:"#D9A56B", anim:"drift5", duration:7.5, delay:"3.2s" },
                { x: 22, y:-28, size:5, color:"#8FB996", anim:"drift6", duration:5.5, delay:"1.9s" },
              ];
              const PLANT_DURATION = "14s";
              return (
                <div style={{ background:tokens.neutral.surface, borderRadius:R["2xl"], padding:`${SP["8xl"]}px ${SP["4xl"]}px`, textAlign:"center", boxShadow:tokens.shadow.sm }}>
                  {/* 植物 + 粒子コンテナ */}
                  <div style={{ position:"relative", width:140, height:120, margin:`0 auto ${SP["2xl"]}px` }}>
                    {/* 周囲の粒子 */}
                    {particles.map((p, i) => (
                      <span key={i} style={{
                        position:"absolute",
                        left: `calc(50% + ${p.x}px)`,
                        top:  `calc(50% + ${p.y}px)`,
                        width: p.size, height: p.size,
                        borderRadius:"50%",
                        background: p.color,
                        transform:"translate(-50%, -50%)",
                        animation: `${p.anim} ${p.duration}s ease-in-out infinite`,
                        animationDelay: p.delay,
                        opacity: 0.5,
                        pointerEvents:"none",
                        willChange:"transform, opacity",
                      }}/>
                    ))}
                    {/* 成長する植物SVG
                        - 種(60,95)→茎(60,95→60,50)→葉(60,50で左右に開く) が一本の植物として連結。
                        - 位置決め(translate -50%)は親divに分離し、keyframesは scale だけを担当（transform競合回避）。 */}
                    <div style={{
                      position:"absolute", left:"50%", top:"50%",
                      transform:"translate(-50%, -50%)",
                      width:120, height:120,
                    }}>
                      <svg
                        viewBox="0 0 120 120"
                        width="120" height="120"
                        style={{
                          display:"block",
                          animation: `plantBreathe ${PLANT_DURATION} ease-in-out infinite`,
                          willChange:"transform, opacity",
                        }}
                        aria-hidden="true"
                      >
                        {/* 地面：薄ベージュの楕円。植物が立っているコンテキストを示す。 */}
                        <ellipse cx="60" cy="100" rx="26" ry="3" fill="#F0E4D0" opacity="0.6"/>

                        {/* 種：地面の中央上。最初の2秒、左右に微かに揺れる。 */}
                        <circle
                          cx="60" cy="95" r="5"
                          fill="#D9A56B"
                          style={{
                            transformOrigin:"60px 95px",
                            transformBox:"fill-box",
                            animation:`seedSway ${PLANT_DURATION} ease-in-out infinite`,
                            willChange:"transform, opacity",
                          }}
                        />

                        {/* 茎：種から葉の付け根(60,50)まで、わずかにS字に伸びる。
                            stroke-dashoffsetで「下から上に線が描かれる」アニメーションを実現。 */}
                        <path
                          d="M 60 95 Q 57 78 61 65 Q 59 56 60 50"
                          stroke="#8FB996" strokeWidth="2" fill="none" strokeLinecap="round"
                          style={{
                            strokeDasharray:60, strokeDashoffset:60,
                            animation:`stemDraw ${PLANT_DURATION} ease-in-out infinite`,
                            willChange:"stroke-dashoffset, opacity",
                          }}
                        />

                        {/* 左の双葉：茎の先端(60,50)から左斜め上にふっくらと開く。
                            transform-origin を茎の接続点に置くことで、開く動きが自然に見える。 */}
                        <path
                          d="M 60 50 Q 42 42 32 50 Q 44 56 60 53 Z"
                          fill="#A4D2B0" stroke="#7DAE8A" strokeWidth="1" strokeLinejoin="round"
                          style={{
                            transformOrigin:"60px 50px",
                            transformBox:"fill-box",
                            animation:`leafRevealLeft ${PLANT_DURATION} ease-in-out infinite`,
                            willChange:"transform, opacity",
                          }}
                        />
                        {/* 葉脈（左）：開きと同期して薄く現れる。 */}
                        <path
                          d="M 60 50 Q 50 48 38 50"
                          stroke="#7DAE8A" strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.6"
                          style={{
                            transformOrigin:"60px 50px",
                            transformBox:"fill-box",
                            animation:`leafRevealLeft ${PLANT_DURATION} ease-in-out infinite`,
                          }}
                        />

                        {/* 右の双葉 */}
                        <path
                          d="M 60 50 Q 78 42 88 50 Q 76 56 60 53 Z"
                          fill="#C5E0CD" stroke="#7DAE8A" strokeWidth="1" strokeLinejoin="round"
                          style={{
                            transformOrigin:"60px 50px",
                            transformBox:"fill-box",
                            animation:`leafRevealRight ${PLANT_DURATION} ease-in-out infinite`,
                            willChange:"transform, opacity",
                          }}
                        />
                        {/* 葉脈（右） */}
                        <path
                          d="M 60 50 Q 70 48 82 50"
                          stroke="#7DAE8A" strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.6"
                          style={{
                            transformOrigin:"60px 50px",
                            transformBox:"fill-box",
                            animation:`leafRevealRight ${PLANT_DURATION} ease-in-out infinite`,
                          }}
                        />
                      </svg>
                    </div>
                  </div>
                  {/* 段階メッセージ：key を変えてフェード再演出 */}
                  <div
                    key={stockLoadStage}
                    style={{ fontSize:FS.base, color:stockTheme.primary, fontWeight:FW.semibold, marginBottom:SP.sm, animation: "msgFade 4s ease-in-out" }}
                  >
                    {stageMessages[stockLoadStage] || stageMessages[0]}
                  </div>
                  <p style={{ fontSize:FS.sm, color:tokens.text.tertiary, lineHeight:LH.normal }}>育ってきた言葉を、そっと並べています</p>
                </div>
              );
            })()}

            {/* 集計エラー */}
            {!stockLoading && stockError && (
              <div style={{ padding:`${SP.xl}px ${SP["3xl"]}px`, background:tokens.semantic.warningLight, border:`1px solid ${tokens.semantic.warningBorder}`, borderRadius:R.xl, marginBottom:SP.lg, lineHeight:LH.normal }}>
                <div style={{ fontSize:FS.sm, color:"#7A5A1F", marginBottom:SP.lg }}>
                  {stockError}
                </div>
                <button
                  onClick={runStockAggregation}
                  style={{ background:tokens.accent.primary, color:tokens.text.inverse, border:"none", borderRadius:R.lg, padding:`${SP.base}px ${SP["4xl"]}px`, fontSize:FS.md, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                >
                  もう一度試してみる
                </button>
              </div>
            )}

            {/* 集計結果 — ターゲットモックアップ準拠 */}
            {!stockLoading && stockAgg && stockAgg.approvedCount > 0 && !stockAgg.insufficient && (() => {
              const sectionCard = {
                background: tokens.neutral.surface,
                borderRadius: R["2xl"],
                padding: `${SP["3xl"]}px ${SP["3xl"]}px ${SP["2xl"]}px`,
                marginBottom: SP.xl,
                boxShadow: tokens.shadow.sm,
              };
              // セクションヘッダー: アイコン色は accent をそのまま使う（呼び出し側で指定）
              // 線画 strokeWidth=1.5、サイズ 18px に統一して優しい印象に
              const sectionHeader = (label, accent, Icon, onSeeAll) => (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:SP.md, marginBottom:SP.lg }}>
                  <div style={{ display:"flex", alignItems:"center", gap:SP.base }}>
                    <Icon size={18} strokeWidth={1.5} color={accent}/>
                    <span style={{ fontSize:FS.md, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug }}>{label}</span>
                  </div>
                  {onSeeAll && (
                    <button onClick={onSeeAll} style={{ background:"transparent", border:"none", color:accent, fontSize:FS.sm, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F, display:"inline-flex", alignItems:"center", gap:SP.xxs, padding:0 }}>
                      もっと見る <ChevronRight size={14}/>
                    </button>
                  )}
                </div>
              );
              return (
              <>
                {/* ① ヒーロー: 今のあなたを一言で（唯一の暖色面） */}
                {stockAgg.currentSelf && stockAgg.currentSelf.headline && (
                  <section style={{
                    background: tokens.hero.gradient,
                    borderRadius: R["3xl"],
                    padding: `${SP["3xl"]}px ${SP["3xl"]}px ${SP["2xl"]}px`,
                    marginBottom: SP.xl,
                    boxShadow: tokens.shadow.sm,
                    border: `1px solid ${tokens.hero.border}55`,
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    {/* 植物イラスト：右下角にコンパクトに、テキスト領域を圧迫しない */}
                    <svg width="84" height="84" viewBox="0 0 118 118" style={{ position:"absolute", right:-8, bottom:-10, opacity:0.85 }} aria-hidden="true">
                      <defs>
                        <linearGradient id="leafGradLight" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#D7EEDD"/>
                          <stop offset="100%" stopColor="#9CCBA9"/>
                        </linearGradient>
                        <linearGradient id="leafGradMid" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#BFE2C9"/>
                          <stop offset="100%" stopColor="#7DB995"/>
                        </linearGradient>
                        <linearGradient id="leafGradDeep" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#A4D2B0"/>
                          <stop offset="100%" stopColor="#6BA383"/>
                        </linearGradient>
                        <radialGradient id="soilGrad" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="#E8C9A4" stopOpacity="0.6"/>
                          <stop offset="100%" stopColor="#E8C9A4" stopOpacity="0"/>
                        </radialGradient>
                      </defs>

                      {/* 土：放射グラデで自然に消える */}
                      <ellipse cx="59" cy="100" rx="34" ry="6" fill="url(#soilGrad)"/>

                      {/* 茎：細く伸びる優しい曲線 */}
                      <path d="M59 100 C 56 84, 60 70, 59 50" stroke="#7DAE8A" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.85"/>

                      {/* 左下の葉（一番奥・薄め） */}
                      <path d="M59 86 C 42 84, 28 78, 22 64 C 36 70, 50 76, 59 86 Z" fill="url(#leafGradLight)" stroke="#7DAE8A" strokeWidth="0.9" strokeLinejoin="round" opacity="0.85"/>
                      {/* 脈 */}
                      <path d="M59 86 C 46 80, 34 74, 24 66" stroke="#6FA383" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.55"/>

                      {/* 右下の葉（中間） */}
                      <path d="M59 78 C 76 76, 90 68, 96 54 C 80 60, 68 68, 59 78 Z" fill="url(#leafGradMid)" stroke="#6FA383" strokeWidth="0.9" strokeLinejoin="round"/>
                      <path d="M59 78 C 72 72, 84 64, 94 56" stroke="#5E9072" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.6"/>

                      {/* 左上の葉（深め色） */}
                      <path d="M59 64 C 44 60, 34 50, 32 36 C 44 44, 54 54, 59 64 Z" fill="url(#leafGradMid)" stroke="#6FA383" strokeWidth="0.9" strokeLinejoin="round"/>
                      <path d="M59 64 C 48 56, 40 48, 33 38" stroke="#5E9072" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.6"/>

                      {/* 右上の葉（一番手前・濃い緑、ハイライト） */}
                      <path d="M59 56 C 74 50, 84 42, 88 26 C 76 36, 66 46, 59 56 Z" fill="url(#leafGradDeep)" stroke="#5E9072" strokeWidth="1" strokeLinejoin="round"/>
                      <path d="M59 56 C 70 48, 78 40, 86 28" stroke="#4D7E62" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.7"/>

                      {/* 中央先端の小さな葉 */}
                      <path d="M59 50 C 64 46, 66 42, 65 36 C 60 40, 58 44, 59 50 Z" fill="url(#leafGradDeep)" stroke="#4D7E62" strokeWidth="0.8" strokeLinejoin="round"/>

                      {/* 浮遊する小さな粒（余韻） */}
                      <circle cx="98" cy="20" r="1.4" fill="#E8893A88"/>
                      <circle cx="20" cy="40" r="1" fill="#E8893A66"/>
                      <circle cx="100" cy="48" r="0.8" fill="#E8893A55"/>
                    </svg>

                    {/* テキストエリア：横幅いっぱい、右下の植物だけ避ける。
                        日本語の長文がはみ出さないよう、wordBreak/overflowWrap で確実に折り返す。 */}
                    <div style={{ position:"relative", zIndex:1, width:"100%", maxWidth:"100%", minWidth:0 }}>
                      <div style={{ fontSize:FS.sm, color:tokens.accent.orange, fontWeight:FW.semibold, letterSpacing:tokens.letterSpacing.wide, marginBottom:SP.base }}>今のあなたを一言で</div>
                      <div style={{
                        fontSize:FS.display-3, color:tokens.text.primary, fontWeight:FW.bold,
                        letterSpacing:tokens.letterSpacing.tight, lineHeight:1.45,
                        marginBottom:SP.xl,
                        // 右下の植物SVG(84px幅 + 余白)とぶつからないように右余白を確保
                        paddingRight:64,
                        wordBreak:"break-word",
                        overflowWrap:"break-word",
                        maxWidth:"100%",
                      }}>
                        {stockAgg.currentSelf.headline}
                      </div>
                      {stockAgg.currentSelf.description && (
                        <p style={{
                          fontSize:FS.md, color:tokens.text.secondary, lineHeight:LH.relaxed,
                          margin:`0 0 ${SP["2xl"]}px`,
                          paddingRight:64,
                          wordBreak:"break-word", overflowWrap:"break-word",
                          maxWidth:"100%",
                        }}>
                          {stockAgg.currentSelf.description}
                        </p>
                      )}
                    </div>
                    {/* 最近よく出てきた言葉 chip 列（暖色アクセント） */}
                    {Array.isArray(stockAgg.currentSelf.recentWords) && stockAgg.currentSelf.recentWords.length > 0 && (
                      <div style={{ position:"relative", zIndex:1, marginTop:SP.lg, padding:`${SP.md}px ${SP.xl}px`, background:"rgba(255,255,255,0.7)", borderRadius:R.xl, display:"flex", alignItems:"center", gap:SP.md, flexWrap:"wrap" }}>
                        <span style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.medium, flexShrink:0 }}>最近よく出てきた言葉:</span>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:SP.sm }}>
                          {stockAgg.currentSelf.recentWords.map((w, i) => (
                            <span key={i} style={{ padding:`${SP.xxs+1}px ${SP.md}px`, background:tokens.accent.orangeLight, color:tokens.accent.orange, border:`1px solid ${tokens.accent.orange}33`, borderRadius:R.pill, fontSize:FS.sm, fontWeight:FW.semibold, lineHeight:1.45 }}>{w}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {/* ② 最近、新しく見えてきたこと（横スクロールカード） */}
                {Array.isArray(stockAgg.newlyEmerged) && stockAgg.newlyEmerged.length > 0 && (() => {
                  // セクションは通常通り padding を持ち、横スクロール領域だけ負マージン+正パディングでブリードさせる
                  const sectionPadX = SP["3xl"]; // 左右余白の統一値
                  return (
                  <section style={{
                    background: tokens.neutral.surface,
                    borderRadius: R["2xl"],
                    padding: `${SP["3xl"]}px ${sectionPadX}px ${SP["2xl"]}px`,
                    marginBottom: SP.xl,
                    boxShadow: tokens.shadow.sm,
                    // カルーセル内の負マージンと scroll コンテナが、セクション境界の外に
                    // はみ出してページ全体の横スクロールを誘発するのを防ぐ。
                    overflow: "hidden",
                    maxWidth: "100%",
                  }}>
                    <div style={{ marginBottom:SP.lg }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:SP.md }}>
                        <div style={{ display:"flex", alignItems:"center", gap:SP.base }}>
                          {/* セクションアイコン：淡いオレンジ */}
                          <Sparkles size={18} strokeWidth={1.5} color="#D9A56B"/>
                          <span style={{ fontSize:FS.md, fontWeight:FW.semibold, color:tokens.text.primary, lineHeight:LH.snug }}>最近、新しく見えてきたこと</span>
                        </div>
                        {stockAgg.newlyEmerged.length > 3 && (
                          <button onClick={() => setStockModalKey("newlyEmerged")} style={{ background:"transparent", border:"none", color:stockTheme.primary, fontSize:FS.sm, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F, display:"inline-flex", alignItems:"center", gap:SP.xxs, padding:0 }}>
                            もっと見る <ChevronRight size={14}/>
                          </button>
                        )}
                      </div>
                    </div>
                    {/* 横スクロール領域：負マージンで section padding を打ち消し、padding で先頭/末尾カードに均等な余白を確保 */}
                    <div style={{
                      display:"flex", gap:SP.lg, overflowX:"auto",
                      scrollSnapType:"x mandatory",
                      scrollPaddingLeft: sectionPadX,
                      scrollPaddingRight: sectionPadX,
                      marginLeft: `-${sectionPadX}px`,
                      marginRight: `-${sectionPadX}px`,
                      paddingLeft: sectionPadX,
                      paddingRight: sectionPadX,
                      paddingBottom: SP.sm,
                      scrollbarWidth: "thin",
                    }}>
                      {(stockExpand.newlyEmerged ? stockAgg.newlyEmerged : stockAgg.newlyEmerged.slice(0, 4)).map((c, i) => {
                        const Icon = iconMap[c.iconHint] || Sparkles;
                        const iconColor = iconColorMap[c.iconHint] || stockTheme.primary;
                        return (
                          <div key={i} style={{
                            // 幅: viewport 基準で「ほぼ1画面分 - 余白」。maxWidth でデスクトップも制御
                            flex:"0 0 auto",
                            width:"calc(100vw - 80px)",
                            maxWidth:270, minWidth:180,
                            scrollSnapAlign:"start",
                            background:tokens.neutral.bg, borderRadius:R.xl,
                            padding:`${SP.xl}px ${SP.xl}px ${SP.lg}px`,
                            display:"flex", flexDirection:"column", gap:SP.md,
                            border:`1px solid ${tokens.neutral.border}66`,
                            boxSizing:"border-box",
                            overflow:"hidden",
                          }}>
                            {/* アイコン */}
                            <div style={{ display:"flex", alignItems:"center", gap:SP.sm }}>
                              <span style={{ width:30, height:30, borderRadius:R.md, background:iconColor+"18", color:iconColor, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                                <Icon size={16} strokeWidth={2.2}/>
                              </span>
                            </div>
                            {/* タイトル（太字、最大2行） */}
                            <div style={{
                              fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.semibold,
                              lineHeight:LH.snug,
                              display:"-webkit-box", WebkitLineClamp:2,
                              WebkitBoxOrient:"vertical", overflow:"hidden",
                            }}>
                              {c.title}
                            </div>
                            {/* 説明文（2行クランプ） */}
                            {c.description && (
                              <div style={{
                                fontSize:FS.sm, color:tokens.text.secondary, lineHeight:LH.normal,
                                display:"-webkit-box", WebkitLineClamp:2,
                                WebkitBoxOrient:"vertical", overflow:"hidden",
                              }}>
                                {c.description}
                              </div>
                            )}
                            {/* 続きを見る（右揃え、カード詳細モーダルを開く） */}
                            <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"auto", paddingTop:SP.xs }}>
                              <button
                                onClick={() => { setNeDetailCard(c); setNeDetailExpandedBodies({}); }}
                                style={{
                                  background:"transparent", border:"none", padding:0,
                                  fontSize:FS.xs, color:stockTheme.primary,
                                  cursor:"pointer", fontFamily:F,
                                  display:"inline-flex", alignItems:"center", gap:2,
                                  lineHeight:1.4,
                                }}
                              >
                                続きを見る ›
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* ページネーション dots（カード数のみ） */}
                    {stockAgg.newlyEmerged.length > 1 && (
                      <div style={{ display:"flex", justifyContent:"center", gap:SP.sm, marginTop:SP.md, paddingLeft:sectionPadX, paddingRight:sectionPadX }}>
                        {stockAgg.newlyEmerged.slice(0, stockExpand.newlyEmerged ? stockAgg.newlyEmerged.length : 4).map((_, i) => (
                          <span key={i} style={{ width:6, height:6, borderRadius:"50%", background: i === 0 ? stockTheme.primary : tokens.neutral.border }}/>
                        ))}
                      </div>
                    )}
                  </section>
                  );
                })()}

                {/* ③ あなたの中で、繰り返し現れている言葉（ワードクラウド） */}
                <section style={sectionCard}>
                  {sectionHeader("あなたの中で、繰り返し現れている言葉", "#D88C8C", Heart, stockAgg.valueCloud && stockAgg.valueCloud.length > 8 ? () => setStockModalKey("valueCloud") : null)}
                  {(!stockAgg.valueCloud || stockAgg.valueCloud.length === 0) ? (
                    <p style={{ fontSize:FS.sm, color:tokens.text.tertiary, lineHeight:LH.normal, margin:0 }}>もう少しノートを書くと、繰り返し現れる言葉が見えてきます。</p>
                  ) : (() => {
                    // count降順済みの並び。3段階階層を「行ごと」に分けて表示。
                    const sorted = stockExpand.valueCloud ? stockAgg.valueCloud : stockAgg.valueCloud.slice(0, 12);
                    // 行1: 上位 2件（32px）
                    // 行2: 3〜5位（22px）
                    // 行3: 6位以降（16px）
                    const tier1 = sorted.slice(0, 2);
                    const tier2 = sorted.slice(2, 5);
                    const tier3 = sorted.slice(5);
                    const tiers = [
                      { items: tier1, size: 32, weight: FW.bold,     mb: SP["2xl"] },
                      { items: tier2, size: 22, weight: FW.semibold, mb: SP.xl },
                      { items: tier3, size: 16, weight: FW.medium,   mb: 0 },
                    ].filter(t => t.items.length > 0);
                    // tier 内の並びを軽くシャッフルして単調を避ける（決定的）
                    const shuffleTier = (arr, seed) => {
                      const a = [...arr];
                      for (let i = a.length - 1; i > 0; i--) {
                        const j = ((i * 7) + seed * 3) % (i + 1);
                        [a[i], a[j]] = [a[j], a[i]];
                      }
                      return a;
                    };
                    return (
                      <>
                        <div style={{ padding:`${SP["2xl"]}px ${SP.md}px ${SP.lg}px` }}>
                          {tiers.map((t, ti) => (
                            <div key={ti} style={{
                              display:"flex", flexWrap:"wrap",
                              justifyContent:"center", alignItems:"baseline",
                              gap:`${SP.base}px 24px`,
                              marginBottom: t.mb,
                            }}>
                              {shuffleTier(t.items, ti).map((v) => {
                                // sorted内のグローバルなrankを使って色を決める（連続性）
                                const globalRank = sorted.findIndex(x => x.keyword === v.keyword);
                                const color = cloudColors[globalRank % cloudColors.length];
                                return (
                                  <span key={v.keyword} style={{
                                    fontSize: t.size, color, fontWeight: t.weight,
                                    lineHeight: 1.25, letterSpacing: "-0.01em",
                                    cursor: "default", display:"inline-block",
                                  }} title={`${v.count}回`}>
                                    {v.keyword}
                                  </span>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        <p style={{ fontSize:FS.xs, color:tokens.text.tertiary, lineHeight:LH.normal, margin:`${SP.lg}px 0 0`, textAlign:"center" }}>
                          ※ ノートから繰り返し出てきた言葉をもとにしています
                        </p>
                      </>
                    );
                  })()}
                </section>

                {/* ④ 自然とやっていること（自然文 + 回数） */}
                <section style={sectionCard}>
                  {sectionHeader("自然とやっていること", "#6D8FD6", Star, stockAgg.strengths && stockAgg.strengths.length > 3 ? () => setStockModalKey("strengths") : null)}
                  {(!stockAgg.strengths || stockAgg.strengths.length === 0) ? (
                    <p style={{ fontSize:FS.sm, color:tokens.text.tertiary, lineHeight:LH.normal, margin:0 }}>もう少しノートを書くと、自然とやっていることが見えてきます。</p>
                  ) : (
                    <div>
                      {(stockExpand.strengths ? stockAgg.strengths : stockAgg.strengths.slice(0, 3)).map((s, i, arr) => {
                        // 強みのアイコンはラインアップ（パターン）から循環
                        const strengthIcons = [Users, BookOpen, Sparkles, Heart, Star, Compass];
                        const Icon = strengthIcons[i % strengthIcons.length];
                        return (
                          <div key={i} style={{
                            display:"flex", alignItems:"center", gap:SP.lg,
                            padding:`${SP.lg}px 0`,
                            borderBottom: i < arr.length - 1 ? `1px solid ${tokens.neutral.border}55` : "none",
                          }}>
                            <span style={{ width:34, height:34, borderRadius:"50%", background:strengthTheme.light, color:strengthTheme.primary, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                              <Icon size={15} strokeWidth={2.2}/>
                            </span>
                            <span style={{ flex:1, fontSize:FS.md, color:tokens.text.primary, fontWeight:FW.medium, lineHeight:LH.snug, minWidth:0 }}>
                              {s.description}
                            </span>
                            <span style={{ fontSize:FS.base, color:"#7A5A3A", fontWeight:FW.semibold, background:"#F7F2E5", borderRadius:R.pill, padding:"4px 10px", flexShrink:0, whiteSpace:"nowrap" }}>
                              {s.count}回
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* ⑤ 言葉の変化 — data-gate: ペアあり＋3週間以上の期間がある場合のみ表示 */}
                {Array.isArray(stockAgg.wordChange) && stockAgg.wordChange.length > 0 && stockAgg.wordChangeBeforeLabel && (
                <section style={{ ...sectionCard, padding:`${SP["2xl"]}px ${SP["2xl"]}px ${SP.xl}px`, opacity:0.95 }}>
                  {sectionHeader("言葉の変化", tokens.semantic.warning, TrendingUp, null)}
                  <div style={{ display:"flex", flexDirection:"column", gap:SP.lg }}>
                    {stockAgg.wordChange.map((w, i, arr) => (
                      <div key={i} style={{ display:"flex", flexDirection:"column", gap:SP.xs, paddingBottom: i < arr.length - 1 ? SP.lg : 0, borderBottom: i < arr.length - 1 ? `1px solid ${tokens.neutral.border}55` : "none" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                          <span style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold, flexShrink:0, paddingTop:1, minWidth:48 }}>{stockAgg.wordChangeBeforeLabel}</span>
                          <span style={{ fontSize:FS.sm, color:tokens.text.secondary, lineHeight:LH.normal }}>{w.before}</span>
                        </div>
                        <div style={{ paddingLeft:48, color:tokens.semantic.warning, fontSize:FS.xxs }}>↓</div>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:SP.base }}>
                          <span style={{ fontSize:FS.xs, color:tokens.semantic.warning, fontWeight:FW.semibold, flexShrink:0, paddingTop:1, minWidth:48 }}>今</span>
                          <span style={{ fontSize:FS.sm, color:tokens.text.primary, lineHeight:LH.normal, fontWeight:FW.medium }}>{w.after}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
                )}

                {/* 集計メタ + 再集計 */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:`${SP.sm}px ${SP.xs}px` }}>
                  <span style={{ fontSize:FS.xs, color:tokens.text.tertiary }}>承認済み {stockAgg.approvedCount} 件 · {stockAgg.noteCount} ノート</span>
                  <button
                    onClick={runStockAggregation}
                    disabled={stockLoading}
                    style={{ background:"transparent", border:"none", color: stockLoading ? tokens.text.tertiary : stockTheme.primary, fontSize:FS.xs, fontWeight:FW.medium, cursor: stockLoading ? "not-allowed" : "pointer", fontFamily:F, textDecoration:"underline", opacity: stockLoading ? 0.6 : 1 }}
                  >
                    {stockLoading ? "集計中…" : "集計し直す"}
                  </button>
                </div>
              </>
              );
            })()}
          </div>
          );
        })()}


        {/* ══ 自分ログ (時系列ノート一覧) ══ */}
        {tab==="log"&&(() => {
          // 0件：セレクタも月別チップも出さず、種のメッセージのみ
          if (logNotes.length === 0) {
            return (
              <div style={{ animation:tokens.motion.fadeUp }}>
                <div style={{ background:tokens.neutral.surface, borderRadius:R["2xl"], padding:`${SP["8xl"]}px ${SP["5xl"]}px`, textAlign:"center", boxShadow:tokens.shadow.sm }}>
                  <div style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, marginBottom:SP.md }}>
                    まだ記録がありません
                  </div>
                  <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed, marginBottom:SP["2xl"] }}>
                    書いていくと、ここに残っていきます。
                  </p>
                  <button
                    onClick={()=>switchTab("note")}
                    style={{ padding:`${SP.md}px ${SP["5xl"]}px`, background:tokens.accent.primary, color:tokens.text.inverse, border:"none", borderRadius:R.md, fontSize:FS.base, fontWeight:FW.semibold, cursor:"pointer", fontFamily:F }}
                  >
                    自分ノートを書く →
                  </button>
                </div>
              </div>
            );
          }

          // 全ノートから年月リスト（新しい順・重複排除）
          const ymList = [...new Set(
            [...logNotes]
              .sort((a, b) => new Date(b.closedAt || b.createdAt || 0) - new Date(a.closedAt || a.createdAt || 0))
              .map(n => {
                const d = new Date(n.closedAt || n.createdAt || Date.now());
                return `${d.getFullYear()}-${d.getMonth() + 1}`;
              })
          )];
          // 有効な選択月（存在しない月が選ばれていたら最新月へ）
          const effectiveYM = (logSelectedYM && ymList.includes(logSelectedYM)) ? logSelectedYM : (ymList[0] || null);
          const ymIndex = effectiveYM ? ymList.indexOf(effectiveYM) : 0;
          const ymLabel = effectiveYM ? (() => {
            const [y, m] = effectiveYM.split("-");
            return `${y}年${m}月`;
          })() : "";

          // 選択月のノート（emotionフィルタ前）
          const notesInMonth = logNotes.filter(n => {
            const d = new Date(n.closedAt || n.createdAt || Date.now());
            return `${d.getFullYear()}-${d.getMonth() + 1}` === effectiveYM;
          });

          // 月内 + emotionフィルタ後、新しい順
          const filteredOrdered = [...notesInMonth]
            .filter(n => logFilter == null || n.emotion === logFilter)
            .sort((a, b) => new Date(b.closedAt || b.createdAt || 0) - new Date(a.closedAt || a.createdAt || 0));

          return (
          <div style={{ animation:tokens.motion.fadeUp }}>
            <div style={{ marginBottom:SP.xl }}>
              <div style={{ fontSize:FS.sm, color:tokens.accent.primary, fontWeight:FW.semibold }}>あなたが残してきた言葉</div>
            </div>

            {/* 月セレクタ */}
            <div style={{ display:"flex", alignItems:"center", gap:SP.md, marginBottom:SP.xl }}>
              <button
                onClick={() => setLogSelectedYM(ymList[ymIndex + 1] || ymList[ymList.length - 1])}
                disabled={ymIndex >= ymList.length - 1}
                aria-label="前の月"
                style={{
                  width:36, height:36, borderRadius:"50%",
                  background: ymIndex >= ymList.length - 1 ? "transparent" : tokens.neutral.surface,
                  border: `1px solid ${ymIndex >= ymList.length - 1 ? "transparent" : tokens.neutral.border}`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color: ymIndex >= ymList.length - 1 ? tokens.text.tertiary : tokens.text.secondary,
                  cursor: ymIndex >= ymList.length - 1 ? "default" : "pointer",
                  flexShrink:0,
                }}
              >
                <ChevronLeft size={18} strokeWidth={1.5}/>
              </button>

              <div style={{ textAlign:"center", flex:1 }}>
                <div style={{ fontSize:FS.base, fontWeight:FW.semibold, color:tokens.text.primary }}>
                  {ymLabel}
                </div>
                <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, marginTop:2 }}>
                  {notesInMonth.length}件の記録
                </div>
              </div>

              <button
                onClick={() => setLogSelectedYM(ymList[ymIndex - 1] || ymList[0])}
                disabled={ymIndex <= 0}
                aria-label="次の月"
                style={{
                  width:36, height:36, borderRadius:"50%",
                  background: ymIndex <= 0 ? "transparent" : tokens.neutral.surface,
                  border: `1px solid ${ymIndex <= 0 ? "transparent" : tokens.neutral.border}`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color: ymIndex <= 0 ? tokens.text.tertiary : tokens.text.secondary,
                  cursor: ymIndex <= 0 ? "default" : "pointer",
                  flexShrink:0,
                }}
              >
                <ChevronRight size={18} strokeWidth={1.5}/>
              </button>
            </div>

            {/* emotionフィルタータブ（画面幅を超えたら折り返し） */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:SP.sm, marginBottom:SP.xl }}>
              {LOG_FILTERS.map(f => {
                const selected = logFilter === f.value;
                const Icon = f.Icon;
                const themeColor = f.value == null ? tokens.accent.primary : f.color;
                return (
                  <button
                    key={String(f.value)}
                    onClick={()=>setLogFilter(f.value)}
                    style={{
                      display:"inline-flex", alignItems:"center", gap:SP.sm,
                      flexShrink:0,
                      padding:`${SP.sm}px ${SP.lg}px`,
                      background: selected ? themeColor + "24" : "#FFFFFF",
                      color: themeColor,
                      border: `1.5px solid ${selected ? themeColor : tokens.neutral.border}`,
                      borderRadius: R.pill,
                      fontSize: FS.sm,
                      fontWeight: selected ? FW.semibold : FW.medium,
                      cursor:"pointer", fontFamily:F,
                      transition: `all ${tokens.motion.durationFast} ${tokens.motion.ease}`,
                      whiteSpace:"nowrap",
                    }}
                  >
                    {Icon && <Icon size={15} strokeWidth={1.5} color={themeColor}/>}
                    <span>{f.label}</span>
                  </button>
                );
              })}
            </div>

            {filteredOrdered.length === 0 ? (
              /* emotionフィルタで絞り込んだ結果0件（logNotes > 0 が保証されているのでここは常にフィルタ起因） */
              <div style={{ background:tokens.neutral.surface, borderRadius:R["2xl"], padding:`${SP["8xl"]}px ${SP["5xl"]}px`, textAlign:"center", boxShadow:tokens.shadow.sm }}>
                <div style={{ fontSize:FS.xl, fontWeight:FW.semibold, color:tokens.text.primary, marginBottom:SP.md }}>
                  この月のノートはありません
                </div>
                <p style={{ fontSize:FS.base, color:tokens.text.secondary, lineHeight:LH.relaxed }}>
                  別のフィルターを選んでみてください。
                </p>
              </div>
            ) : (
              <div>
                {/* 時系列の縦線つきリスト */}
                <div style={{ position:"relative", paddingLeft:24 }}>
                  <div style={{
                    position:"absolute", left:7, top:8, bottom:8,
                    width:1, background:"#E2E8F4",
                  }}/>
                  <div style={{ display:"flex", flexDirection:"column", gap:SP.md }}>
                    {filteredOrdered.map((note) => {
                      const emo = EMOTION_MAP[note.emotion] || null;
                      const dotColor = "#CBD5E5";
                      const iconStroke = emo?.color || tokens.accent.primary;
                      const iconBg = emo?.colorLighter || tokens.accent.light;
                      const IconCmp = emo?.Icon || Sparkles;
                      const subTag = (note.cycles || []).find(c => c.level === "a")?.nearWords?.selected || null;
                      return (
                        <div key={note.id} style={{ position:"relative" }}>
                          <span style={{
                            position:"absolute", left:-21, top:24,
                            width:10, height:10, borderRadius:"50%",
                            background: dotColor,
                            border:`2px solid ${tokens.neutral.bg}`,
                            boxShadow: `0 0 0 1px ${tokens.neutral.border}`,
                          }}/>
                          <button
                            onClick={()=>setPopupNoteId(note.id)}
                            style={{
                              width:"100%", textAlign:"left",
                              display:"flex", alignItems:"stretch", gap:SP.lg,
                              padding:`${SP.lg}px ${SP.xl}px`,
                              background:tokens.neutral.surface,
                              border:`1px solid ${tokens.neutral.border}`,
                              borderRadius:R.xl,
                              boxShadow:tokens.shadow.sm,
                              cursor:"pointer",
                              fontFamily:F,
                              transition:`box-shadow ${tokens.motion.durationFast} ${tokens.motion.ease}`,
                            }}
                          >
                            {/* 左：感情アイコン */}
                            <div style={{
                              flexShrink:0,
                              width:30, height:30, borderRadius:"50%",
                              background: iconBg, border:"none",
                              display:"flex", alignItems:"center", justifyContent:"center",
                              alignSelf:"flex-start", marginTop:4,
                            }}>
                              <IconCmp size={16} strokeWidth={1.8} color={iconStroke}/>
                            </div>

                            {/* 中央：日付 + 受け止め名（主役）+ 本文 + emotionタグ */}
                            <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:SP.xs }}>
                              <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, fontWeight:FW.semibold }}>
                                {formatNoteDate(note.closedAt || note.createdAt)}
                              </div>
                              {subTag && (
                                <div style={{ fontSize:FS.sm, color:tokens.text.primary, fontWeight:FW.semibold, lineHeight:1.4 }}>
                                  {subTag}
                                </div>
                              )}
                              <p style={{
                                fontSize:FS.sm,
                                color: subTag ? tokens.text.secondary : tokens.text.primary,
                                lineHeight:LH.normal, margin:0, whiteSpace:"pre-line",
                                display:"-webkit-box", WebkitBoxOrient:"vertical",
                                WebkitLineClamp:2, overflow:"hidden",
                              }}>
                                {note.body}
                              </p>
                              {emo && (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:SP.xs, marginTop:SP.xxs }}>
                                  <span style={{
                                    display:"inline-block",
                                    padding:"2px 8px",
                                    background: tokens.neutral.surface,
                                    color: emo.color,
                                    border: `1px solid ${emo.color}`,
                                    borderRadius: R.sm,
                                    fontSize: 11, fontWeight: FW.medium,
                                    lineHeight: 1.4,
                                  }}>
                                    {emo.label}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* 右：矢印 */}
                            <div style={{
                              flexShrink:0,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              color: tokens.text.tertiary,
                            }}>
                              <ChevronRight size={20} strokeWidth={1.5}/>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 静的メッセージ */}
                <div style={{
                  marginTop: SP["4xl"],
                  padding:`${SP.xl}px ${SP["2xl"]}px`,
                  borderTop:`1px solid ${tokens.neutral.border}`,
                  textAlign:"center",
                }}>
                  <p style={{
                    fontSize: FS.sm,
                    color: tokens.text.tertiary,
                    lineHeight: LH.relaxed,
                    margin: 0,
                  }}>
                    半年後、1年後のあなたが見返したとき、<br/>
                    「あの時の私は、こんなことを感じていたんだ」<br/>
                    と思い出せる。
                  </p>
                </div>
              </div>
            )}
          </div>
          );
        })()}


        {/* ══ マイページ ══ */}
        {tab==="page"&&(() => {
          // 4セクション構成のマイページ：プロフィール → あなたの記録 → 最近の変化 → PathNoteとの歩み
          const stats = computeMyStats();
          const heroHeadline = stockAgg?.currentSelf?.headline || null;
          const sectionCard = {
            background: tokens.neutral.surface,
            border: `1px solid ${tokens.neutral.border}`,
            borderRadius: R.xl,
            padding: SP.xl,
            boxShadow: tokens.shadow.sm,
          };
          // 「最近の変化」：newlyEmerged を最大3件 + wordChange の最新1件。
          // 動詞は付けず、言葉のフレーズだけを表示。右端に NEW バッジ。
          const recentChanges = (() => {
            const out = [];
            const newly = Array.isArray(stockAgg?.newlyEmerged) ? stockAgg.newlyEmerged : [];
            newly.slice(0, 3).forEach((n, i) => {
              const text = n.title || n.description;
              if (!text) return;
              const Icon = i === 0 ? Heart : i === 1 ? Sparkles : Star;
              const color = i === 0 ? "#D88C8C" : i === 1 ? "#D9A56B" : tokens.accent.primary;
              out.push({ Icon, color, text });
            });
            const wc = Array.isArray(stockAgg?.wordChange) ? stockAgg.wordChange : [];
            if (wc.length > 0 && wc[0]?.after) {
              out.push({
                Icon: TrendingUp,
                color: tokens.accent.primary,
                text: wc[0].after,
              });
            }
            return out.slice(0, 4);
          })();
          // 直近6週棒グラフ用の最大値（高さの正規化基準）。最低でも1にして潰れ防止。
          const maxCount = Math.max(1, ...stats.last6Weeks.map(w => w.count));

          return (
          <div style={{ animation:tokens.motion.fadeUp, display:"flex", flexDirection:"column", gap:SP.lg }}>
            {/* ── セクション1: プロフィール ── */}
            <div style={sectionCard}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:SP.lg }}>
                <button onClick={()=>avatarInputRef.current?.click()} aria-label="アイコンを変更"
                  style={{ width:58, height:58, borderRadius:"50%", background:"#EAF2FF", border:`1px solid ${tokens.neutral.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden", padding:0, cursor:"pointer" }}>
                  {profile?.avatar ? (
                    <img src={profile.avatar} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  ) : (
                    <svg width="50" height="50" viewBox="0 0 50 50" aria-hidden="true">
                      <circle cx="25" cy="25" r="25" fill="#EAF2FF"/>
                      <circle cx="25" cy="19" r="8" fill="#F3C7A7"/>
                      <path d="M12 44c2-10 8-15 13-15s11 5 13 15" fill="#6F95C8"/>
                    </svg>
                  )}
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{ display:"none" }}/>
                <div style={{ minWidth:0, flex:1, display:"flex", flexDirection:"column", gap:SP.xs }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:SP.sm }}>
                    {profileEditing ? (
                      <input value={profileDraftName} onChange={e=>setProfileDraftName(e.target.value)}
                        style={{ flex:1, minWidth:0, width:0, boxSizing:"border-box", padding:`${SP.sm}px ${SP.md}px`, border:`1.5px solid ${tokens.neutral.border}`, borderRadius:R.md, fontSize:FS.lg, fontWeight:FW.semibold, color:tokens.text.primary, fontFamily:F, outline:"none" }}
                      />
                    ) : (
                      <div style={{ fontSize:FS.h3, color:tokens.text.primary, fontWeight:FW.semibold, lineHeight:LH.snug }}>{profileName}</div>
                    )}
                    {profileEditing ? (
                      <button onClick={()=>saveProfile({ ...profile, name:profileDraftName })} style={{ background:"transparent", border:"none", color:tokens.accent.primary, fontSize:FS.sm, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}>保存</button>
                    ) : (
                      <button onClick={()=>{ setProfileDraftName(profile?.name || ""); setProfileEditing(true); }} style={{ background:"transparent", border:"none", color:tokens.accent.primary, fontSize:FS.sm, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}>編集</button>
                    )}
                  </div>
                  {/* 一言メッセージ：自分の言葉タブのヒーロー headline を読み込んで表示。
                      未生成時は誘導付きの優しい空状態。 */}
                  {heroHeadline ? (
                    <div style={{ fontSize:FS.sm, color:tokens.accent.primary, fontWeight:FW.medium, lineHeight:LH.normal, fontStyle:"italic" }}>
                      「{heroHeadline}」
                    </div>
                  ) : (
                    <div style={{ marginTop:SP.xs }}>
                      <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, lineHeight:LH.normal, marginBottom:SP.xs }}>
                        まだ言葉が育っていません。<br/>「自分の言葉」タブを開いて集計すると、ここに芽吹きます。
                      </div>
                      <button
                        onClick={()=>switchTab("stock")}
                        style={{ padding:`${SP.xs}px ${SP.md}px`, background:"transparent", color:tokens.accent.primary, border:`1px solid ${tokens.accent.primary}`, borderRadius:R.sm, fontSize:FS.xs, fontWeight:FW.medium, cursor:"pointer", fontFamily:F }}
                      >
                        自分の言葉タブを開く →
                      </button>
                    </div>
                  )}
                  <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, marginTop:SP.xs }}>
                    記録をはじめた日：{firstRecordDate ? firstRecordDate.toLocaleDateString("ja-JP") : "まだありません"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── セクション2: あなたの記録（KPI 2×2） ── */}
            <div style={sectionCard}>
              <div style={{ fontSize:FS.sm, color:tokens.accent.primary, fontWeight:FW.semibold, marginBottom:SP.lg }}>あなたの記録</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:SP.md }}>
                {[
                  { label:"ノート数",     value: stats.totalNotes,     unit:"件" },
                  { label:"言葉の数",     value: stats.totalWords,     unit:"個" },
                  { label:"向き合った日", value: stats.engagedDays,    unit:"日" },
                  { label:"見つめ直した回数", value: stats.totalDeepDives, unit:"回" },
                ].map((item) => (
                  <div key={item.label} style={{
                    padding:`${SP.lg}px ${SP.md}px`,
                    background: tokens.neutral.bg,
                    border:`1px solid ${tokens.neutral.border}`,
                    borderRadius:R.md, textAlign:"center",
                  }}>
                    <div style={{ fontSize:12, color:tokens.text.secondary, fontWeight:FW.medium, marginBottom:SP.xs }}>{item.label}</div>
                    <div style={{ display:"inline-flex", alignItems:"baseline", gap:2, color:tokens.text.primary }}>
                      <span style={{ fontSize:30, fontWeight:FW.semibold, lineHeight:1.1 }}>{item.value}</span>
                      <span style={{ fontSize:14, fontWeight:FW.normal, color:tokens.text.tertiary }}>{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── セクション3: 最近の変化 ── */}
            <div style={sectionCard}>
              <div style={{ fontSize:FS.sm, color:tokens.accent.primary, fontWeight:FW.semibold, marginBottom:SP.lg }}>最近の変化</div>
              {recentChanges.length > 0 ? (
                <div style={{ display:"flex", flexDirection:"column", gap:SP.md }}>
                  {recentChanges.map((r, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:SP.md, padding:`${SP.sm}px 0` }}>
                      <span style={{ flexShrink:0, width:24, height:24, borderRadius:"50%", background: r.color + "1A", display:"inline-flex", alignItems:"center", justifyContent:"center", marginTop:1 }}>
                        <r.Icon size={14} strokeWidth={1.8} color={r.color}/>
                      </span>
                      <div style={{ fontSize:FS.sm, color:tokens.text.primary, lineHeight:LH.relaxed, flex:1 }}>
                        {r.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, lineHeight:LH.normal, marginBottom:SP.xs }}>
                    もう少しノートを書くと、ここに変化が見えてきます。
                  </div>
                  <div style={{ fontSize:FS.xxs, color:tokens.text.tertiary, lineHeight:LH.normal }}>
                    （「自分の言葉」タブで集計を実行すると更新されます）
                  </div>
                </div>
              )}
            </div>

            {/* ── セクション4: PathNoteとの歩み（直近6週の言葉の数） ── */}
            <div style={sectionCard}>
              <div style={{ fontSize:FS.sm, color:tokens.accent.primary, fontWeight:FW.semibold, marginBottom:SP.lg }}>記録した言葉の数（直近6週）</div>
              {stats.totalNotes === 0 ? (
                <div style={{ fontSize:FS.xs, color:tokens.text.tertiary, lineHeight:LH.normal }}>
                  最初のノートを書いてみましょう。<br/>
                  ここに記録の歩みが見えてきます。
                </div>
              ) : (
                <div>
                  {/* 棒グラフ：各週の承認 summary 数を最大値で正規化。0 の週は棒なし、ラベルだけ表示。 */}
                  <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:SP.sm, height:120, padding:`0 ${SP.xs}px`, marginBottom:SP.sm }}>
                    {stats.last6Weeks.map((w) => {
                      const heightPct = w.count === 0 ? 0 : Math.max(8, (w.count / maxCount) * 100);
                      return (
                        <div key={w.key} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", height:"100%", gap:4 }}>
                          {w.count > 0 && (
                            <span style={{ fontSize:11, color:tokens.text.secondary, fontWeight:FW.semibold, lineHeight:1 }}>{w.count}</span>
                          )}
                          {w.count > 0 ? (
                            <div
                              title={`${w.label}：${w.count}個`}
                              style={{
                                width:"70%",
                                height: heightPct + "%",
                                background: tokens.accent.primary,
                                borderRadius:"3px 3px 0 0",
                                minHeight: 6,
                                transition:`height ${tokens.motion.durationFast} ${tokens.motion.ease}`,
                              }}
                            />
                          ) : (
                            // 値0の週：棒は描かないが、ラベルだけ下に出るように高さ0のスペーサーを置く
                            <div style={{ width:"70%", height:0 }}/>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:SP.sm, padding:`0 ${SP.xs}px` }}>
                    {stats.last6Weeks.map((w) => (
                      <div key={w.key} style={{ flex:1, textAlign:"center", fontSize:10, color:tokens.text.tertiary, lineHeight:1.3 }}>
                        {w.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}


// ── Home用サブコンポーネント ─────────────────────────────────
